import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StarIcon from '@mui/icons-material/Star';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useProductImages } from '../../../pages/embeddings/hooks/useProductImages';
import { CropEmbeddingSearchResponse, ProductImageSource } from '../../../types';
import {
  CandidatePreviewCard,
  type CandidateItemNumberMatch,
} from '../../components/common/CandidatePreviewCard';
import {
  CropActionBar,
  NEW_DESIGN_SHORTCUT_RETIRED_MESSAGE,
} from '../../components/common/CropActionBar';
import { MasterLabelWorkspaceControls } from '../../components/common/MasterLabelWorkspaceControls';
import { ProductImagePreview } from '../../components/common/ProductImagePreview';
import {
  reviewIssueActionFromShortcutKey,
  ReviewIssueChooserOverlay,
} from '../../components/common/ReviewIssueChooserOverlay';
import { ReviewStatusControl } from '../../components/common/ReviewStatusControl';
import { SaveWarningDialog } from '../../components/common/SaveWarningDialog';
import { FocusedHighlightInfo, UPCColumnView } from '../../components/common/UPCColumnView';
import { UseCropAsProductImageDialog } from '../../components/common/UseCropAsProductImageDialog';
import {
  CROP_COMPARE_MAX,
  CROP_COMPARE_MIN,
  CROP_COMPARE_TOO_FEW_MESSAGE,
  CROP_COMPARE_TOO_MANY_MESSAGE,
  CropComparePopup,
} from '../../components/common/CropComparePopup';
import { VizSimDatasetAddDialog } from '../../components/common/VizSimDatasetAddDialog';
import {
  DEFAULT_FORGE_CHAIN_ID,
  FORGE_CHAIN_OPTIONS_ASDA_FIRST as CHAIN_OPTIONS,
  type ForgeChainOption,
} from '../../config/chains';
import { useMasterLabelEditor } from '../../hooks/useMasterLabelEditor';
import { useNotification } from '../../hooks/useNotification';
import {
  getCachedImageStatus,
  getImageCacheSummary,
  ImageCacheSummary,
  releaseCachedImages,
  useZoomPreview,
} from '../../hooks/useZoomPreview';
import {
  ForgeApiError,
  ForgeApiService,
  SaveCheckWarningsError,
} from '../../services/ForgeApiService';
import { buildMasterLabelDebugInfo } from '../../services/masterLabelState';
import {
  transformApiDesignLockStatusInfo,
  transformApiUPCData,
} from '../../services/masterLabelTransforms';
import {
  CleaningIssueScope,
  DataCollectionCreateRequest,
  DataCollectionPreviewImage,
  DataCollectionRequestedType,
  DataCollectionTargetScope,
  DesignLockStatusInfo,
  ForgeDesignGroupReviewSummary,
  ForgeDesignGroupScope,
  ForgeDesignGroupSearchResult,
  ForgeDesignNote,
  MasterBarcodeEntry,
  MasterCropData,
  MasterDesignData,
  MasterLabelBackendCropMutation,
  MasterLabelBackendDeletionMutation,
  MasterLabelBackendDesignMutation,
  MasterLabelBackendEmbeddingMutation,
  MasterLabelCropQueryResponse,
  MasterLabelDebugInfo,
  MasterLabelReviewIssueType,
  MasterLabelSaveSummary,
  MasterLabelStateSaveResponse,
  MasterUPCData,
  RemoteDataCollectionCandidateGroup,
  RemoteDataCollectionReferenceCrop,
  RemoteDataCollectionReviewTarget,
  SaveWarning,
  SaveWarningReviewOutcome,
} from '../../types';
import {
  getMasterBarcodeGroupTheme,
  MasterBarcodeGroupTheme,
} from '../../utils/masterBarcodeGroupTheme';
import {
  buildDerivedBarcodeRows,
  getAllBarcodeEntries,
  resolveDraftDesignVerifiedBarcode,
} from '../../utils/masterBarcodeRows';
import { resolveMasterLabelChromeSizing } from '../../utils/masterLabelChromeSizing';
import {
  isDisplayableMasterLabelColumn,
  MasterLabelTrailingTile,
} from '../../utils/masterLabelColumnState';
import { resolveMasterLabelAuxImageSizing } from '../../utils/masterLabelImageSizing';
import {
  collectMasterLabelCropImageUrls,
  collectMasterLabelUpcCropImageUrls,
  collectMasterLabelUpcCropImageUrlVariants,
} from '../../utils/masterLabelImageUrls';
import { collectSelectedCrops, collectUpcCrops } from '../../utils/masterLabelCrops';
import {
  setCropContextViewAllowed,
  toggleCropContextView,
  useCropContextViewAllowed,
  useCropContextViewEnabled,
} from '../../hooks/useCropContextView';
import {
  appendMasterLabelHighlightParams,
  buildMasterLabelUrl,
  MasterLabelHighlightMode,
} from '../../utils/masterLabelLinks';
import { accumulateSaveWarningAcknowledgement } from '../../utils/saveChecks';

function isMasterLabelSaveDebugEnabled(): boolean {
  if (process.env.NODE_ENV !== 'development') {
    return false;
  }
  try {
    return window.localStorage.getItem('forgeMasterLabelSaveDebug') === 'true';
  } catch {
    return false;
  }
}

const ITEM_NUMBER_LENGTH_BY_CHAIN: Record<number, number> = {
  24: 8,
  30: 9,
  33: 9,
  41: 8,
  44: 8,
};
const DEFAULT_CHAIN_ID = DEFAULT_FORGE_CHAIN_ID;
const CHAIN_OPTION_BY_ID = new Map(CHAIN_OPTIONS.map((chain) => [chain.id, chain]));
const CHAIN_ORDER_BY_ID = new Map(CHAIN_OPTIONS.map((chain, index) => [chain.id, index]));
// Matches the item-mod-prefixes endpoint's per-request item-number cap.
const MOD_PREFIX_REQUEST_BATCH_SIZE = 200;
const AUTO_RELOAD_STORAGE_KEY = 'forge-auto-reload-images';
const SAVE_REVIEW_STORAGE_KEY = 'forge-show-save-review';
const TEXT_SCALE_STORAGE_KEY = 'forge-text-scale';
const MASTER_LABEL_INITIAL_PRELOAD_IMAGE_COUNT = 24;
const MASTER_LABEL_DEFERRED_PRELOAD_START_DELAY_MS = 5000;

const EMPTY_IMAGE_CACHE_SUMMARY: ImageCacheSummary = {
  total: 0,
  loaded: 0,
  pending: 0,
  failed: 0,
  idle: 0,
};

function scheduleMasterLabelImagePreload(callback: () => void) {
  const idleCallback = (
    window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
    }
  ).requestIdleCallback;

  if (idleCallback) {
    idleCallback(callback, { timeout: 1500 });
    return;
  }

  window.setTimeout(callback, 0);
}

export function hasImageCacheSummaryChanged(
  previousSummary: ImageCacheSummary,
  nextSummary: ImageCacheSummary
): boolean {
  return (
    previousSummary.total !== nextSummary.total ||
    previousSummary.loaded !== nextSummary.loaded ||
    previousSummary.pending !== nextSummary.pending ||
    previousSummary.failed !== nextSummary.failed ||
    previousSummary.idle !== nextSummary.idle
  );
}

function getItemNumberLengthForChain(chainId: number): number {
  return ITEM_NUMBER_LENGTH_BY_CHAIN[chainId] ?? 7;
}

function getPaddedItemNumberQuery(value: string, chainId: number): string | null {
  const trimmed = value.trim();
  const targetLength = getItemNumberLengthForChain(chainId);
  if (!/^\d+$/.test(trimmed) || trimmed.length >= targetLength) {
    return null;
  }
  return trimmed.padStart(targetLength, '0');
}

function isNormalizedBarcodeQuery(value: string, chainId: number): boolean {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return false;
  }
  return trimmed.length > getItemNumberLengthForChain(chainId);
}

function isMongoObjectIdQuery(value: string): boolean {
  return /^[a-f\d]{24}$/i.test(value.trim());
}

// Each preset has colors for the `c` and `c2` evidence sets. Most presets use
// action/reference roles; peer_groups deliberately gives both sets equal rank.
// `referenceMessage` replaces `message` when the second set is present.
const HIGHLIGHT_PRESETS = {
  similar: {
    issueLabel: 'Similarity',
    color: '#1f7aff',
    glow: '0 0 0 3px rgba(31,122,255,0.48), 0 0 18px rgba(31,122,255,0.92), 0 0 36px rgba(31,122,255,0.46)',
    message: 'Highlighted crops are very similar.',
    secondaryColor: '#a855f7',
    secondaryGlow:
      '0 0 0 3px rgba(168,85,247,0.44), 0 0 18px rgba(168,85,247,0.85), 0 0 36px rgba(168,85,247,0.42)',
    tertiaryColor: undefined,
    tertiaryGlow: undefined,
    referenceMessage: 'Blue and purple Crops look the same but are in different Designs.',
    fullGroupMessage: undefined,
  },
  different: {
    issueLabel: 'Coherence',
    color: '#ff3b30',
    glow: '0 0 0 3px rgba(255,59,48,0.46), 0 0 18px rgba(255,59,48,0.9), 0 0 36px rgba(255,59,48,0.44)',
    message: 'Highlighted crops should not be in the same design.',
    secondaryColor: '#1f7aff',
    secondaryGlow:
      '0 0 0 3px rgba(31,122,255,0.44), 0 0 18px rgba(31,122,255,0.85), 0 0 36px rgba(31,122,255,0.42)',
    tertiaryColor: undefined,
    tertiaryGlow: undefined,
    referenceMessage: 'Red Crops do not match the blue Crops.',
    fullGroupMessage: undefined,
  },
  peer_groups: {
    issueLabel: 'Split coherence',
    color: '#1f7aff',
    glow: '0 0 0 3px rgba(31,122,255,0.48), 0 0 18px rgba(31,122,255,0.92), 0 0 36px rgba(31,122,255,0.46)',
    message: 'Blue and purple Crops look different.',
    secondaryColor: '#a855f7',
    secondaryGlow:
      '0 0 0 3px rgba(168,85,247,0.44), 0 0 18px rgba(168,85,247,0.85), 0 0 36px rgba(168,85,247,0.42)',
    tertiaryColor: undefined,
    tertiaryGlow: undefined,
    referenceMessage: 'Blue and purple Crops look different.',
    fullGroupMessage: 'The full Design group between blue and purple looks incorrect.',
  },
  fragmented: {
    issueLabel: 'Fragmentation',
    color: '#c98500',
    glow: '0 0 0 3px rgba(201,133,0,0.5), 0 0 18px rgba(201,133,0,0.9), 0 0 36px rgba(201,133,0,0.45)',
    message: 'Orange Crops in this Design do not match.',
    secondaryColor: '#c98500',
    secondaryGlow:
      '0 0 0 3px rgba(201,133,0,0.5), 0 0 18px rgba(201,133,0,0.9), 0 0 36px rgba(201,133,0,0.45)',
    tertiaryColor: undefined,
    tertiaryGlow: undefined,
    referenceMessage: 'Orange Crops in this Design do not match.',
    fullGroupMessage: 'Orange Crops in this full group do not match.',
  },
  bad_quality: {
    issueLabel: 'Crop quality',
    color: '#c98500',
    glow: '0 0 0 3px rgba(201,133,0,0.5), 0 0 18px rgba(201,133,0,0.9), 0 0 36px rgba(201,133,0,0.45)',
    message: 'Highlighted crops may have bad quality.',
    secondaryColor: '#1f7aff',
    secondaryGlow:
      '0 0 0 3px rgba(31,122,255,0.44), 0 0 18px rgba(31,122,255,0.85), 0 0 36px rgba(31,122,255,0.42)',
    tertiaryColor: undefined,
    tertiaryGlow: undefined,
    referenceMessage: 'Orange crops may have bad quality; blue crops are shown for reference.',
    fullGroupMessage: undefined,
  },
  coherence_confusion: {
    issueLabel: 'Coherence + Confusion',
    color: '#1f7aff',
    glow: '0 0 0 3px rgba(31,122,255,0.48), 0 0 18px rgba(31,122,255,0.92), 0 0 36px rgba(31,122,255,0.46)',
    message: 'Red Crops match purple, not blue.',
    secondaryColor: '#ff3b30',
    secondaryGlow:
      '0 0 0 3px rgba(255,59,48,0.46), 0 0 18px rgba(255,59,48,0.9), 0 0 36px rgba(255,59,48,0.44)',
    tertiaryColor: '#a855f7',
    tertiaryGlow:
      '0 0 0 3px rgba(168,85,247,0.44), 0 0 18px rgba(168,85,247,0.85), 0 0 36px rgba(168,85,247,0.42)',
    referenceMessage: 'Red Crops match purple, not blue.',
    fullGroupMessage: undefined,
  },
} as const;

type HighlightPresetKey = keyof typeof HIGHLIGHT_PRESETS;
type HighlightedCropSection = 'design' | 'pending' | 'no-design' | 'carton' | 'inactive';
type HighlightedCropSnapshot = {
  cropId: string;
  itemNumber: string;
  section: HighlightedCropSection;
  designId: string | null;
  orientation: string;
  classificationType: string;
  isInactive: boolean;
  isPending: boolean;
  reviewIssueType: string | null;
  markedForDeletion: boolean;
  normalizedBarcode: string;
  isRepresentative: boolean;
};
type OpenDesignGroupCandidate = {
  upc: MasterUPCData;
  design: MasterDesignData;
  summary: ForgeDesignGroupSearchResult;
  theme: MasterBarcodeGroupTheme;
};
type MasterLabelUrlTargets = {
  itemNumbers: string[];
  normalizedBarcodes: string[];
  designIds: string[];
  cropIds: string[];
  /** Second evidence set shown in the highlight preset's secondary color (`c2`). */
  secondaryCropIds: string[];
  /** Third evidence set shown in the highlight preset's tertiary color (`c3`). */
  tertiaryCropIds: string[];
};

const MASTER_LABEL_ITEM_PARAM_KEYS = ['in', 'item_number', 'item_numbers'];
const MASTER_LABEL_BARCODE_PARAM_KEYS = [
  'nb',
  'normalized_barcode',
  'normalized_barcodes',
  'barcode',
  'barcodes',
];
const MASTER_LABEL_DESIGN_PARAM_KEYS = ['d', 'design_id', 'design_ids'];
const MASTER_LABEL_CROP_PARAM_KEYS = ['c', 'crop_id', 'crop_ids'];
const MASTER_LABEL_SECONDARY_CROP_PARAM_KEYS = ['c2', 'secondary_crop_id', 'secondary_crop_ids'];
const MASTER_LABEL_TERTIARY_CROP_PARAM_KEYS = ['c3', 'tertiary_crop_id', 'tertiary_crop_ids'];

function parseRepeatedSearchParam(searchParams: URLSearchParams, key: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  searchParams
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((value) => {
      if (seen.has(value)) return;
      seen.add(value);
      result.push(value);
    });
  return result;
}

function parseRepeatedSearchParams(searchParams: URLSearchParams, keys: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  keys.forEach((key) => {
    parseRepeatedSearchParam(searchParams, key).forEach((value) => {
      if (seen.has(value)) return;
      seen.add(value);
      result.push(value);
    });
  });
  return result;
}

function parseMasterLabelUrlTargets(searchParams: URLSearchParams): MasterLabelUrlTargets {
  const cropIds = parseRepeatedSearchParams(searchParams, MASTER_LABEL_CROP_PARAM_KEYS);
  const primaryCropIdSet = new Set(cropIds);
  const secondaryCropIds = parseRepeatedSearchParams(
    searchParams,
    MASTER_LABEL_SECONDARY_CROP_PARAM_KEYS
  ).filter((cropId) => !primaryCropIdSet.has(cropId));
  const usedCropIds = new Set([...cropIds, ...secondaryCropIds]);
  return {
    itemNumbers: parseRepeatedSearchParams(searchParams, MASTER_LABEL_ITEM_PARAM_KEYS),
    normalizedBarcodes: parseRepeatedSearchParams(searchParams, MASTER_LABEL_BARCODE_PARAM_KEYS),
    designIds: parseRepeatedSearchParams(searchParams, MASTER_LABEL_DESIGN_PARAM_KEYS),
    cropIds,
    secondaryCropIds,
    tertiaryCropIds: parseRepeatedSearchParams(
      searchParams,
      MASTER_LABEL_TERTIARY_CROP_PARAM_KEYS
    ).filter((cropId) => !usedCropIds.has(cropId)),
  };
}

function isSyntheticNormalizedBarcodeColumnKey(value: string): boolean {
  return value.startsWith('nb:');
}

function mergeUniqueValues(...groups: Array<Array<string | null | undefined>>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  groups.flat().forEach((value) => {
    const normalized = value?.trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function collectVisibleUrlScope(upcs: MasterUPCData[]) {
  const itemNumbers: string[] = [];
  const cropIds = new Set<string>();
  const designIds = new Set<string>();
  const normalizedBarcodes = new Set<string>();

  const addBarcode = (value?: string | null) => {
    const normalized = value?.trim();
    if (normalized) normalizedBarcodes.add(normalized);
  };

  upcs.forEach((upc) => {
    if (!isSyntheticNormalizedBarcodeColumnKey(upc.itemNumber)) {
      itemNumbers.push(upc.itemNumber);
    }
    addBarcode(upc.upc);
    addBarcode(upc.normalizedUpc);
    addBarcode(upc.lookupNormalizedBarcode);
    upc.barcodeSummary?.primaryUpcs.forEach((entry) => addBarcode(entry.upc));
    upc.barcodeSummary?.alternateUpcs.forEach((entry) => addBarcode(entry.upc));
    upc.barcodeSummary?.casePacks.forEach((entry) => addBarcode(entry.upc));
    upc.designs.forEach((design) => {
      designIds.add(design.id);
      addBarcode(design.normalizedBarcode);
      design.crops.forEach((crop) => cropIds.add(crop.id));
    });
    upc.pendingCrops?.forEach((crop) => cropIds.add(crop.id));
    upc.unassignedCrops.forEach((crop) => cropIds.add(crop.id));
    upc.cartonCrops.forEach((crop) => cropIds.add(crop.id));
    upc.inactiveCrops?.forEach((crop) => cropIds.add(crop.id));
  });

  return {
    itemNumbers: mergeUniqueValues(itemNumbers),
    cropIds,
    designIds,
    normalizedBarcodes,
  };
}

function pruneUrlTargetsForVisibleColumns(
  targets: MasterLabelUrlTargets,
  upcs: MasterUPCData[]
): MasterLabelUrlTargets {
  const visibleScope = collectVisibleUrlScope(upcs);
  return {
    itemNumbers: visibleScope.itemNumbers,
    normalizedBarcodes: targets.normalizedBarcodes.filter((barcode) =>
      visibleScope.normalizedBarcodes.has(barcode)
    ),
    designIds: targets.designIds.filter((designId) => visibleScope.designIds.has(designId)),
    cropIds: targets.cropIds.filter((cropId) => visibleScope.cropIds.has(cropId)),
    secondaryCropIds: targets.secondaryCropIds.filter((cropId) => visibleScope.cropIds.has(cropId)),
    tertiaryCropIds: targets.tertiaryCropIds.filter((cropId) => visibleScope.cropIds.has(cropId)),
  };
}

function buildCanonicalMasterLabelSearchParams(
  chainId: number,
  targets: MasterLabelUrlTargets,
  highlightMode?: MasterLabelHighlightMode | null,
  highlightScope?: CleaningIssueScope | null
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('chain', String(chainId));
  targets.itemNumbers
    .filter((itemNumber) => !isSyntheticNormalizedBarcodeColumnKey(itemNumber))
    .forEach((itemNumber) => params.append('in', itemNumber));
  targets.cropIds.forEach((cropId) => params.append('c', cropId));
  targets.secondaryCropIds.forEach((cropId) => params.append('c2', cropId));
  targets.tertiaryCropIds.forEach((cropId) => params.append('c3', cropId));
  appendMasterLabelHighlightParams(params, targets.cropIds.length, highlightMode, highlightScope);
  targets.normalizedBarcodes.forEach((barcode) => params.append('nb', barcode));
  targets.designIds.forEach((designId) => params.append('d', designId));
  return params;
}

function buildMasterLabelHighlightTaskKey(
  chainId: number,
  itemNumbers: string[],
  highlightedCropIds: Iterable<string>,
  highlightPresetKey: HighlightPresetKey | null,
  secondaryHighlightedCropIds: Iterable<string> = [],
  tertiaryHighlightedCropIds: Iterable<string> = []
): string {
  return JSON.stringify({
    chainId,
    itemNumbers: [...itemNumbers].sort(),
    highlightedCropIds: [...highlightedCropIds].sort(),
    highlightPresetKey,
    secondaryHighlightedCropIds: [...secondaryHighlightedCropIds].sort(),
    tertiaryHighlightedCropIds: [...tertiaryHighlightedCropIds].sort(),
  });
}

function resolveHighlightTaskKeyEvidenceGroups(
  highlightPresetKey: HighlightPresetKey | null,
  primaryCropIds: Iterable<string>,
  secondaryCropIds: Iterable<string>,
  tertiaryCropIds: Iterable<string>
): {
  secondaryCropIds: Iterable<string>;
  tertiaryCropIds: Iterable<string>;
} {
  return {
    secondaryCropIds:
      highlightPresetKey === 'peer_groups'
        ? []
        : highlightPresetKey === 'coherence_confusion'
          ? primaryCropIds
          : secondaryCropIds,
    tertiaryCropIds,
  };
}

function parseMasterLabelChainId(searchParams: URLSearchParams): number {
  const param = searchParams.get('chain') ?? searchParams.get('chain_id');
  const parsed = param ? Number(param) : NaN;
  return CHAIN_OPTIONS.some((c) => c.id === parsed) ? parsed : DEFAULT_CHAIN_ID;
}

function chainOptionForSupportedChain(chain: {
  chainId: number;
  chainName?: string;
}): ForgeChainOption {
  return (
    CHAIN_OPTION_BY_ID.get(chain.chainId) ?? {
      id: chain.chainId,
      label: chain.chainName || `Chain ${chain.chainId}`,
    }
  );
}

function buildSupportedChainOptions(
  chains: Array<{ chainId: number; chainName?: string }>
): ForgeChainOption[] {
  const seen = new Set<number>();
  return chains
    .filter((chain) => Number.isFinite(chain.chainId) && !seen.has(chain.chainId))
    .map((chain) => {
      seen.add(chain.chainId);
      return chainOptionForSupportedChain(chain);
    })
    .sort((left, right) => {
      const leftOrder = CHAIN_ORDER_BY_ID.get(left.id) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = CHAIN_ORDER_BY_ID.get(right.id) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.label.localeCompare(right.label);
    });
}

function getForgeDesignGroupScopeLabel(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'Full';
  if (scope === 'partial') return 'Partial';
  return 'Non-front';
}

function getForgeDesignGroupScopeTone(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'var(--positive)';
  if (scope === 'partial') return 'var(--warning)';
  return 'var(--danger)';
}

function getForgeDesignGroupScopeSubtleTone(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'var(--positive-subtle)';
  if (scope === 'partial') return 'var(--warning-subtle)';
  return 'var(--danger-subtle)';
}

const FORGE_DESIGN_GROUP_SCOPES: ForgeDesignGroupScope[] = ['full', 'partial', 'non-front'];

function getDesignGroupField(
  scope: ForgeDesignGroupScope
): 'fullGroupDesigns' | 'partialGroupDesigns' | 'nonFrontGroupDesigns' {
  if (scope === 'full') return 'fullGroupDesigns';
  if (scope === 'partial') return 'partialGroupDesigns';
  return 'nonFrontGroupDesigns';
}

function getDesignGroupReviewField(
  scope: ForgeDesignGroupScope
): 'fullGroupReview' | 'partialGroupReview' | 'nonFrontGroupReview' {
  if (scope === 'full') return 'fullGroupReview';
  if (scope === 'partial') return 'partialGroupReview';
  return 'nonFrontGroupReview';
}

function getDesignGroupReview(
  design: MasterDesignData,
  scope: ForgeDesignGroupScope
): ForgeDesignGroupReviewSummary | null {
  return design[getDesignGroupReviewField(scope)] ?? null;
}

function clearDesignGroupReview(
  design: MasterDesignData,
  scope: ForgeDesignGroupScope
): MasterDesignData {
  const reviewField = getDesignGroupReviewField(scope);
  const review = design[reviewField];
  if (!review) return design;
  return {
    ...design,
    [reviewField]: {
      ...review,
      isReviewed: false,
      reviewedBy: null,
      reviewedAt: null,
    },
  };
}

function isDesignPendingDeletion(design: MasterDesignData): boolean {
  return design.crops.length > 0 && design.crops.every((crop) => crop.markedForDeletion);
}

function collectPendingDeletedDesignIds(upcs: MasterUPCData[]): Set<string> {
  const designIds = new Set<string>();
  upcs.forEach((upc) => {
    upc.designs.forEach((design) => {
      if (!isDesignPendingDeletion(design)) return;
      designIds.add(design.id);
      if (design.persistedDesignId) {
        designIds.add(design.persistedDesignId);
      }
    });
  });
  return designIds;
}

function buildForgeUrlForDesign(
  chainId: number,
  designId?: string | null,
  normalizedBarcode?: string | null
): string | null {
  if (designId) return buildMasterLabelUrl({ chainId, designId });
  if (!normalizedBarcode) return null;
  return buildMasterLabelUrl({ chainId, normalizedBarcode });
}

function buildGroupSummaryForDesign(
  chainId: number,
  design: MasterDesignData
): ForgeDesignGroupSearchResult {
  const representativeCrop =
    design.crops.find((crop) => crop.id === design.representativeCropId) ?? design.crops[0] ?? null;
  return {
    designId: design.id,
    normalizedBarcode: design.normalizedBarcode ?? null,
    previewVizUrl: representativeCrop?.imageUrl ?? null,
    forgeUrl: buildForgeUrlForDesign(chainId, design.id, design.normalizedBarcode),
  };
}

function upsertDesignGroup(
  design: MasterDesignData,
  scope: ForgeDesignGroupScope,
  member: ForgeDesignGroupSearchResult
): MasterDesignData {
  const field = getDesignGroupField(scope);
  if (member.designId === design.id) {
    return design;
  }
  const nextMembers = [
    ...(design[field] ?? []).filter((entry) => entry.designId !== member.designId),
    member,
  ].sort((left, right) =>
    (left.normalizedBarcode || left.designId).localeCompare(
      right.normalizedBarcode || right.designId
    )
  );
  return clearDesignGroupReview({ ...design, [field]: nextMembers }, scope);
}

function removeDesignGroup(
  design: MasterDesignData,
  scope: ForgeDesignGroupScope,
  memberDesignId: string
): MasterDesignData {
  const field = getDesignGroupField(scope);
  return clearDesignGroupReview(
    {
      ...design,
      [field]: (design[field] ?? []).filter((entry) => entry.designId !== memberDesignId),
    },
    scope
  );
}

function removeDesignGroupMembers(
  design: MasterDesignData,
  scope: ForgeDesignGroupScope,
  memberDesignIds: Set<string>
): MasterDesignData {
  const field = getDesignGroupField(scope);
  return clearDesignGroupReview(
    {
      ...design,
      [field]: (design[field] ?? []).filter((entry) => !memberDesignIds.has(entry.designId)),
    },
    scope
  );
}

function collectGroupSummariesByDesignId(
  chainId: number,
  upcs: MasterUPCData[],
  excludedDesignIds: Set<string>
): Map<string, ForgeDesignGroupSearchResult> {
  const summaries = new Map<string, ForgeDesignGroupSearchResult>();
  upcs.forEach((upc) => {
    upc.designs.forEach((design) => {
      if (excludedDesignIds.has(design.id)) return;
      summaries.set(design.id, buildGroupSummaryForDesign(chainId, design));
      design.fullGroupDesigns?.forEach((summary) => {
        if (!excludedDesignIds.has(summary.designId)) {
          summaries.set(summary.designId, summary);
        }
      });
      design.partialGroupDesigns?.forEach((summary) => {
        if (!excludedDesignIds.has(summary.designId)) {
          summaries.set(summary.designId, summary);
        }
      });
      design.nonFrontGroupDesigns?.forEach((summary) => {
        if (!excludedDesignIds.has(summary.designId)) {
          summaries.set(summary.designId, summary);
        }
      });
    });
  });
  return summaries;
}

function normalizeDesignGroupScope(
  chainId: number,
  upcs: MasterUPCData[],
  scope: ForgeDesignGroupScope
): MasterUPCData[] {
  const field = getDesignGroupField(scope);
  const excludedDesignIds = collectPendingDeletedDesignIds(upcs);
  const summariesById = collectGroupSummariesByDesignId(chainId, upcs, excludedDesignIds);
  const componentByDesignId = collectDesignGroupComponents(upcs, scope, excludedDesignIds);

  return upcs.map((upc) => ({
    ...upc,
    designs: upc.designs.map((design) => {
      if (excludedDesignIds.has(design.id)) {
        return { ...design, [field]: [] };
      }
      const component = componentByDesignId.get(design.id);
      if (!component || component.size <= 1) {
        return { ...design, [field]: [] };
      }
      const nextMembers = [...component]
        .filter((designId) => designId !== design.id)
        .map((designId) => summariesById.get(designId))
        .filter((summary): summary is ForgeDesignGroupSearchResult => Boolean(summary))
        .sort((left, right) =>
          (left.normalizedBarcode || left.designId).localeCompare(
            right.normalizedBarcode || right.designId
          )
        );
      return { ...design, [field]: nextMembers };
    }),
  }));
}

function collectDesignGroupComponents(
  upcs: MasterUPCData[],
  scope: ForgeDesignGroupScope,
  excludedDesignIds = new Set<string>()
): Map<string, Set<string>> {
  const field = getDesignGroupField(scope);
  const graph = new Map<string, Set<string>>();
  const ensureNode = (designId: string) => {
    if (!graph.has(designId)) {
      graph.set(designId, new Set());
    }
  };

  upcs.forEach((upc) => {
    upc.designs.forEach((design) => {
      if (excludedDesignIds.has(design.id)) return;
      ensureNode(design.id);
      (design[field] ?? []).forEach((member) => {
        if (excludedDesignIds.has(member.designId)) return;
        ensureNode(member.designId);
        graph.get(design.id)?.add(member.designId);
        graph.get(member.designId)?.add(design.id);
      });
    });
  });

  const componentByDesignId = new Map<string, Set<string>>();
  const visited = new Set<string>();
  [...graph.keys()].forEach((startId) => {
    if (visited.has(startId)) return;
    const component = new Set<string>();
    const stack = [startId];
    visited.add(startId);
    while (stack.length > 0) {
      const currentId = stack.pop()!;
      component.add(currentId);
      graph.get(currentId)?.forEach((nextId) => {
        if (!visited.has(nextId)) {
          visited.add(nextId);
          stack.push(nextId);
        }
      });
    }
    component.forEach((designId) => componentByDesignId.set(designId, component));
  });

  return componentByDesignId;
}

function collectDesignGroupScopeMoves(
  upcs: MasterUPCData[],
  sourceDesignId: string,
  targetDesignId: string,
  targetScope: ForgeDesignGroupScope
): Array<{ sourceScope: ForgeDesignGroupScope; componentDesignIds: Set<string> }> {
  const excludedDesignIds = collectPendingDeletedDesignIds(upcs);
  if (excludedDesignIds.has(sourceDesignId) || excludedDesignIds.has(targetDesignId)) {
    return [];
  }
  const loadedDesignIds = new Set(
    upcs.flatMap((upc) =>
      upc.designs.map((design) => design.id).filter((designId) => !excludedDesignIds.has(designId))
    )
  );

  return FORGE_DESIGN_GROUP_SCOPES.flatMap((sourceScope) => {
    if (sourceScope === targetScope) {
      return [];
    }

    const component = collectDesignGroupComponents(upcs, sourceScope, excludedDesignIds).get(
      sourceDesignId
    );
    if (!component?.has(targetDesignId) || component.size <= 1) {
      return [];
    }
    if ([...component].some((designId) => !loadedDesignIds.has(designId))) {
      return [];
    }

    return [{ sourceScope, componentDesignIds: new Set(component) }];
  });
}

function hasIncompleteDesignGroupScopeMove(
  upcs: MasterUPCData[],
  sourceDesignId: string,
  targetDesignId: string,
  targetScope: ForgeDesignGroupScope
): boolean {
  const excludedDesignIds = collectPendingDeletedDesignIds(upcs);
  if (excludedDesignIds.has(sourceDesignId) || excludedDesignIds.has(targetDesignId)) {
    return false;
  }
  const loadedDesignIds = new Set(
    upcs.flatMap((upc) =>
      upc.designs.map((design) => design.id).filter((designId) => !excludedDesignIds.has(designId))
    )
  );

  return FORGE_DESIGN_GROUP_SCOPES.some((sourceScope) => {
    if (sourceScope === targetScope) {
      return false;
    }
    const component = collectDesignGroupComponents(upcs, sourceScope, excludedDesignIds).get(
      sourceDesignId
    );
    return (
      Boolean(component?.has(targetDesignId)) &&
      (component?.size ?? 0) > 1 &&
      [...(component ?? [])].some((designId) => !loadedDesignIds.has(designId))
    );
  });
}

function expandGroupScopeThroughStrongerGroups(
  chainId: number,
  upcs: MasterUPCData[],
  scope: ForgeDesignGroupScope,
  strongerScope: ForgeDesignGroupScope
): MasterUPCData[] {
  const excludedDesignIds = collectPendingDeletedDesignIds(upcs);
  const summariesById = collectGroupSummariesByDesignId(chainId, upcs, excludedDesignIds);
  const strongerComponentByDesignId = collectDesignGroupComponents(
    upcs,
    strongerScope,
    excludedDesignIds
  );
  const field = getDesignGroupField(scope);

  return upcs.map((upc) => ({
    ...upc,
    designs: upc.designs.map((design) => {
      if (excludedDesignIds.has(design.id)) {
        return { ...design, [field]: [] };
      }
      const existingMemberIds = new Set(
        (design[field] ?? [])
          .map((member) => member.designId)
          .filter((designId) => !excludedDesignIds.has(designId))
      );
      if (existingMemberIds.size === 0) {
        return design;
      }

      const expandedMemberIds = new Set(existingMemberIds);
      strongerComponentByDesignId
        .get(design.id)
        ?.forEach((designId) => expandedMemberIds.add(designId));
      existingMemberIds.forEach((memberDesignId) => {
        strongerComponentByDesignId
          .get(memberDesignId)
          ?.forEach((designId) => expandedMemberIds.add(designId));
      });

      const nextMembers = [...expandedMemberIds]
        .filter((designId) => designId !== design.id && !excludedDesignIds.has(designId))
        .map((designId) => summariesById.get(designId))
        .filter((summary): summary is ForgeDesignGroupSearchResult => Boolean(summary))
        .sort((left, right) =>
          (left.normalizedBarcode || left.designId).localeCompare(
            right.normalizedBarcode || right.designId
          )
        );
      return { ...design, [field]: nextMembers };
    }),
  }));
}

function normalizeDesignGroups(chainId: number, upcs: MasterUPCData[]): MasterUPCData[] {
  let nextUpcs = normalizeDesignGroupScope(chainId, upcs, 'full');
  nextUpcs = expandGroupScopeThroughStrongerGroups(chainId, nextUpcs, 'partial', 'full');
  nextUpcs = normalizeDesignGroupScope(chainId, nextUpcs, 'partial');
  nextUpcs = expandGroupScopeThroughStrongerGroups(chainId, nextUpcs, 'non-front', 'full');
  nextUpcs = expandGroupScopeThroughStrongerGroups(chainId, nextUpcs, 'non-front', 'partial');
  nextUpcs = normalizeDesignGroupScope(chainId, nextUpcs, 'non-front');
  return nextUpcs;
}

function getDesignThemeMap(upcs: MasterUPCData[]): Map<string, MasterBarcodeGroupTheme> {
  const themes = new Map<string, MasterBarcodeGroupTheme>();
  upcs.forEach((upc) => {
    const { barcodeRows } = buildDerivedBarcodeRows(upc);
    barcodeRows.forEach((row, index) => {
      const theme = getMasterBarcodeGroupTheme(index);
      row.matchedDesigns.forEach((design) => themes.set(design.id, theme));
    });
    upc.designs.forEach((design) => {
      if (!themes.has(design.id)) {
        themes.set(design.id, getMasterBarcodeGroupTheme(0));
      }
    });
  });
  return themes;
}

function resolveLoadedDesignContext(
  upcs: MasterUPCData[],
  target: { itemNumber: string; designId: string } | null
): { upc: MasterUPCData; design: MasterDesignData } | null {
  if (!target) {
    return null;
  }

  const upc = upcs.find((item) => item.itemNumber === target.itemNumber);
  if (!upc) {
    return null;
  }

  const design = upc.designs.find((entry) => entry.id === target.designId);
  if (!design) {
    return null;
  }

  return { upc, design };
}

export function buildControlEmbeddingsUrl(
  chainId: number,
  options?: { crop?: MasterCropData; upc?: MasterUPCData }
): string {
  const params = new URLSearchParams({
    chain_id: String(chainId),
  });
  const itemNumber =
    options?.upc?.actualItemNumber === null
      ? options?.crop?.itemNumber
      : options?.upc?.actualItemNumber || options?.upc?.itemNumber || options?.crop?.itemNumber;
  if (itemNumber) {
    params.set('item_number', itemNumber);
  }
  const storeId = options?.upc?.lookupStoreId ?? options?.crop?.storeId;
  if (storeId) {
    params.set('store_id', String(storeId));
  }
  return `/embeddings/detail?${params.toString()}`;
}

type RemoteDataCollectionLinksLookupEntry = {
  status: 'loading' | 'ready' | 'error';
  links: RemoteDataCollectionReviewTarget[];
  searchedStoreCount?: number;
  maxStores?: number;
};

const REMOTE_DATA_COLLECTION_DEFAULT_STORE_LIMIT = 5;

function getRemoteDataCollectionQuery(upc: MasterUPCData): {
  itemNumber: string | null;
  normalizedBarcode: string | null;
} {
  const itemNumber =
    upc.actualItemNumber === null ? null : upc.actualItemNumber || upc.itemNumber || null;
  const normalizedBarcode = upc.lookupNormalizedBarcode || upc.normalizedUpc || upc.upc || null;
  return { itemNumber, normalizedBarcode };
}

function getRemoteDataCollectionLookupBaseKey(chainId: number, upc: MasterUPCData): string {
  const { itemNumber, normalizedBarcode } = getRemoteDataCollectionQuery(upc);
  return `${chainId}:${itemNumber ?? ''}:${normalizedBarcode ?? ''}`;
}

function getRemoteDataCollectionLookupKey(
  chainId: number,
  upc: MasterUPCData,
  maxStores: number
): string {
  return `${getRemoteDataCollectionLookupBaseKey(chainId, upc)}:${maxStores}`;
}

type DataCollectionTargetOption = {
  scope: DataCollectionTargetScope;
  key: string;
  label: string;
  detail?: string;
  itemNumber?: string | null;
  normalizedBarcode?: string | null;
  designId?: string | null;
  productName?: string | null;
  previewVizUrl?: string | null;
  previewImages?: DataCollectionPreviewImage[];
  cropCount?: number;
};

type DataCollectionTargetsByScope = Record<DataCollectionTargetScope, DataCollectionTargetOption[]>;
type DataCollectionRequestSelection = Map<string, Set<DataCollectionRequestedType>>;

function getRepresentativeDesignPreview(design: MasterDesignData): string | null {
  return (
    design.frontImageUrl ||
    design.backImageUrl ||
    design.barcodeImageUrl ||
    design.crops[0]?.imageUrl ||
    null
  );
}

function addPreviewImage(
  images: DataCollectionPreviewImage[],
  seenUrls: Set<string>,
  url: string | null | undefined,
  label: string,
  kind: string
) {
  if (!url || seenUrls.has(url)) return;
  seenUrls.add(url);
  images.push({ url, label, kind });
}

function getItemPreviewImages(upc: MasterUPCData): DataCollectionPreviewImage[] {
  const images: DataCollectionPreviewImage[] = [];
  const seenUrls = new Set<string>();
  addPreviewImage(images, seenUrls, upc.productImageUrl, 'Item product image', 'item_product');
  upc.alternativeImageUrls?.forEach((url, index) => {
    addPreviewImage(images, seenUrls, url, `Item alternate ${index + 1}`, 'item_alternate');
  });
  upc.designs.slice(0, 4).forEach((design) => {
    addPreviewImage(
      images,
      seenUrls,
      getRepresentativeDesignPreview(design),
      design.normalizedBarcode ? `Design ${design.normalizedBarcode}` : 'Design preview',
      'design'
    );
  });
  return images.slice(0, 8);
}

function getBarcodePreviewImages(
  upc: MasterUPCData,
  barcode: string
): DataCollectionPreviewImage[] {
  const images: DataCollectionPreviewImage[] = [];
  const seenUrls = new Set<string>();
  const entry = getAllBarcodeEntries(upc).find((candidate) => candidate.upc === barcode);
  entry?.storeDetails
    .flatMap((detail) => detail.images ?? [])
    .forEach((image) => {
      const label = image.type
        ? `Barcode ${image.type}`
        : barcode.length > 0
          ? `Barcode ${barcode}`
          : 'Barcode image';
      addPreviewImage(images, seenUrls, image.url, label, image.type || 'barcode');
    });
  upc.designs
    .filter((design) => design.normalizedBarcode === barcode)
    .forEach((design) => {
      addPreviewImage(images, seenUrls, design.frontImageUrl, 'Design front', 'design_front');
      addPreviewImage(images, seenUrls, design.backImageUrl, 'Design back', 'design_back');
      addPreviewImage(images, seenUrls, design.barcodeImageUrl, 'Design barcode', 'design_barcode');
      design.crops.slice(0, 3).forEach((crop, index) => {
        addPreviewImage(images, seenUrls, crop.imageUrl, `Crop ${index + 1}`, 'crop');
      });
    });
  return images.slice(0, 8);
}

function getDesignPreviewImages(design: MasterDesignData): DataCollectionPreviewImage[] {
  const images: DataCollectionPreviewImage[] = [];
  const seenUrls = new Set<string>();
  addPreviewImage(images, seenUrls, design.frontImageUrl, 'Design front', 'design_front');
  addPreviewImage(images, seenUrls, design.backImageUrl, 'Design back', 'design_back');
  addPreviewImage(images, seenUrls, design.barcodeImageUrl, 'Design barcode', 'design_barcode');
  design.crops.slice(0, 5).forEach((crop, index) => {
    addPreviewImage(images, seenUrls, crop.imageUrl, `Crop ${index + 1}`, 'crop');
  });
  return images.slice(0, 8);
}

function getBarcodeEntryPreview(entry: MasterBarcodeEntry | undefined): string | null {
  if (!entry) return null;
  const images = entry.storeDetails.flatMap((detail) => detail.images ?? []);
  return images.find((image) => image.type === 'front')?.url || images[0]?.url || null;
}

function getBarcodePreviewByValue(upc: MasterUPCData, barcode: string): string | null {
  const normalizedBarcode = barcode.trim();
  if (!normalizedBarcode) return null;
  return (
    getBarcodeEntryPreview(
      getAllBarcodeEntries(upc).find((entry) => entry.upc === normalizedBarcode)
    ) ||
    upc.designs.find((design) => design.normalizedBarcode === normalizedBarcode)?.frontImageUrl ||
    null
  );
}

export function getDataCollectionTargets(upc: MasterUPCData): DataCollectionTargetsByScope {
  const itemNumber =
    upc.actualItemNumber === null ? null : upc.actualItemNumber || upc.itemNumber || null;
  const barcodeValues = new Set<string>();
  if (upc.normalizedUpc) barcodeValues.add(upc.normalizedUpc);
  if (upc.lookupNormalizedBarcode) barcodeValues.add(upc.lookupNormalizedBarcode);
  getAllBarcodeEntries(upc).forEach((entry) => {
    if (entry.upc) barcodeValues.add(entry.upc);
  });
  upc.designs.forEach((design) => {
    if (design.normalizedBarcode) barcodeValues.add(design.normalizedBarcode);
  });
  collectUpcCrops(upc).forEach((crop) => {
    if (crop.normalizedBarcode) barcodeValues.add(crop.normalizedBarcode);
    if (crop.normalizedUpc) barcodeValues.add(crop.normalizedUpc);
  });

  return {
    item_number: itemNumber
      ? [
          {
            scope: 'item_number',
            key: itemNumber,
            label: itemNumber,
            detail: upc.productName,
            itemNumber,
            normalizedBarcode: upc.normalizedUpc || upc.lookupNormalizedBarcode || null,
            productName: upc.productName,
            previewVizUrl: upc.productImageUrl || upc.alternativeImageUrls?.[0] || null,
            previewImages: getItemPreviewImages(upc),
            cropCount: collectUpcCrops(upc).length,
          },
        ]
      : [],
    normalized_barcode: [...barcodeValues]
      .filter((barcode) => barcode.trim().length > 0)
      .sort()
      .map((barcode) => ({
        scope: 'normalized_barcode' as const,
        key: barcode,
        label: barcode,
        detail: upc.productName,
        itemNumber,
        normalizedBarcode: barcode,
        productName: upc.productName,
        previewVizUrl: getBarcodePreviewByValue(upc, barcode),
        previewImages: getBarcodePreviewImages(upc, barcode),
        cropCount: collectUpcCrops(upc).filter(
          (crop) => crop.normalizedBarcode === barcode || crop.normalizedUpc === barcode
        ).length,
      })),
    design: upc.designs
      .filter((design) => Boolean(design.persistedDesignId))
      .map((design) => {
        const designId = design.persistedDesignId || design.id;
        const previewVizUrl = getRepresentativeDesignPreview(design);
        return {
          scope: 'design' as const,
          key: designId,
          label: upc.productName || design.normalizedBarcode || designId,
          detail: `${design.normalizedBarcode || 'no barcode'} · ${design.crops.length} crops · ${designId}`,
          itemNumber,
          normalizedBarcode: design.normalizedBarcode || upc.normalizedUpc || null,
          designId,
          productName: upc.productName,
          previewVizUrl,
          previewImages: getDesignPreviewImages(design),
          cropCount: design.crops.length,
        };
      }),
  };
}

function getDataCollectionTargetRequestKey(
  scope: DataCollectionTargetScope,
  targetKey: string
): string {
  return `${scope}:${targetKey}`;
}

function getDataCollectionTargetSelectionKey(target: DataCollectionTargetOption): string {
  return getDataCollectionTargetRequestKey(target.scope, target.key);
}

function countDataCollectionSelections(selections: DataCollectionRequestSelection): number {
  let count = 0;
  selections.forEach((types) => {
    count += types.size;
  });
  return count;
}

function buildExistingDataCollectionRequestMap(
  rows: {
    targetScope: DataCollectionTargetScope;
    targetKey: string;
    requestedTypes: DataCollectionRequestedType[];
  }[]
): DataCollectionRequestSelection {
  const existing = new Map<string, Set<DataCollectionRequestedType>>();
  rows.forEach((row) => {
    const key = getDataCollectionTargetRequestKey(row.targetScope, row.targetKey);
    const types = existing.get(key) ?? new Set<DataCollectionRequestedType>();
    row.requestedTypes.forEach((type) => types.add(type));
    existing.set(key, types);
  });
  return existing;
}

function normalizeHighlightValue(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function buildHighlightedCropSnapshotLookup(
  upcs: MasterUPCData[]
): Map<string, HighlightedCropSnapshot> {
  const lookup = new Map<string, HighlightedCropSnapshot>();

  upcs.forEach((upc) => {
    upc.designs.forEach((design) => {
      design.crops.forEach((crop) => {
        lookup.set(crop.id, {
          cropId: crop.id,
          itemNumber: upc.itemNumber,
          section: 'design',
          designId: design.id,
          orientation: normalizeHighlightValue(crop.orientation),
          classificationType: normalizeHighlightValue(crop.classificationType),
          isInactive: Boolean(crop.isInactive),
          isPending: Boolean(crop.isPending),
          reviewIssueType: crop.reviewIssueType ?? null,
          markedForDeletion: Boolean(crop.markedForDeletion),
          normalizedBarcode: normalizeHighlightValue(crop.normalizedBarcode),
          isRepresentative: design.representativeCropId === crop.id,
        });
      });
    });

    (
      [
        ['pending', upc.pendingCrops ?? []],
        ['no-design', upc.unassignedCrops],
        ['carton', upc.cartonCrops],
        ['inactive', upc.inactiveCrops ?? []],
      ] as const
    ).forEach(([section, crops]) => {
      crops.forEach((crop) => {
        lookup.set(crop.id, {
          cropId: crop.id,
          itemNumber: upc.itemNumber,
          section,
          designId: null,
          orientation: normalizeHighlightValue(crop.orientation),
          classificationType: normalizeHighlightValue(crop.classificationType),
          isInactive: Boolean(crop.isInactive),
          isPending: Boolean(crop.isPending),
          reviewIssueType: crop.reviewIssueType ?? null,
          markedForDeletion: Boolean(crop.markedForDeletion),
          normalizedBarcode: normalizeHighlightValue(crop.normalizedBarcode),
          isRepresentative: false,
        });
      });
    });
  });

  return lookup;
}

function areHighlightedCropSnapshotsEqual(
  left: HighlightedCropSnapshot,
  right: HighlightedCropSnapshot
): boolean {
  return (
    left.itemNumber === right.itemNumber &&
    left.section === right.section &&
    left.designId === right.designId &&
    left.orientation === right.orientation &&
    left.classificationType === right.classificationType &&
    left.isInactive === right.isInactive &&
    left.isPending === right.isPending &&
    left.reviewIssueType === right.reviewIssueType &&
    left.markedForDeletion === right.markedForDeletion &&
    left.normalizedBarcode === right.normalizedBarcode &&
    left.isRepresentative === right.isRepresentative
  );
}

type SaveReviewData = {
  debugInfo: MasterLabelDebugInfo;
  backendSummary?: MasterLabelSaveSummary;
  backendItemCount?: number;
  backendCropMutations?: MasterLabelBackendCropMutation[];
  backendDesignMutations?: MasterLabelBackendDesignMutation[];
  backendEmbeddingMutations?: MasterLabelBackendEmbeddingMutation[];
  backendDeletionMutations?: MasterLabelBackendDeletionMutation[];
  error?: {
    message: string;
    status?: number;
    detail?: string;
    rawBody?: string;
  } | null;
};

// State for the save-check-warnings dialog: opened when a save 422s with unacknowledged save
// checks. `resolve` settles the promise handleSubmit is awaiting so it can either abort ("Go
// back") or resubmit the save with the acknowledgement fields ("Acknowledge & save").
type SaveWarningPromptState = {
  warnings: SaveWarning[];
  saving: boolean;
  resolve: (result: { comment: string; outcome: SaveWarningReviewOutcome } | null) => void;
};

const formatMutationValue = (value: string | number | boolean | null | undefined): string => {
  if (value === null || value === undefined) return 'none';
  if (value === '') return "''";
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const formatReviewIssueType = (value: string | null | undefined): string =>
  formatMutationValue(value ? value.replace(/_/g, ' ') : value);

const formatCropSourceSummary = (bySource: Record<string, number>): string => {
  const parts = Object.entries(bySource)
    .filter(([, count]) => count > 0)
    .sort(([leftSource], [rightSource]) => leftSource.localeCompare(rightSource))
    .map(([source, count]) => `${count} ${source}`);
  return parts.join(', ');
};

const getBarcodeEntryPrimaryLabel = (entry: MasterBarcodeEntry): string | undefined => {
  const withName = entry.storeDetails.find((detail) => detail.name);
  if (!withName || !withName.name) return undefined;
  return withName.casePack ? `${withName.name} (${withName.casePack})` : withName.name;
};

const getBarcodeEntryRoleHint = (
  entry: MasterBarcodeEntry
): 'primary' | 'alternate' | 'case' | undefined => {
  const priority: Record<'primary' | 'alternate' | 'case', number> = {
    primary: 0,
    alternate: 1,
    case: 2,
  };
  let best: 'primary' | 'alternate' | 'case' | undefined;
  for (const detail of entry.storeDetails) {
    if (best === undefined || priority[detail.role] < priority[best]) {
      best = detail.role;
    }
  }
  return best;
};

const SECONDARY_PRODUCT_IMAGE_POSITION = 11;

// TODO onDesignLockAction

/**
 * Wraps the shared useProductImages hook so it is keyed to the targeted UPC.
 * Mounts only while the product-image dialog is open (rendered conditionally by
 * the parent), so the hook's chainId/upc are stable for the lifetime of the
 * dialog. Forge has no reliable store name, so the hook is called without one.
 */
const ProductImageDialogContainer: React.FC<{
  chainId: number;
  crop: MasterCropData;
  upc: MasterUPCData;
  onNotify: (text: string, type?: 'success' | 'warning' | 'error') => void;
  onUpdated: (itemNumber: string, primaryUrl: string, alternativeUrls: string[]) => void;
  onClose: () => void;
}> = ({ chainId, crop, upc, onNotify, onUpdated, onClose }) => {
  const productUpc = upc.normalizedUpc || upc.upc;
  const cropImageUrl = crop.imageUrl;
  const { primaryImage, alternativeImages, uploadImage, deleteImage, reorderImages, refetch } =
    useProductImages({
      chainId,
      upc: productUpc,
    });
  const [saving, setSaving] = useState(false);

  const handleConfirm = useCallback(
    async (target: 'primary' | 'secondary', conflictResolution?: 'delete' | 'demote') => {
      if (saving) return;
      setSaving(true);
      try {
        const blob = await ForgeApiService.fetchImageBlob(cropImageUrl);

        // Track the resulting primary/alternative URLs so the parent can patch
        // its MasterUPCData (the dialog-local hook's refetch is discarded on
        // close). Seed from the current managed images.
        const existingAltUrls = alternativeImages.map((img) => img.imageUri);
        let nextPrimaryUrl = primaryImage?.imageUri ?? '';
        let nextAltUrls = existingAltUrls;

        if (target === 'secondary') {
          const result = await uploadImage(
            blob,
            SECONDARY_PRODUCT_IMAGE_POSITION,
            ProductImageSource.Embedding
          );
          if (!result) return; // hook already surfaced an error toast
          nextAltUrls = [...existingAltUrls, result.image_uri];
        } else {
          // Primary: resolve any existing managed primary first so we don't end
          // up with two images at position 0.
          if (primaryImage?.productImageId) {
            if (conflictResolution === 'delete') {
              const ok = await deleteImage(primaryImage.productImageId);
              if (!ok) return;
            } else if (conflictResolution === 'demote') {
              const ok = await reorderImages([
                {
                  product_image_id: primaryImage.productImageId,
                  position: SECONDARY_PRODUCT_IMAGE_POSITION,
                },
              ]);
              if (!ok) return;
              // Demoted primary becomes a secondary image.
              if (primaryImage.imageUri) {
                nextAltUrls = [...existingAltUrls, primaryImage.imageUri];
              }
            }
          }
          const result = await uploadImage(blob, 0, ProductImageSource.Embedding);
          if (!result) return;
          nextPrimaryUrl = result.image_uri;
        }

        await refetch();
        onUpdated(upc.itemNumber, nextPrimaryUrl, nextAltUrls);
        onNotify('Product image updated', 'success');
        onClose();
      } catch (err) {
        console.error('Failed to set product image:', err);
        onNotify('Failed to set product image', 'error');
      } finally {
        setSaving(false);
      }
    },
    [
      saving,
      cropImageUrl,
      uploadImage,
      deleteImage,
      reorderImages,
      refetch,
      primaryImage,
      alternativeImages,
      upc.itemNumber,
      onUpdated,
      onNotify,
      onClose,
    ]
  );

  return (
    <UseCropAsProductImageDialog
      isOpen
      cropImageUrl={cropImageUrl}
      existingPrimary={primaryImage}
      saving={saving}
      onCancel={onClose}
      onConfirm={handleConfirm}
    />
  );
};

const NewDesignConfirmOverlay: React.FC<{
  itemNumber: string;
  cropSummary: { total: number; bySource: Record<string, number> };
  availableBarcodes: MasterBarcodeEntry[];
  onConfirm: (normalizedBarcode: string) => void;
  onClose: () => void;
}> = ({ itemNumber, cropSummary, availableBarcodes, onConfirm, onClose }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const sourceSummary = formatCropSourceSummary(cropSummary.bySource);
  const isConfirmEnabled = selected !== null && selected.length > 0;

  return (
    <div
      data-testid="new-design-confirm-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.36)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 30,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 420,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 14px 32px rgba(15, 23, 42, 0.22)',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700 }}>
              Create design without scan source
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
              Item {itemNumber} — {cropSummary.total} crop{cropSummary.total === 1 ? '' : 's'}
              {sourceSummary ? ` (${sourceSummary})` : ''}. Pick a barcode to assign to the new
              design:
            </div>
          </div>
          <CloseIcon
            onClick={onClose}
            style={{ fontSize: 14, cursor: 'pointer', color: 'var(--text-tertiary)' }}
          />
        </div>
        <div
          data-testid="new-design-barcode-picker"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            overflowY: 'auto',
            maxHeight: 320,
          }}
        >
          {availableBarcodes.map((entry) => {
            const normalizedBarcode = entry.upc.trim();
            if (!normalizedBarcode) return null;
            const isSelected = selected === normalizedBarcode;
            const label = getBarcodeEntryPrimaryLabel(entry);
            const role = getBarcodeEntryRoleHint(entry);
            return (
              <button
                key={normalizedBarcode}
                data-testid={`new-design-barcode-option-${normalizedBarcode}`}
                onClick={() => setSelected(normalizedBarcode)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: isSelected ? '1px solid var(--accent-border)' : '1px solid var(--border)',
                  background: isSelected ? 'var(--accent-subtle)' : 'var(--bg)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{normalizedBarcode}</span>
                  {label && (
                    <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{label}</span>
                  )}
                </span>
                {role && (
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                    {role}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            data-testid="new-design-confirm-cancel"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            data-testid="new-design-confirm-submit"
            disabled={!isConfirmEnabled}
            onClick={() => {
              if (selected) onConfirm(selected);
            }}
            style={{
              padding: '6px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--accent-border)',
              background: isConfirmEnabled ? 'var(--accent)' : 'var(--border)',
              color: isConfirmEnabled ? 'var(--on-accent, #fff)' : 'var(--text-tertiary)',
              cursor: isConfirmEnabled ? 'pointer' : 'not-allowed',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Create design
          </button>
        </div>
        <div
          style={{ fontSize: 10, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}
        >
          Esc closes this window.
        </div>
      </div>
    </div>
  );
};

type DesignLockAction = 'lock' | 'update' | 'unlock';

interface DesignLockDialogTarget {
  action: DesignLockAction;
  design: MasterDesignData;
  upc: MasterUPCData;
}

function getDesignLockDivergenceReasons(lock: DesignLockStatusInfo): string[] {
  return [
    lock.cropsChanged ? 'crops' : null,
    lock.metadataChanged ? 'metadata' : null,
    lock.groupsChanged ? 'groups' : null,
  ].filter((reason): reason is string => Boolean(reason));
}

function mergeDesignLockStatuses(
  upcs: MasterUPCData[],
  designLocks?: Record<string, unknown>
): MasterUPCData[] {
  if (!designLocks) return upcs;
  return upcs.map((upc) => ({
    ...upc,
    designs: upc.designs.map((design) => {
      const designId = design.persistedDesignId || design.id;
      if (!Object.prototype.hasOwnProperty.call(designLocks, designId)) return design;
      const designLock = transformApiDesignLockStatusInfo(designLocks[designId], designId);
      return designLock ? { ...design, designLock } : design;
    }),
  }));
}

const DesignLockConfirmOverlay: React.FC<{
  target: DesignLockDialogTarget;
  isSubmitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ target, isSubmitting, onConfirm, onClose }) => {
  const lock = target.design.designLock;
  const reasons = lock ? getDesignLockDivergenceReasons(lock) : [];
  const title =
    target.action === 'lock'
      ? 'Lock design for training'
      : target.action === 'update'
        ? 'Update design lock'
        : 'Unlock design';
  const confirmLabel =
    target.action === 'lock'
      ? 'Lock design'
      : target.action === 'update'
        ? 'Update lock'
        : 'Unlock';

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isSubmitting) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [isSubmitting, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="design-lock-confirm-dialog"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 20,
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        style={{
          width: 440,
          maxWidth: 'calc(100vw - 32px)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-elevated)',
          padding: 16,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
        <div
          style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)' }}
        >
          {target.action === 'lock' ? (
            <>
              Lock this design for training? Training can then use this frozen snapshot instead of
              the live design.
            </>
          ) : target.action === 'update' && lock ? (
            <>
              <div>
                Diverged: {reasons.length > 0 ? reasons.join(', ') : 'live design changes'}.
              </div>
              <div>
                Active crops: locked {lock.lockedActiveCropCount.toLocaleString()} · live{' '}
                {lock.liveActiveCropCount.toLocaleString()}.
              </div>
              <div style={{ marginTop: 8 }}>
                Updating replaces the previous frozen snapshot irreversibly.
              </div>
            </>
          ) : (
            <>The frozen snapshot is deleted permanently.</>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={isSubmitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="dg-primary-button"
            data-testid="design-lock-confirm-submit"
            disabled={isSubmitting}
            onClick={onConfirm}
          >
            {isSubmitting ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const DesignGroupManagerOverlay: React.FC<{
  design: MasterDesignData;
  upc: MasterUPCData;
  openDesigns: OpenDesignGroupCandidate[];
  isSubmitting: boolean;
  cropSize: number;
  onImageHover: ReturnType<typeof useZoomPreview>['handleImageHover'];
  onImageMove: ReturnType<typeof useZoomPreview>['handleImageMove'];
  onImageLeave: ReturnType<typeof useZoomPreview>['handleImageLeave'];
  onAddToGroup: (scope: ForgeDesignGroupScope, targetDesignId: string) => void;
  onLeaveGroup: (scope: ForgeDesignGroupScope) => void;
  onConvertGroupScope: (
    sourceScope: ForgeDesignGroupScope,
    targetScope: ForgeDesignGroupScope
  ) => void;
  onToggleGroupReview?: (
    scope: ForgeDesignGroupScope,
    review: ForgeDesignGroupReviewSummary
  ) => void;
  onLoadGroupMember: (member: ForgeDesignGroupSearchResult) => void;
  onClose: () => void;
}> = ({
  design,
  upc,
  openDesigns,
  isSubmitting,
  cropSize,
  onImageHover,
  onImageMove,
  onImageLeave,
  onAddToGroup,
  onLeaveGroup,
  onConvertGroupScope,
  onToggleGroupReview,
  onLoadGroupMember,
  onClose,
}) => {
  const [showZeroCropCandidates, setShowZeroCropCandidates] = useState(false);
  const fullGroupDesignIds = new Set(
    (design.fullGroupDesigns ?? []).map((member) => member.designId)
  );
  const partialGroupDesignIds = new Set(
    (design.partialGroupDesigns ?? []).map((member) => member.designId)
  );
  const nonFrontGroupDesignIds = new Set(
    (design.nonFrontGroupDesigns ?? []).map((member) => member.designId)
  );
  const openDesignById = new Map(openDesigns.map((entry) => [entry.design.id, entry]));
  const allCandidateDesigns = openDesigns.filter((entry) => entry.design.id !== design.id);
  const hiddenZeroCropCandidateCount = allCandidateDesigns.filter(
    (entry) => entry.design.crops.length === 0
  ).length;
  const candidateDesigns = showZeroCropCandidates
    ? allCandidateDesigns
    : allCandidateDesigns.filter((entry) => entry.design.crops.length > 0);
  const currentTheme = openDesignById.get(design.id)?.theme ?? getMasterBarcodeGroupTheme(0);
  const sourceCropPreviewSize = Math.max(88, Math.min(150, Math.round(cropSize * 0.95)));
  const candidateCropPreviewSize = Math.max(72, Math.min(126, Math.round(cropSize * 0.8)));
  const groupCropPreviewSize = Math.max(68, Math.min(118, Math.round(cropSize * 0.74)));

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  const renderCropStrip = (
    crops: MasterCropData[],
    size: number,
    emptyText: string,
    maxItems = 8
  ) => {
    const visibleCrops = crops.slice(0, maxItems);
    if (visibleCrops.length === 0) {
      return (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-tertiary)',
          }}
        >
          {emptyText}
        </div>
      );
    }
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          overflowX: 'auto',
          paddingBottom: 2,
        }}
      >
        {visibleCrops.map((crop) => (
          <ProductImagePreview
            key={crop.id}
            imageUrl={crop.imageUrl}
            alt={crop.id}
            size={size}
            borderColor="var(--border)"
            borderRadius={8}
            meta={{
              id: crop.id,
              storeId: crop.storeId ? String(crop.storeId) : undefined,
              source: crop.source,
              forgeOriginSource: crop.forgeOriginSource ?? undefined,
              normalizedBarcode: crop.normalizedBarcode,
              normalizedUpc: crop.normalizedUpc,
              classificationType: crop.classificationType,
              imageResolution: crop.imageResolution ?? undefined,
              createdBy: crop.createdBy ?? undefined,
              createdAt: crop.createdAt ?? undefined,
              updatedBy: crop.updatedBy ?? undefined,
              updatedAt: crop.updatedAt ?? undefined,
            }}
            onImageHover={onImageHover}
            onImageMove={onImageMove}
            onImageLeave={onImageLeave}
          />
        ))}
        {crops.length > visibleCrops.length ? (
          <div
            style={{
              minWidth: 42,
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-tertiary)',
            }}
          >
            +{crops.length - visibleCrops.length}
          </div>
        ) : null}
      </div>
    );
  };

  const renderReferenceMediaSlot = (
    key: string,
    options: {
      imageUrl?: string | null;
      alt: string;
      label?: string;
      icon?: React.ReactNode;
      badge?: React.ReactNode;
      meta?: React.ComponentProps<typeof ProductImagePreview>['meta'];
      theme: MasterBarcodeGroupTheme;
      size: number;
    }
  ) => {
    return (
      <div
        key={key}
        data-testid={key}
        style={{
          width: options.size,
          height: options.size,
          position: 'relative',
          flexShrink: 0,
          borderRadius: 4,
        }}
      >
        {options.imageUrl ? (
          <ProductImagePreview
            imageUrl={options.imageUrl}
            alt={options.alt}
            size={options.size}
            borderColor={options.theme.cardBorder}
            borderRadius={4}
            meta={options.meta}
            onImageHover={onImageHover}
            onImageMove={onImageMove}
            onImageLeave={onImageLeave}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 4,
              border: `1px dashed ${options.theme.cardBorder}`,
              background: options.theme.cardBackground,
              color: options.theme.supportingText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
            }}
          >
            {options.icon ?? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: Math.max(9, Math.round(options.size * 0.2)),
                  fontWeight: 800,
                  lineHeight: 1,
                  opacity: 0.58,
                }}
              >
                {options.label}
              </span>
            )}
          </div>
        )}
        {options.badge ? (
          <span
            style={{
              position: 'absolute',
              top: -5,
              right: -5,
              display: 'inline-flex',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))',
            }}
          >
            {options.badge}
          </span>
        ) : null}
      </div>
    );
  };

  const renderDesignReferenceStrip = (
    targetDesign: MasterDesignData,
    theme: MasterBarcodeGroupTheme,
    size: number
  ) => {
    const representativeCrop =
      targetDesign.crops.find((crop) => crop.id === targetDesign.representativeCropId) ??
      targetDesign.crops[0] ??
      null;
    const isConfirmed = targetDesign.barcodeStatus === 'confirmed';
    const badgeSize = Math.max(13, Math.round(size * 0.24));
    const {
      stackHeightPx: auxStackHeight,
      gapPx: auxGap,
      slotSizePx: auxSize,
    } = resolveMasterLabelAuxImageSizing(size);
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 8,
          padding: '4px 0 2px',
          minWidth: 0,
        }}
      >
        {renderReferenceMediaSlot(`group-front-${targetDesign.id}`, {
          imageUrl: isConfirmed ? targetDesign.frontImageUrl : null,
          alt: 'Front',
          label: 'F',
          badge: isConfirmed ? (
            <CheckCircleIcon style={{ fontSize: badgeSize, color: '#E8976C' }} />
          ) : undefined,
          theme,
          size,
        })}
        {renderReferenceMediaSlot(`group-rep-${targetDesign.id}`, {
          imageUrl: representativeCrop?.imageUrl,
          alt: 'Representative',
          label: 'R',
          badge: representativeCrop ? (
            <StarIcon style={{ fontSize: badgeSize, color: '#E8976C' }} />
          ) : undefined,
          meta: representativeCrop
            ? {
                id: representativeCrop.id,
                storeId: representativeCrop.storeId
                  ? String(representativeCrop.storeId)
                  : undefined,
                source: representativeCrop.source,
                forgeOriginSource: representativeCrop.forgeOriginSource ?? undefined,
                normalizedBarcode: representativeCrop.normalizedBarcode,
                normalizedUpc: representativeCrop.normalizedUpc,
                classificationType: representativeCrop.classificationType,
              }
            : undefined,
          theme,
          size,
        })}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: auxGap,
            width: auxSize,
            height: auxStackHeight,
            flexShrink: 0,
          }}
        >
          {renderReferenceMediaSlot(`group-back-${targetDesign.id}`, {
            imageUrl: isConfirmed ? targetDesign.backImageUrl : null,
            alt: 'Back',
            label: 'B',
            meta: isConfirmed && targetDesign.backImageUrl ? { source: 'design back' } : undefined,
            theme,
            size: auxSize,
          })}
          {renderReferenceMediaSlot(`group-barcode-${targetDesign.id}`, {
            imageUrl: isConfirmed ? targetDesign.barcodeImageUrl : null,
            alt: 'Barcode',
            icon: (
              <QrCode2OutlinedIcon
                style={{
                  fontSize: Math.max(13, Math.round(auxSize * 0.44)),
                  color: theme.supportingText,
                }}
              />
            ),
            meta:
              isConfirmed && targetDesign.barcodeImageUrl
                ? { source: 'design barcode' }
                : undefined,
            theme,
            size: auxSize,
          })}
        </div>
      </div>
    );
  };

  const renderDesignHeader = (
    targetUpc: MasterUPCData,
    targetDesign: MasterDesignData,
    theme: MasterBarcodeGroupTheme
  ) => (
    <div style={{ minWidth: 0 }}>
      {targetUpc.productName ? (
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: theme.upcText,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: 3,
          }}
        >
          {targetUpc.productName}
        </div>
      ) : null}
      <div
        data-testid={`design-group-design-nb-${targetDesign.id}`}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: theme.upcText,
          fontWeight: 700,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginBottom: 2,
        }}
      >
        <span style={{ color: theme.supportingText, fontWeight: 500 }}>nb: </span>
        {targetDesign.normalizedBarcode || 'none'}
      </div>
      <div
        data-testid={`design-group-design-id-${targetDesign.id}`}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: theme.supportingText,
          fontWeight: 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <span>d: </span>
        {targetDesign.id}
      </div>
    </div>
  );

  const renderExistingGroupSection = (
    scope: ForgeDesignGroupScope,
    groupDesigns:
      | MasterDesignData['fullGroupDesigns']
      | MasterDesignData['partialGroupDesigns']
      | MasterDesignData['nonFrontGroupDesigns']
  ) => {
    const scopeTone = getForgeDesignGroupScopeTone(scope);
    const scopeSubtleTone = getForgeDesignGroupScopeSubtleTone(scope);
    const groupReview = getDesignGroupReview(design, scope);
    const canConvertGroupScope =
      Boolean(groupDesigns?.length) &&
      (groupDesigns ?? []).every((member) => openDesignById.has(member.designId));

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 'var(--radius-sm)',
          border: `1px solid color-mix(in srgb, var(--border) 70%, ${scopeTone} 30%)`,
          borderLeft: `4px solid ${scopeTone}`,
          background: `linear-gradient(135deg, color-mix(in srgb, var(--bg) 88%, ${scopeSubtleTone} 12%), var(--bg))`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: scopeTone }}>
            {getForgeDesignGroupScopeLabel(scope)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {groupReview && onToggleGroupReview ? (
              <ReviewStatusControl
                isReviewed={groupReview.isReviewed}
                canToggle
                isBusy={isSubmitting}
                reviewedLabel="Clear group review"
                unreviewedLabel="Mark group reviewed"
                reviewedBy={groupReview.reviewedBy}
                reviewedAt={groupReview.reviewedAt}
                testId={`design-group-review-${scope}`}
                onToggle={() => onToggleGroupReview(scope, groupReview)}
              />
            ) : null}
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-tertiary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {groupDesigns?.length ?? 0} in group
            </div>
            {groupDesigns && groupDesigns.length > 0 ? (
              <button
                type="button"
                data-testid={`design-group-leave-${scope}`}
                disabled={isSubmitting}
                onClick={() => onLeaveGroup(scope)}
                style={{
                  padding: '5px 9px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  background: 'rgba(239, 68, 68, 0.08)',
                  color: 'var(--red)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: 10,
                  fontWeight: 700,
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                Leave {getForgeDesignGroupScopeLabel(scope)} group
              </button>
            ) : null}
          </div>
        </div>
        {groupDesigns && groupDesigns.length > 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {FORGE_DESIGN_GROUP_SCOPES.filter((targetScope) => targetScope !== scope).map(
              (targetScope) => {
                const targetTone = getForgeDesignGroupScopeTone(targetScope);
                return (
                  <button
                    key={`${scope}-to-${targetScope}`}
                    type="button"
                    data-testid={`design-group-convert-${scope}-to-${targetScope}`}
                    disabled={isSubmitting || !canConvertGroupScope}
                    title={
                      canConvertGroupScope
                        ? `Change to ${getForgeDesignGroupScopeLabel(targetScope)}`
                        : 'Load all group members before changing type'
                    }
                    onClick={() => onConvertGroupScope(scope, targetScope)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid color-mix(in srgb, ${targetTone} 42%, var(--border))`,
                      background: 'var(--bg)',
                      color:
                        isSubmitting || !canConvertGroupScope ? 'var(--text-tertiary)' : targetTone,
                      cursor: isSubmitting || !canConvertGroupScope ? 'not-allowed' : 'pointer',
                      fontSize: 10,
                      fontWeight: 700,
                      opacity: isSubmitting || !canConvertGroupScope ? 0.6 : 1,
                    }}
                  >
                    <SwapHorizIcon style={{ fontSize: 13 }} />
                    Change to {getForgeDesignGroupScopeLabel(targetScope)}
                  </button>
                );
              }
            )}
          </div>
        ) : null}
        {groupDesigns && groupDesigns.length > 0 ? (
          groupDesigns.map((member) => {
            const loadedDesign = openDesignById.get(member.designId);
            const memberTheme = loadedDesign?.theme ?? currentTheme;
            const previewCrops = loadedDesign?.design.crops ?? [];
            return (
              <div
                key={`${scope}-${member.designId}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px',
                  borderRadius: 'var(--radius-sm)',
                  border: `1px solid ${memberTheme.cardBorder}`,
                  background: memberTheme.nestedSurface,
                }}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, flex: 1 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <ProductImagePreview
                      imageUrl={member.previewVizUrl}
                      alt={member.designId}
                      size={Math.min(72, groupCropPreviewSize)}
                      borderColor={scopeTone}
                      borderRadius={8}
                      meta={{
                        id: member.designId,
                        normalizedBarcode: member.normalizedBarcode ?? undefined,
                        classificationType: 'product',
                      }}
                      onImageHover={onImageHover}
                      onImageMove={onImageMove}
                      onImageLeave={onImageLeave}
                    />
                    <div style={{ minWidth: 0 }}>
                      {loadedDesign?.upc.productName ? (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            color: 'var(--text-primary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginBottom: 3,
                          }}
                        >
                          {loadedDesign.upc.productName}
                        </div>
                      ) : null}
                      <div
                        data-testid={`design-group-member-nb-${scope}-${member.designId}`}
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 11,
                          fontWeight: 700,
                          color: memberTheme.upcText,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginBottom: 2,
                        }}
                      >
                        <span style={{ color: memberTheme.supportingText, fontWeight: 500 }}>
                          nb:{' '}
                        </span>
                        {member.normalizedBarcode || 'none'}
                      </div>
                      <div
                        data-testid={`design-group-member-id-${scope}-${member.designId}`}
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          color: memberTheme.supportingText,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <span>d: </span>
                        {member.designId}
                      </div>
                    </div>
                  </div>
                  {loadedDesign ? (
                    renderCropStrip(previewCrops, groupCropPreviewSize, 'No crops loaded.', 6)
                  ) : (
                    <div
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      Load this group member to compare all crops.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    disabled={!member.designId || isSubmitting}
                    onClick={() => {
                      onLoadGroupMember(member);
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg)',
                      color:
                        member.designId && !isSubmitting
                          ? 'var(--text-primary)'
                          : 'var(--text-tertiary)',
                      cursor: member.designId && !isSubmitting ? 'pointer' : 'not-allowed',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {loadedDesign ? 'Highlight' : 'Load column'}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}
          >
            No {getForgeDesignGroupScopeLabel(scope).toLowerCase()} group members.
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="design-group-manager-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(1320px, 98vw)',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-elevated)',
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LinkOutlinedIcon style={{ fontSize: 16, color: 'var(--accent)' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                Design groups
              </div>
            </div>
            <div
              style={{
                marginTop: 6,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              <span>{upc.itemNumber}</span>
              <span>nb: {design.normalizedBarcode || 'none'}</span>
              <span>d: {design.id}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(360px, 0.8fr)',
            gap: 12,
            minHeight: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${currentTheme.cardBorder}`,
                background: currentTheme.nestedSurface,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: currentTheme.upcText }}>
                Current design
              </div>
              {renderDesignHeader(upc, design, currentTheme)}
              {renderDesignReferenceStrip(
                design,
                currentTheme,
                Math.max(42, Math.min(58, Math.round(candidateCropPreviewSize * 0.58)))
              )}
              {renderCropStrip(
                design.crops,
                sourceCropPreviewSize,
                'This design has no loaded crops.',
                10
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Add currently open designs to this group
                </div>
                {hiddenZeroCropCandidateCount > 0 ? (
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid="design-group-show-zero-crop-toggle"
                      checked={showZeroCropCandidates}
                      onChange={(event) => setShowZeroCropCandidates(event.target.checked)}
                      style={{ width: 12, height: 12, accentColor: 'var(--accent)' }}
                    />
                    Show {hiddenZeroCropCandidateCount} empty design
                    {hiddenZeroCropCandidateCount === 1 ? '' : 's'}
                  </label>
                ) : null}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                }}
              >
                Load another item/design in Master Label to make it available here.
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {candidateDesigns.length > 0 ? (
                  candidateDesigns.map((candidate) => {
                    const result = candidate.summary;
                    const alreadyFull = fullGroupDesignIds.has(result.designId);
                    const alreadyPartial = partialGroupDesignIds.has(result.designId);
                    const alreadyNonFront = nonFrontGroupDesignIds.has(result.designId);

                    return (
                      <div
                        key={result.designId}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) 168px',
                          gap: 10,
                          padding: '10px',
                          borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${candidate.theme.cardBorder}`,
                          background: candidate.theme.nestedSurface,
                        }}
                      >
                        <div
                          style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}
                        >
                          {renderDesignHeader(candidate.upc, candidate.design, candidate.theme)}
                          {renderDesignReferenceStrip(
                            candidate.design,
                            candidate.theme,
                            Math.max(40, Math.min(54, Math.round(candidateCropPreviewSize * 0.54)))
                          )}
                          {renderCropStrip(
                            candidate.design.crops,
                            candidateCropPreviewSize,
                            'No crops loaded.',
                            6
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            gap: 6,
                            flexShrink: 0,
                          }}
                        >
                          <button
                            type="button"
                            data-testid={`design-group-add-full-${result.designId}`}
                            disabled={isSubmitting || alreadyFull}
                            onClick={() => onAddToGroup('full', result.designId)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid rgba(16, 185, 129, 0.24)',
                              background: alreadyFull ? 'rgba(16, 185, 129, 0.12)' : 'var(--bg)',
                              color: alreadyFull ? 'var(--positive)' : 'var(--text-primary)',
                              cursor: isSubmitting || alreadyFull ? 'not-allowed' : 'pointer',
                              fontSize: 11,
                              fontWeight: 700,
                              opacity: isSubmitting ? 0.6 : 1,
                            }}
                          >
                            {alreadyFull ? 'In Full group' : 'Full'}
                          </button>
                          <button
                            type="button"
                            data-testid={`design-group-add-partial-${result.designId}`}
                            disabled={isSubmitting || alreadyPartial}
                            onClick={() => onAddToGroup('partial', result.designId)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid rgba(245, 158, 11, 0.24)',
                              background: alreadyPartial ? 'rgba(245, 158, 11, 0.12)' : 'var(--bg)',
                              color: alreadyPartial ? 'var(--warning)' : 'var(--text-primary)',
                              cursor: isSubmitting || alreadyPartial ? 'not-allowed' : 'pointer',
                              fontSize: 11,
                              fontWeight: 700,
                              opacity: isSubmitting ? 0.6 : 1,
                            }}
                          >
                            {alreadyPartial ? 'In Partial group' : 'Partial'}
                          </button>
                          <button
                            type="button"
                            data-testid={`design-group-add-non-front-${result.designId}`}
                            disabled={isSubmitting || alreadyNonFront}
                            onClick={() => onAddToGroup('non-front', result.designId)}
                            style={{
                              padding: '6px 10px',
                              borderRadius: 'var(--radius-sm)',
                              border: '1px solid rgba(239, 68, 68, 0.26)',
                              background: alreadyNonFront ? 'var(--danger-subtle)' : 'var(--bg)',
                              color: alreadyNonFront ? 'var(--danger)' : 'var(--text-primary)',
                              cursor: isSubmitting || alreadyNonFront ? 'not-allowed' : 'pointer',
                              fontSize: 11,
                              fontWeight: 700,
                              opacity: isSubmitting ? 0.6 : 1,
                            }}
                          >
                            {alreadyNonFront ? 'In Non-front group' : 'Non-front'}
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {allCandidateDesigns.length > 0 && hiddenZeroCropCandidateCount > 0
                      ? 'Only empty designs are available. Enable "Show empty designs" to include them.'
                      : 'No other open designs. Load another item number first, then open this panel again.'}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
            {renderExistingGroupSection('full', design.fullGroupDesigns ?? [])}
            {renderExistingGroupSection('partial', design.partialGroupDesigns ?? [])}
            {renderExistingGroupSection('non-front', design.nonFrontGroupDesigns ?? [])}
          </div>
        </div>
      </div>
    </div>
  );
};

const DesignGroupConflictOverlay: React.FC<{
  conflict: {
    scope: ForgeDesignGroupScope;
    targetDesignId: string;
    targetSummary: ForgeDesignGroupSearchResult;
  };
  onMove: () => void;
  onClose: () => void;
}> = ({ conflict, onMove, onClose }) => {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return (
    <div
      data-testid="design-group-conflict-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.56)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1150,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(680px, 96vw)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-elevated)',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Move existing group relationship?
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: 'var(--text-tertiary)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {conflict.targetSummary.normalizedBarcode || conflict.targetDesignId}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '12px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(245, 158, 11, 0.24)',
            background: 'rgba(245, 158, 11, 0.08)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
            These designs are already grouped in the other scope.
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            Confirming will remove the opposite-scope group relationship in this draft and move it
            to {getForgeDesignGroupScopeLabel(conflict.scope)}. Nothing is saved until you press the
            main Save button.
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="design-group-conflict-move-scope"
            onClick={onMove}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--accent-border)',
              background: 'var(--accent)',
              color: 'var(--on-accent, #fff)',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            Move to {getForgeDesignGroupScopeLabel(conflict.scope)}
          </button>
        </div>
      </div>
    </div>
  );
};

const describeBackendCropMutation = (mutation: MasterLabelBackendCropMutation): string[] =>
  mutation.changedFields.map((field) => {
    switch (field) {
      case 'source':
        return `source: ${formatMutationValue(mutation.baseSource)} -> ${formatMutationValue(mutation.nextSource)}`;
      case 'forge_origin_source':
        return (
          `forge_origin_source: ${formatMutationValue(mutation.baseForgeOriginSource)} -> ` +
          `${formatMutationValue(mutation.nextForgeOriginSource)}`
        );
      case 'barcode':
        return `barcode: ${formatMutationValue(mutation.baseBarcode)} -> ${formatMutationValue(mutation.nextBarcode)}`;
      case 'normalized_barcode':
        return `normalized_barcode: ${formatMutationValue(mutation.baseNormalizedBarcode)} -> ${formatMutationValue(mutation.nextNormalizedBarcode)}`;
      case 'item_number':
        return `item_number: ${formatMutationValue(mutation.baseItemNumber)} -> ${formatMutationValue(mutation.nextItemNumber)}`;
      case 'normalized_upc':
        return `normalized_upc: ${formatMutationValue(mutation.baseNormalizedUpc)} -> ${formatMutationValue(mutation.nextNormalizedUpc)}`;
      case 'design_id':
        return `design_id: ${formatMutationValue(mutation.baseDesignId)} -> ${formatMutationValue(mutation.nextDesignId)}`;
      case 'orientation':
        return `orientation: ${formatMutationValue(mutation.baseOrientation)} -> ${formatMutationValue(mutation.nextOrientation)}`;
      case 'classification_type':
        return `classification_type: ${formatMutationValue(mutation.baseClassificationType)} -> ${formatMutationValue(mutation.nextClassificationType)}`;
      case 'is_active':
        return `is_active: ${formatMutationValue(mutation.baseIsActive)} -> ${formatMutationValue(mutation.nextIsActive)}`;
      case 'is_pending':
        return `is_pending: ${formatMutationValue(mutation.baseIsPending)} -> ${formatMutationValue(mutation.nextIsPending)}`;
      case 'pending_reason':
        return `pending_reason: ${formatMutationValue(mutation.basePendingReason)} -> ${formatMutationValue(mutation.nextPendingReason)}`;
      case 'review_issue_type':
        return `review_issue_type: ${formatReviewIssueType(mutation.baseReviewIssueType)} -> ${formatReviewIssueType(mutation.nextReviewIssueType)}`;
      default:
        return field;
    }
  });

const resolveBackendCropCurrentNormalizedBarcode = (
  mutation: MasterLabelBackendCropMutation
): string | null =>
  mutation.currentNormalizedBarcode ??
  mutation.nextNormalizedBarcode ??
  mutation.baseNormalizedBarcode;

const resolveBackendCropCurrentIsActive = (mutation: MasterLabelBackendCropMutation): boolean =>
  mutation.currentIsActive ?? mutation.nextIsActive ?? mutation.baseIsActive;

const resolveBackendCropCurrentSource = (mutation: MasterLabelBackendCropMutation): string | null =>
  mutation.currentSource ?? mutation.nextSource ?? mutation.baseSource;

const resolveBackendCropCurrentForgeOriginSource = (
  mutation: MasterLabelBackendCropMutation
): string | null =>
  mutation.currentForgeOriginSource ??
  mutation.nextForgeOriginSource ??
  mutation.baseForgeOriginSource;

const resolveBackendCropCurrentOrientation = (
  mutation: MasterLabelBackendCropMutation
): string | null =>
  mutation.currentOrientation ?? mutation.nextOrientation ?? mutation.baseOrientation;

const resolveBackendCropCurrentClassificationType = (
  mutation: MasterLabelBackendCropMutation
): string | null =>
  mutation.currentClassificationType ??
  mutation.nextClassificationType ??
  mutation.baseClassificationType;

const resolveBackendCropCurrentReviewIssueType = (
  mutation: MasterLabelBackendCropMutation
): string | null =>
  mutation.currentReviewIssueType ??
  mutation.nextReviewIssueType ??
  mutation.baseReviewIssueType ??
  null;

const resolveBackendDesignCurrentNormalizedBarcode = (
  mutation: MasterLabelBackendDesignMutation
): string | null =>
  mutation.currentNormalizedBarcode ??
  mutation.nextNormalizedBarcode ??
  mutation.baseNormalizedBarcode;

const describeBackendCropContext = (mutation: MasterLabelBackendCropMutation): string | null => {
  const parts: string[] = [];
  if (mutation.baseNormalizedBarcode === mutation.nextNormalizedBarcode) {
    parts.push(
      `normalized_barcode=${formatMutationValue(resolveBackendCropCurrentNormalizedBarcode(mutation))}`
    );
  }
  if (mutation.baseIsActive === mutation.nextIsActive) {
    parts.push(`is_active=${formatMutationValue(resolveBackendCropCurrentIsActive(mutation))}`);
  }
  if (mutation.baseSource === mutation.nextSource) {
    parts.push(`source=${formatMutationValue(resolveBackendCropCurrentSource(mutation))}`);
  }
  if (
    mutation.baseForgeOriginSource === mutation.nextForgeOriginSource &&
    resolveBackendCropCurrentForgeOriginSource(mutation) !== null
  ) {
    parts.push(
      `forge_origin_source=${formatMutationValue(resolveBackendCropCurrentForgeOriginSource(mutation))}`
    );
  }
  if (mutation.baseOrientation === mutation.nextOrientation) {
    parts.push(
      `orientation=${formatMutationValue(resolveBackendCropCurrentOrientation(mutation))}`
    );
  }
  if (mutation.baseClassificationType === mutation.nextClassificationType) {
    parts.push(
      `type=${formatMutationValue(resolveBackendCropCurrentClassificationType(mutation))}`
    );
  }
  if (mutation.baseReviewIssueType === mutation.nextReviewIssueType) {
    parts.push(
      `review_issue_type=${formatReviewIssueType(resolveBackendCropCurrentReviewIssueType(mutation))}`
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

const describeBackendEmbeddingContext = (mutation: MasterLabelBackendEmbeddingMutation): string =>
  [
    `normalized_barcode=${formatMutationValue(mutation.currentNormalizedBarcode)}`,
    `is_active=${formatMutationValue(mutation.currentIsActive)}`,
  ].join(' · ');

const describeBackendEmbeddingMutation = (
  mutation: MasterLabelBackendEmbeddingMutation
): string[] =>
  mutation.changedFields.map((field) => {
    switch (field) {
      case 'is_active':
        return 'linked ProductEmbedding.is_active changed';
      case 'normalized_barcode':
        return 'linked ProductEmbedding.normalized_barcode changed';
      default:
        return `${field} changed`;
    }
  });

const describeBackendDesignMutation = (mutation: MasterLabelBackendDesignMutation): string[] => {
  const lines: string[] = [];
  if (mutation.baseNormalizedBarcode !== mutation.nextNormalizedBarcode) {
    lines.push(
      `Normalized barcode: ${formatMutationValue(mutation.baseNormalizedBarcode)} -> ${formatMutationValue(mutation.nextNormalizedBarcode)}`
    );
  }
  if (mutation.baseRepresentativeCropId !== mutation.nextRepresentativeCropId) {
    lines.push(
      `Representative: ${formatMutationValue(mutation.baseRepresentativeCropId)} -> ${formatMutationValue(mutation.nextRepresentativeCropId)}`
    );
  }
  return lines;
};

const describeBackendDeletionMutation = (
  mutation: MasterLabelBackendDeletionMutation
): string[] => [
  `item_number=${formatMutationValue(mutation.baseItemNumber)}`,
  `design_id=${formatMutationValue(mutation.baseDesignId)}`,
  `normalized_barcode=${formatMutationValue(mutation.baseNormalizedBarcode)}`,
  `review_issue_type=${formatReviewIssueType(mutation.baseReviewIssueType)}`,
  `deleted_embeddings=${formatMutationValue(mutation.deletedEmbeddingsCount)}`,
];

const buildSaveErrorData = (error: unknown): NonNullable<SaveReviewData['error']> => {
  if (error instanceof ForgeApiError) {
    return {
      message: error.message,
      status: error.status,
      detail: error.detail,
      rawBody: error.rawBody,
    };
  }

  if (error instanceof Error) {
    if (error.message === 'Failed to fetch') {
      return {
        message: 'Failed to fetch',
        detail:
          'The browser did not receive an HTTP response. This usually means the backend crashed, the request was blocked by CORS, or the API is unreachable. Check the backend logs.',
      };
    }
    return {
      message: error.message,
    };
  }

  return {
    message: 'Save failed',
  };
};

const SaveReviewDialog: React.FC<{
  data: SaveReviewData;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onClose: () => void;
}> = ({ data, enabled, onEnabledChange, onClose }) => {
  const backendCropMutations = data.backendCropMutations ?? [];
  const backendDesignMutations = data.backendDesignMutations ?? [];
  const backendEmbeddingMutations = data.backendEmbeddingMutations ?? [];
  const backendDeletionMutations = data.backendDeletionMutations ?? [];
  const feDesignGroupChangeTotal = data.debugInfo.designGroupChanges.length;
  const backendDesignGroupChangeTotal = data.backendSummary?.designGroupChanges ?? 0;
  const backendRecordChangeTotal =
    backendCropMutations.length > 0 ||
    backendDesignMutations.length > 0 ||
    backendEmbeddingMutations.length > 0 ||
    backendDeletionMutations.length > 0
      ? backendCropMutations.length +
        backendDesignMutations.length +
        backendEmbeddingMutations.length +
        backendDeletionMutations.length
      : (data.backendSummary?.cropChanges ?? 0) +
        (data.backendSummary?.embeddingChanges ?? 0) +
        (data.backendSummary?.representativeChanges ?? 0) +
        (data.backendSummary?.designsCreated ?? 0) +
        (data.backendSummary?.cropsDeleted ?? 0);
  const backendFieldMutationTotal =
    backendCropMutations.length > 0 ||
    backendDesignMutations.length > 0 ||
    backendEmbeddingMutations.length > 0 ||
    backendDeletionMutations.length > 0
      ? backendCropMutations.reduce((total, mutation) => total + mutation.changedFields.length, 0) +
        backendDesignMutations.length +
        backendEmbeddingMutations.reduce(
          (total, mutation) => total + mutation.changedFields.length,
          0
        ) +
        backendDeletionMutations.length
      : backendRecordChangeTotal;
  const backendTotalChangeUnits = backendRecordChangeTotal + backendDesignGroupChangeTotal;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.66)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(1080px, 96vw)',
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--table-header-bg)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Master Label Save Review
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              FE logical changes: {data.debugInfo.summary.totalChanges}
              {` · FE design groups: ${feDesignGroupChangeTotal}`}
              {data.backendSummary
                ? ` · Backend records changed: ${backendRecordChangeTotal} · Backend design groups: ${backendDesignGroupChangeTotal} · Backend field mutations: ${backendFieldMutationTotal}`
                : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                className="forge-checkbox"
                checked={enabled}
                onChange={(event) => onEnabledChange(event.target.checked)}
              />
              Open after each save
            </label>
            <CloseIcon
              onClick={onClose}
              style={{ fontSize: 18, cursor: 'pointer', color: 'var(--text-tertiary)' }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 0.9fr)',
            gap: 0,
            minHeight: 0,
            flex: 1,
          }}
        >
          <div
            style={{
              overflowY: 'auto',
              padding: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                FE Summary
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7,
                }}
              >
                <div>Total logical changes: {data.debugInfo.summary.totalChanges}</div>
                <div>
                  Changed items: {data.debugInfo.summary.dirtyItemNumbers.join(', ') || 'none'}
                </div>
                {data.debugInfo.summary.itemSummaries.map((itemSummary) => (
                  <div key={itemSummary.itemNumber}>
                    {itemSummary.itemNumber}: {itemSummary.changeCount} total
                    {' · '}
                    {itemSummary.cropChangeCount} crop placements
                    {' · '}
                    {itemSummary.representativeChangeCount} representative updates
                    {' · '}
                    {itemSummary.designNoteChangeCount} design notes
                    {' · '}
                    {itemSummary.draftDesignCount} draft designs
                    {' · '}
                    {itemSummary.designGroupChangeCount} design groups
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                FE Design Group Changes
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.debugInfo.designGroupChanges.length > 0 ? (
                  data.debugInfo.designGroupChanges.map((change) => (
                    <div
                      key={change.designId}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.55,
                        paddingBottom: 8,
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {change.itemNumber} · {change.normalizedBarcode || 'no barcode'} ·{' '}
                        {change.designId}
                      </div>
                      <div>
                        full: {change.baseFullGroupDesigns.join(', ') || 'none'}
                        {' -> '}
                        {change.nextFullGroupDesigns.join(', ') || 'none'}
                      </div>
                      <div>
                        partial: {change.basePartialGroupDesigns.join(', ') || 'none'}
                        {' -> '}
                        {change.nextPartialGroupDesigns.join(', ') || 'none'}
                      </div>
                      <div>
                        non-front: {change.baseNonFrontGroupDesigns.join(', ') || 'none'}
                        {' -> '}
                        {change.nextNonFrontGroupDesigns.join(', ') || 'none'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No design group changes.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                FE Crop Changes
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.debugInfo.cropChanges.length > 0 ? (
                  data.debugInfo.cropChanges.map((change) => (
                    <div
                      key={change.cropId}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        paddingBottom: 6,
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)' }}>{change.cropId}</div>
                      <div>
                        {change.baseItemNumber || 'none'} / {change.baseSection || 'none'} /{' '}
                        {change.baseDesignId || 'no-design'} / {change.baseOrientation || 'none'}
                        {' -> '}
                        {change.nextItemNumber || 'none'} / {change.nextSection || 'none'} /{' '}
                        {change.nextDesignId || 'no-design'} / {change.nextOrientation || 'none'}
                      </div>
                      {(change.baseReviewIssueType || change.nextReviewIssueType) && (
                        <div>
                          review_issue_type: {formatReviewIssueType(change.baseReviewIssueType)}{' '}
                          {' -> '}
                          {formatReviewIssueType(change.nextReviewIssueType)}
                        </div>
                      )}
                      {change.baseMarkedForDeletion !== change.nextMarkedForDeletion && (
                        <div>
                          marked_for_deletion: {change.baseMarkedForDeletion ? 'true' : 'false'}{' '}
                          {' -> '}
                          {change.nextMarkedForDeletion ? 'true' : 'false'}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No crop placement changes.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Backend Deletion Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {backendDeletionMutations.length > 0 ? (
                  backendDeletionMutations.map((mutation) => (
                    <div
                      key={`${mutation.cropId}-deletion`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        padding: 10,
                        border: '1px solid var(--divider)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)' }}>{mutation.cropId}</div>
                      {describeBackendDeletionMutation(mutation).map((line) => (
                        <div key={`${mutation.cropId}-deletion-${line}`}>{line}</div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No backend deletion mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                FE Design Changes
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.debugInfo.representativeChanges.map((change) => (
                  <div
                    key={change.designId}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Representative {change.designId}: {change.baseRepresentativeCropId || 'none'}{' '}
                    {' -> '} {change.nextRepresentativeCropId || 'none'}
                  </div>
                ))}
                {data.debugInfo.draftDesigns.map((design) => (
                  <div
                    key={design.designId}
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Create {design.designId} in {design.itemNumber} with rep{' '}
                    {design.representativeCropId || 'none'} and crops {design.cropIds.join(', ')}
                  </div>
                ))}
                {data.debugInfo.representativeChanges.length === 0 &&
                  data.debugInfo.draftDesigns.length === 0 && (
                    <div
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      No representative or design-creation changes.
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div
            style={{
              overflowY: 'auto',
              padding: 18,
              borderLeft: '1px solid var(--border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              background: 'rgba(255,255,255,0.01)',
            }}
          >
            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Backend Summary
              </div>
              {data.backendSummary ? (
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.7,
                  }}
                >
                  <div>Items saved: {data.backendSummary.itemsSaved}</div>
                  <div>Changed records: {backendRecordChangeTotal}</div>
                  <div>Field mutations: {backendFieldMutationTotal}</div>
                  <div>Crop mutations: {data.backendSummary.cropChanges}</div>
                  <div>Embedding mutations: {data.backendSummary.embeddingChanges}</div>
                  <div>Representative mutations: {data.backendSummary.representativeChanges}</div>
                  <div>Designs created: {data.backendSummary.designsCreated}</div>
                  <div>Design groups changed: {backendDesignGroupChangeTotal}</div>
                  <div>Review issue changes: {data.backendSummary.reviewIssueChanges ?? 0}</div>
                  <div>Crops deleted: {data.backendSummary.cropsDeleted ?? 0}</div>
                  <div>Embeddings deleted: {data.backendSummary.embeddingsDeleted ?? 0}</div>
                  <div>Saved item payload returned: {data.backendItemCount ?? 0}</div>
                </div>
              ) : (
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-tertiary)',
                  }}
                >
                  No backend summary available because the save did not complete.
                </div>
              )}
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Backend Crop Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {backendCropMutations.length > 0 ? (
                  backendCropMutations.map((mutation, index) => {
                    const cropContext = describeBackendCropContext(mutation);
                    const cropLines = describeBackendCropMutation(mutation);
                    return (
                      <div
                        key={mutation.cropId}
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 10,
                          color: 'var(--text-secondary)',
                          lineHeight: 1.5,
                          padding: 10,
                          border: '1px solid var(--divider)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--surface)',
                        }}
                      >
                        <div style={{ color: 'var(--text-primary)' }}>{mutation.cropId}</div>
                        {cropContext && <div>{cropContext}</div>}
                        {cropContext && cropLines.length > 0 && (
                          <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
                        )}
                        {cropLines.map((line) => (
                          <div key={`${mutation.cropId}-${line}`}>{line}</div>
                        ))}
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No backend crop mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Backend Design Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {backendDesignMutations.length > 0 ? (
                  backendDesignMutations.map((mutation) => (
                    <div
                      key={`${mutation.mutationType}-${mutation.designId}`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        padding: 10,
                        border: '1px solid var(--divider)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)' }}>{mutation.designId}</div>
                      <div>Type: {mutation.mutationType}</div>
                      <div>
                        normalized_barcode=
                        {formatMutationValue(
                          resolveBackendDesignCurrentNormalizedBarcode(mutation)
                        )}
                      </div>
                      {describeBackendDesignMutation(mutation).length > 0 && (
                        <>
                          <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
                          {describeBackendDesignMutation(mutation).map((line) => (
                            <div key={`${mutation.designId}-${line}`}>{line}</div>
                          ))}
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No backend design mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Backend Embedding Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {backendEmbeddingMutations.length > 0 ? (
                  backendEmbeddingMutations.map((mutation) => (
                    <div
                      key={`${mutation.cropId}-embedding`}
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        lineHeight: 1.5,
                        padding: 10,
                        border: '1px solid var(--divider)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface)',
                      }}
                    >
                      <div style={{ color: 'var(--text-primary)' }}>{mutation.cropId}</div>
                      <div>Embeddings: {mutation.embeddingsCount}</div>
                      <div>{describeBackendEmbeddingContext(mutation)}</div>
                      <div style={{ margin: '6px 0', borderTop: '1px solid var(--border)' }} />
                      {describeBackendEmbeddingMutation(mutation).map((line) => (
                        <div key={`${mutation.cropId}-embedding-${line}`}>{line}</div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    No backend embedding mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Why FE and BE counts can differ
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                FE counts logical UI actions. Backend `cropChanges` counts crop documents whose
                persisted fields changed during save. Backend embeddings and design groups are
                tracked separately. Crop fields include `normalized_barcode`, `design_id`,
                `orientation`, `classification_type`, `is_active`, `is_pending`,
                `review_issue_type`, and pending-state flags. Hard deletes are reported separately.
              </div>
              {data.backendSummary &&
                backendTotalChangeUnits !== data.debugInfo.summary.totalChanges && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 11,
                      color: 'var(--warning, #e3b341)',
                      lineHeight: 1.6,
                    }}
                  >
                    This save diverged: FE saw {data.debugInfo.summary.totalChanges} logical
                    changes, while backend changed {backendRecordChangeTotal} records,{' '}
                    {backendDesignGroupChangeTotal} design groups, and {backendFieldMutationTotal}{' '}
                    persisted fields. That usually means one UI action rewrote multiple persisted
                    fields or changed a multi-design group.
                  </div>
                )}
            </div>

            {data.error && (
              <div
                style={{
                  padding: 12,
                  border: '1px solid rgba(248,81,73,0.35)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'rgba(248,81,73,0.08)',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--red)',
                  }}
                >
                  Save Error
                </div>
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <div>{data.error.message}</div>
                  {data.error.status !== undefined && <div>Status: {data.error.status}</div>}
                  {data.error.detail && data.error.detail !== data.error.message && (
                    <div>Detail: {data.error.detail}</div>
                  )}
                  {data.error.rawBody && data.error.rawBody !== data.error.detail && (
                    <details>
                      <summary style={{ cursor: 'pointer' }}>Raw backend error body</summary>
                      <pre
                        style={{
                          marginTop: 8,
                          fontSize: 10,
                          lineHeight: 1.5,
                          color: 'var(--text-secondary)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {data.error.rawBody}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            <details
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                }}
              >
                Raw save request
              </summary>
              <pre
                style={{
                  marginTop: 10,
                  fontSize: 10,
                  lineHeight: 1.5,
                  color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(data.debugInfo.request, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrailingColumnTileView: React.FC<{
  tile: MasterLabelTrailingTile;
  onDismiss?: () => void;
}> = ({ tile, onDismiss }) => (
  <div
    data-testid={`master-label-${tile.kind}-tile`}
    style={{
      width: 420,
      minWidth: 420,
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      background: 'var(--surface)',
      display: 'flex',
      flexDirection: 'column',
      color: 'var(--text-tertiary)',
      padding: '18px 16px',
      gap: 16,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {tile.kind === 'loading' ? 'Loading item' : 'No results'}
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {tile.kind === 'loading' ? tile.query : `No results found for ${tile.query}`}
        </div>
      </div>
      {tile.kind === 'no-results' && onDismiss && (
        <button
          onClick={onDismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <CloseIcon style={{ fontSize: 16 }} />
        </button>
      )}
    </div>

    {tile.kind === 'loading' ? (
      <div
        style={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 140,
          gap: 10,
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={22} style={{ color: 'var(--text-tertiary)' }} />
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>Preparing item column...</div>
      </div>
    ) : (
      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 8,
          minHeight: 140,
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          No item metadata or crops matched this `item_number` or `normalized_barcode`.
        </div>
        <div
          style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}
        >
          Try another query or close this tile.
        </div>
      </div>
    )}
  </div>
);

function getDesignNoteKey(note: ForgeDesignNote, index: number): string {
  return note.id ? `id:${note.id}` : `idx:${index}`;
}

function getDesignNotesForEditor(design: Pick<MasterDesignData, 'notes'>): ForgeDesignNote[] {
  return (design.notes ?? []).filter((note) => (note?.text ?? '').trim().length > 0);
}

function getOrdinalDay(day: number): string {
  if (day >= 11 && day <= 13) return `${day}th`;

  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

function formatDesignNoteTimestamp(value?: string | null): string | null {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(parsed);
  const day = getOrdinalDay(parsed.getDate());
  const year = parsed.getFullYear();
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);

  return `${month} ${day}, ${year} ${time}`;
}

interface DesignNoteEditorOverlayProps {
  designLabel: string;
  notes?: ForgeDesignNote[];
  value: string;
  editingNoteKey: string | null;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStartEdit: (note: ForgeDesignNote, noteKey: string) => void;
  onCancelEdit: () => void;
  onDelete: (noteKey: string) => void;
  onClose: () => void;
}

interface DataCollectionRequestOverlayProps {
  upc: MasterUPCData;
  targetsByScope: DataCollectionTargetsByScope;
  selectedScope: DataCollectionTargetScope;
  selections: DataCollectionRequestSelection;
  existingRequests: DataCollectionRequestSelection;
  isLoadingExistingRequests: boolean;
  isSubmitting: boolean;
  onScopeChange: (scope: DataCollectionTargetScope) => void;
  onToggleType: (target: DataCollectionTargetOption, type: DataCollectionRequestedType) => void;
  onSubmit: () => void;
  onClose: () => void;
}

const DATA_COLLECTION_SCOPE_LABELS: Record<DataCollectionTargetScope, string> = {
  item_number: 'Item number',
  normalized_barcode: 'Normalized barcode',
  design: 'Design',
};

const DATA_COLLECTION_TYPE_LABELS: Record<DataCollectionRequestedType, string> = {
  product_image: 'Product image',
  crops: 'Crops / scans',
};
const DATA_COLLECTION_TYPES: DataCollectionRequestedType[] = ['product_image', 'crops'];

const DataCollectionRequestOverlay: React.FC<DataCollectionRequestOverlayProps> = ({
  upc,
  targetsByScope,
  selectedScope,
  selections,
  existingRequests,
  isLoadingExistingRequests,
  isSubmitting,
  onScopeChange,
  onToggleType,
  onSubmit,
  onClose,
}) => {
  const targets = targetsByScope[selectedScope];
  const selectedCount = countDataCollectionSelections(selections);
  const canSubmit = selectedCount > 0 && !isSubmitting;
  return (
    <div
      data-testid="data-collection-request-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(0,0,0,0.46)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        style={{
          width: 'min(720px, 100%)',
          maxHeight: 'min(760px, calc(100vh - 48px))',
          overflow: 'auto',
          background: 'var(--elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) onSubmit();
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>
              Data collection
            </div>
            <div
              style={{
                marginTop: 4,
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                color: 'var(--text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {upc.productName} · {upc.itemNumber || upc.normalizedUpc || upc.columnKey}
            </div>
            {isLoadingExistingRequests ? (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Checking existing requests...
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
            }}
            aria-label="Close data collection request"
          >
            x
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(Object.keys(DATA_COLLECTION_SCOPE_LABELS) as DataCollectionTargetScope[]).map(
            (scope) => {
              const isActive = selectedScope === scope;
              const disabled = targetsByScope[scope].length === 0;
              return (
                <button
                  key={scope}
                  type="button"
                  disabled={disabled}
                  onClick={() => onScopeChange(scope)}
                  data-testid={`data-collection-scope-${scope}`}
                  style={{
                    border: `1px solid ${isActive ? '#3fb950' : 'var(--border)'}`,
                    background: isActive ? 'rgba(63,185,80,0.14)' : 'var(--surface)',
                    color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '7px 10px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {DATA_COLLECTION_SCOPE_LABELS[scope]}
                </button>
              );
            }
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: targets.length > 0 ? 'minmax(0, 1fr)' : '1fr',
            gap: 8,
          }}
        >
          {targets.length > 0 ? (
            targets.map((target) => {
              const selectionKey = getDataCollectionTargetSelectionKey(target);
              const selectedTypes =
                selections.get(selectionKey) ?? new Set<DataCollectionRequestedType>();
              const existingTypes =
                existingRequests.get(selectionKey) ?? new Set<DataCollectionRequestedType>();
              const hasSelection = selectedTypes.size > 0;
              const allRequested = DATA_COLLECTION_TYPES.every((type) => existingTypes.has(type));
              return (
                <div
                  key={`${target.scope}-${target.key}`}
                  data-testid={`data-collection-target-${target.scope}-${target.key}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: target.previewVizUrl
                      ? '56px minmax(0, 1fr) auto auto'
                      : 'minmax(0, 1fr) auto auto',
                    gap: 10,
                    alignItems: 'center',
                    textAlign: 'left',
                    padding: 10,
                    border: `1px solid ${hasSelection ? '#58a6ff' : 'var(--border)'}`,
                    background: hasSelection ? 'rgba(88,166,255,0.12)' : 'var(--bg)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--text-primary)',
                    opacity: allRequested ? 0.66 : 1,
                  }}
                >
                  {target.previewVizUrl ? (
                    <img
                      src={target.previewVizUrl}
                      alt=""
                      style={{
                        width: 48,
                        height: 48,
                        objectFit: 'contain',
                        borderRadius: 4,
                        background: '#050505',
                      }}
                    />
                  ) : null}
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 800,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {target.label}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        marginTop: 4,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        color: 'var(--text-tertiary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {target.detail || target.key}
                    </span>
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {target.cropCount ?? 0} crops
                  </span>
                  <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {DATA_COLLECTION_TYPES.map((type) => {
                      const isExisting = existingTypes.has(type);
                      const isSelected = selectedTypes.has(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          disabled={isExisting || isSubmitting}
                          onClick={() => onToggleType(target, type)}
                          data-testid={`data-collection-toggle-${target.scope}-${target.key}-${type}`}
                          style={{
                            border: `1px solid ${
                              isExisting || isSelected ? '#3fb950' : 'var(--border)'
                            }`,
                            background:
                              isExisting || isSelected ? 'rgba(63,185,80,0.16)' : 'var(--surface)',
                            color: isExisting || isSelected ? '#7ee787' : 'var(--text-secondary)',
                            borderRadius: 999,
                            padding: '7px 10px',
                            cursor: isExisting || isSubmitting ? 'not-allowed' : 'pointer',
                            fontSize: 11,
                            fontWeight: 800,
                            opacity: isExisting ? 0.62 : 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isExisting ? '✓ Requested ' : isSelected ? '✓ ' : ''}
                          {DATA_COLLECTION_TYPE_LABELS[type]}
                        </button>
                      );
                    })}
                  </span>
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: 12,
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-tertiary)',
                fontSize: 12,
              }}
            >
              No targets available for this tab.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 14px',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            Close
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="data-collection-submit"
            style={{
              padding: '9px 14px',
              border: '1px solid #2ea043',
              background: canSubmit ? 'rgba(46,160,67,0.18)' : 'var(--surface)',
              color: canSubmit ? '#7ee787' : 'var(--text-disabled)',
              borderRadius: 'var(--radius-sm)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 800,
            }}
          >
            {isSubmitting
              ? 'Saving...'
              : selectedCount > 0
                ? `Request ${selectedCount}`
                : 'Pick requests'}
          </button>
        </div>
      </form>
    </div>
  );
};

const DesignNoteEditorOverlay: React.FC<DesignNoteEditorOverlayProps> = ({
  designLabel,
  notes = [],
  value,
  editingNoteKey,
  onChange,
  onSubmit,
  onStartEdit,
  onCancelEdit,
  onDelete,
  onClose,
}) => (
  <div
    data-testid="design-note-editor-overlay"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1300,
      background: 'rgba(0,0,0,0.46)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}
  >
    <form
      style={{
        width: 'min(520px, 100%)',
        background: 'var(--elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-lg)',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Design notes
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {designLabel}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: 18,
            lineHeight: 1,
          }}
          aria-label="Close note editor"
        >
          x
        </button>
      </div>
      <div
        data-testid="design-note-existing-list"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 220,
          overflowY: 'auto',
          padding: 8,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg)',
        }}
      >
        {notes.length > 0 ? (
          notes.map((note, index) => {
            const noteKey = getDesignNoteKey(note, index);
            const isEditing = editingNoteKey === noteKey;
            const formattedCreatedAt = formatDesignNoteTimestamp(note.createdAt);
            return (
              <div
                key={noteKey}
                data-testid={`design-note-existing-${noteKey}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  gap: 10,
                  padding: 10,
                  borderRadius: 'var(--radius-sm)',
                  border: isEditing ? '1px solid var(--accent-border)' : '1px solid var(--divider)',
                  background: isEditing ? 'var(--purple-subtle)' : 'var(--surface)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      color: 'var(--text-primary)',
                      fontSize: 12,
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {note.text}
                  </div>
                  {(note.createdBy || note.createdAt) && (
                    <div
                      style={{
                        marginTop: 6,
                        color: 'var(--text-tertiary)',
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9,
                      }}
                    >
                      {note.createdBy ? `by ${note.createdBy}` : 'unknown author'}
                      {formattedCreatedAt ? ` · ${formattedCreatedAt}` : ''}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <button
                    type="button"
                    data-testid={`design-note-edit-${noteKey}`}
                    onClick={() => onStartEdit(note, noteKey)}
                    style={{
                      height: 26,
                      padding: '0 9px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: isEditing ? 'var(--accent)' : 'var(--surface)',
                      color: isEditing ? '#fff' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`design-note-delete-${noteKey}`}
                    onClick={() => onDelete(noteKey)}
                    style={{
                      height: 26,
                      padding: '0 9px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      background: 'rgba(239, 68, 68, 0.08)',
                      color: 'var(--red)',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              padding: 4,
            }}
          >
            No notes yet.
          </div>
        )}
      </div>
      <textarea
        data-testid="design-note-editor-input"
        autoFocus
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={editingNoteKey ? 'Edit note text' : 'Add note text'}
        rows={5}
        style={{
          width: '100%',
          resize: 'vertical',
          minHeight: 96,
          background: 'var(--bg)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: 10,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {editingNoteKey && (
          <button
            type="button"
            onClick={onCancelEdit}
            style={{
              height: 30,
              padding: '0 12px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Cancel edit
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          style={{
            height: 30,
            padding: '0 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Close
        </button>
        <button
          type="submit"
          disabled={value.trim().length === 0}
          style={{
            height: 30,
            padding: '0 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--accent-border)',
            background: value.trim().length === 0 ? 'var(--surface)' : 'var(--accent)',
            color: value.trim().length === 0 ? 'var(--text-tertiary)' : '#fff',
            cursor: value.trim().length === 0 ? 'not-allowed' : 'pointer',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {editingNoteKey ? 'Save note' : 'Add note'}
        </button>
      </div>
    </form>
  </div>
);

function cropQueryReferenceCropIds(group: RemoteDataCollectionCandidateGroup): string[] {
  return [
    ...new Set(group.candidates.flatMap((candidate) => candidate.referenceCropIds ?? [])),
  ].slice(0, 6);
}

function cropQueryPrimaryBarcode(group: RemoteDataCollectionCandidateGroup): string | null {
  return (
    group.normalizedBarcodes[0] ||
    group.candidates.find((candidate) => candidate.normalizedBarcode)?.normalizedBarcode ||
    (group.keyKind === 'barcode' ? group.key : null)
  );
}

function cropQueryCandidateItemNumber(
  group: RemoteDataCollectionCandidateGroup,
  item?: MasterUPCData | null
): string | null {
  return (
    group.itemNumber ||
    group.candidates.find((candidate) => candidate.itemNumber)?.itemNumber ||
    item?.actualItemNumber ||
    (item?.lookupKind === 'item_number' ? item.itemNumber : null) ||
    null
  );
}

function cropQueryItemNumberMatch(
  targetItemNumber: string | null,
  candidateItemNumber: string | null
): CandidateItemNumberMatch {
  if (!targetItemNumber || !candidateItemNumber) return 'unknown';
  return targetItemNumber === candidateItemNumber ? 'same' : 'different';
}

type CropQueryColumnTarget = {
  itemNumber?: string | null;
  normalizedBarcode?: string | null;
  fallbackQuery: string;
  label: string;
};

type MasterLabelLoadLookupKind = 'auto' | 'item_number' | 'normalized_barcode' | 'design_id';

type MasterLabelLoadOptions = {
  lookupKind?: MasterLabelLoadLookupKind;
  normalizedBarcodeHighlight?: string | null;
};

function cropQueryValueOrDash(value?: string | number | null): string {
  if (value === null || value === undefined) return '-';
  const text = String(value);
  return text.trim().length ? text : '-';
}

function cropQueryReferenceCrop(
  crop: CropEmbeddingSearchResponse
): RemoteDataCollectionReferenceCrop {
  return {
    cropId: crop.productEmbeddingCropId || crop.vizUrl,
    vizUrl: crop.vizUrl,
    normalizedBarcode: crop.normalizedBarcode || crop.barcode || null,
    itemNumber: crop.itemNumber !== undefined ? String(crop.itemNumber) : null,
    designId: crop.designId ?? null,
    designName: crop.designName ?? null,
    classificationType: crop.classificationType === 'carton' ? 'carton' : 'product',
    source: crop.source,
    orientation:
      crop.orientation === 'front' || crop.orientation === 'not-front' ? crop.orientation : null,
  };
}

function CropQuerySummaryRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value?: string | number | null;
  mono?: boolean;
}) {
  return (
    <div className="mdc-summary-row">
      <span>{label}</span>
      <strong className={mono ? 'mdc-summary-value--mono' : undefined}>
        {cropQueryValueOrDash(value)}
      </strong>
    </div>
  );
}

interface MasterLabelCropQueryOverlayProps {
  target: { crop: MasterCropData; upc: MasterUPCData };
  response: MasterLabelCropQueryResponse | null;
  candidateItems: Record<string, MasterUPCData | null>;
  referenceCropsById: Record<string, CropEmbeddingSearchResponse>;
  isLoading: boolean;
  isDetailsLoading: boolean;
  error: string | null;
  chainId: number;
  onAddColumn: (target: CropQueryColumnTarget) => void;
  onClose: () => void;
  onImageHover: ReturnType<typeof useZoomPreview>['handleImageHover'];
  onImageMove: ReturnType<typeof useZoomPreview>['handleImageMove'];
  onImageLeave: ReturnType<typeof useZoomPreview>['handleImageLeave'];
}

const MasterLabelCropQueryOverlay: React.FC<MasterLabelCropQueryOverlayProps> = ({
  target,
  response,
  candidateItems,
  referenceCropsById,
  isLoading,
  isDetailsLoading,
  error,
  chainId,
  onAddColumn,
  onClose,
  onImageHover,
  onImageMove,
  onImageLeave,
}) => {
  const groups = response?.candidateGroups.slice(0, 6) ?? [];
  const queryCrop = target.crop;
  const targetItemNumber = queryCrop.itemNumber || target.upc.actualItemNumber || null;
  const queryCropZoomMeta = {
    id: queryCrop.id,
    storeId: queryCrop.storeId ?? undefined,
    source: queryCrop.source ?? undefined,
    forgeOriginSource: queryCrop.forgeOriginSource ?? undefined,
    normalizedBarcode: queryCrop.normalizedBarcode ?? queryCrop.normalizedUpc ?? undefined,
    classificationType: queryCrop.classificationType ?? undefined,
  };
  const showQueryLoading = isLoading || (!response && !error);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return (
    <div
      className="mdc-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Crop query results"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="mdc-modal master-crop-query-modal">
        <div className="mdc-modal-header">
          <div>
            <h2>Product recognition candidates</h2>
          </div>
          <button type="button" className="mdc-modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="mdc-modal-body mdc-modal-body--detection">
          <section className="mdc-detection-stage master-crop-query-stage">
            <div className="mdc-detail-preview master-crop-query-source-image">
              <img
                src={queryCrop.imageUrl}
                alt="Query crop"
                onMouseEnter={(event) => onImageHover(event, queryCrop.imageUrl, queryCropZoomMeta)}
                onMouseMove={(event) => onImageMove(event, queryCrop.imageUrl, queryCropZoomMeta)}
                onMouseLeave={onImageLeave}
              />
            </div>
            <aside className="mdc-detection-summary-card master-crop-query-summary">
              <div className="mdc-summary-section">
                <div className="mdc-summary-section-title">Query crop</div>
                <CropQuerySummaryRow label="Product" value={target.upc.productName} />
                <CropQuerySummaryRow label="Crop" value={queryCrop.id} mono />
                <CropQuerySummaryRow
                  label="Item"
                  value={queryCrop.itemNumber || target.upc.itemNumber}
                  mono
                />
              </div>
              <div className="mdc-summary-section">
                <div className="mdc-summary-mini-grid">
                  <CropQuerySummaryRow
                    label="Barcode"
                    value={
                      queryCrop.normalizedBarcode ||
                      queryCrop.normalizedUpc ||
                      target.upc.normalizedUpc ||
                      target.upc.upc
                    }
                    mono
                  />
                  <CropQuerySummaryRow
                    label="Model"
                    value={response?.visualEmbeddingModelVersion}
                    mono
                  />
                  <CropQuerySummaryRow label="Chain" value={chainId} mono />
                </div>
              </div>
            </aside>
          </section>

          {showQueryLoading ? (
            <div className="master-crop-query-loading">
              <CircularProgress size={22} />
              <div>
                <strong>Running visual similarity</strong>
                <span>Searching active embeddings for this chain.</span>
              </div>
            </div>
          ) : error ? (
            <div className="mdc-detail-empty">{error}</div>
          ) : groups.length ? (
            <section className="mdc-modal-section">
              <div className="mdc-modal-section-title">
                Top predictions{isDetailsLoading ? ' · loading product evidence' : ''}
              </div>
              <div className="mdc-candidate-grid master-crop-query-grid">
                {groups.map((group, index) => {
                  const rank = index + 1;
                  const item = candidateItems[group.key];
                  const barcode = item?.normalizedUpc || cropQueryPrimaryBarcode(group);
                  const itemNumber = cropQueryCandidateItemNumber(group, item);
                  const referenceCrops = cropQueryReferenceCropIds(group)
                    .map((cropId) => referenceCropsById[cropId])
                    .filter((crop): crop is CropEmbeddingSearchResponse => Boolean(crop));
                  const addColumnTarget: CropQueryColumnTarget = {
                    itemNumber,
                    normalizedBarcode: barcode,
                    fallbackQuery: itemNumber || barcode || group.key,
                    label:
                      item?.productName || group.productName || itemNumber || barcode || group.key,
                  };
                  const referenceCropSamples = referenceCrops.map(cropQueryReferenceCrop);
                  const sampleDesignNamesById = Object.fromEntries(
                    referenceCrops
                      .filter((crop) => crop.designId && crop.designName)
                      .map((crop) => [crop.designId as string, crop.designName as string])
                  );

                  return (
                    <CandidatePreviewCard
                      key={`${group.keyKind}:${group.key}`}
                      group={group}
                      item={item}
                      referenceCrops={referenceCropSamples}
                      rank={rank}
                      chainId={chainId}
                      assignLabel="Add column"
                      assignDisabled={!addColumnTarget.fallbackQuery}
                      itemNumberMatch={cropQueryItemNumberMatch(targetItemNumber, itemNumber)}
                      sampleDesignNamesById={sampleDesignNamesById}
                      showForgeLink={false}
                      samplesLoading={isDetailsLoading}
                      onAssign={() => onAddColumn(addColumnTarget)}
                      onImageHover={onImageHover}
                      onImageMove={onImageMove}
                      onImageLeave={onImageLeave}
                    />
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="mdc-detail-empty">No candidates returned for this crop.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MasterLabelPage: React.FC = () => {
  const { token, canDoTasks, hasInternalAccess } = useAuthContext();

  // URL state
  const [searchParams, setSearchParams] = useSearchParams();

  // Chain state (from query param, default 46)
  const [selectedChainId, setSelectedChainId] = useState<number>(() => {
    const requestedChainId = parseMasterLabelChainId(searchParams);
    return requestedChainId;
  });
  const [labelerChainOptions, setLabelerChainOptions] = useState<ForgeChainOption[] | null>(null);
  const selectedChainIdRef = useRef(selectedChainId);
  const saveSequenceRef = useRef(0);

  // Item entry state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Loaded item-number columns
  const [selectedItemNumbers, setSelectedItemNumbers] = useState<string[]>([]);
  const [trailingColumnTile, setTrailingColumnTile] = useState<MasterLabelTrailingTile | null>(
    null
  );
  const [focusedItemNumber, setFocusedItemNumber] = useState<string | null>(null);
  const [remoteDataCollectionLinksByKey, setRemoteDataCollectionLinksByKey] = useState<
    Record<string, RemoteDataCollectionLinksLookupEntry>
  >({});
  const [remoteDataCollectionStoreLimitByKey, setRemoteDataCollectionStoreLimitByKey] = useState<
    Record<string, number>
  >({});
  const remoteDataCollectionLinksByKeyRef = useRef(remoteDataCollectionLinksByKey);
  const [cropQueryTarget, setCropQueryTarget] = useState<{
    crop: MasterCropData;
    upc: MasterUPCData;
  } | null>(null);
  const [productImageTarget, setProductImageTarget] = useState<{
    crop: MasterCropData;
    upc: MasterUPCData;
  } | null>(null);
  const [isVizSimDatasetDialogOpen, setIsVizSimDatasetDialogOpen] = useState(false);
  const [isCropCompareOpen, setIsCropCompareOpen] = useState(false);
  const [designLockDialogTarget, setDesignLockDialogTarget] =
    useState<DesignLockDialogTarget | null>(null);
  const [isDesignLockSubmitting, setIsDesignLockSubmitting] = useState(false);
  const [cropQueryResponse, setCropQueryResponse] = useState<MasterLabelCropQueryResponse | null>(
    null
  );
  const [cropQueryItems, setCropQueryItems] = useState<Record<string, MasterUPCData | null>>({});
  const [cropQueryReferenceCrops, setCropQueryReferenceCrops] = useState<
    Record<string, CropEmbeddingSearchResponse>
  >({});
  const [isCropQueryLoading, setIsCropQueryLoading] = useState(false);
  const [isCropQueryDetailsLoading, setIsCropQueryDetailsLoading] = useState(false);
  const [cropQueryError, setCropQueryError] = useState<string | null>(null);
  const cropQueryCacheRef = useRef(new Map<string, MasterLabelCropQueryResponse>());
  const deepLinkHandled = useRef(false);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const trailingTileSequence = useRef(0);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveReview, setShowSaveReview] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SAVE_REVIEW_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [saveReviewData, setSaveReviewData] = useState<SaveReviewData | null>(null);
  const [saveWarningPrompt, setSaveWarningPrompt] = useState<SaveWarningPromptState | null>(null);
  const [autoReloadImages, setAutoReloadImages] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTO_RELOAD_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [imageCacheSummary, setImageCacheSummary] =
    useState<ImageCacheSummary>(EMPTY_IMAGE_CACHE_SUMMARY);
  const [isLoadingRemainingImages, setIsLoadingRemainingImages] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [isReviewChooserOpen, setIsReviewChooserOpen] = useState(false);
  const [pendingNewDesign, setPendingNewDesign] = useState<{
    itemNumber: string;
    cropSummary: { total: number; bySource: Record<string, number> };
    availableBarcodes: MasterBarcodeEntry[];
  } | null>(null);
  const [groupManagerTarget, setGroupManagerTarget] = useState<{
    itemNumber: string;
    designId: string;
  } | null>(null);
  const [noteEditorTarget, setNoteEditorTarget] = useState<{
    itemNumber: string;
    designId: string;
    designLabel: string;
  } | null>(null);
  const [dataCollectionTargetUpc, setDataCollectionTargetUpc] = useState<MasterUPCData | null>(
    null
  );
  const [dataCollectionScope, setDataCollectionScope] =
    useState<DataCollectionTargetScope>('item_number');
  const [dataCollectionSelections, setDataCollectionSelections] =
    useState<DataCollectionRequestSelection>(() => new Map());
  const [existingDataCollectionRequests, setExistingDataCollectionRequests] =
    useState<DataCollectionRequestSelection>(() => new Map());
  const [isLoadingExistingDataCollectionRequests, setIsLoadingExistingDataCollectionRequests] =
    useState(false);
  const [isCreatingDataCollection, setIsCreatingDataCollection] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNoteKey, setEditingNoteKey] = useState<string | null>(null);
  const [groupConflict, setGroupConflict] = useState<{
    scope: ForgeDesignGroupScope;
    targetDesignId: string;
    targetSummary: ForgeDesignGroupSearchResult;
  } | null>(null);
  const shiftPressedRef = useRef(false);

  const [cropSize, setCropSize] = useState<number>(() => {
    try {
      const v = localStorage.getItem('forge-crop-size');
      const parsed = v ? Number(v) : NaN;
      return Number.isFinite(parsed) ? parsed : 100;
    } catch {
      return 100;
    }
  });
  const [textScale, setTextScale] = useState<number>(() => {
    try {
      const v = localStorage.getItem(TEXT_SCALE_STORAGE_KEY);
      const parsed = v ? Number(v) : NaN;
      return Number.isFinite(parsed) ? parsed : 100;
    } catch {
      return 100;
    }
  });
  const contentRef = useRef<HTMLDivElement>(null);
  const appliedHighlightTaskKeyRef = useRef<string | null>(null);
  const pendingHighlightResolutionRef = useRef<{
    itemNumber: string | null;
    orderBefore: string[];
    unresolvedCandidates: string[];
  } | null>(null);
  const [taskHighlightCropIds, setTaskHighlightCropIds] = useState<Set<string>>(new Set());
  const [taskHighlightBaselineById, setTaskHighlightBaselineById] = useState<
    Map<string, HighlightedCropSnapshot>
  >(new Map());
  const [focusedHighlight, setFocusedHighlight] = useState<FocusedHighlightInfo | null>(null);

  const { notify, NotificationToast } = useNotification();
  const chrome = React.useMemo(() => resolveMasterLabelChromeSizing(textScale), [textScale]);

  const editor = useMasterLabelEditor({ notify });
  const isSubmittingQuery = trailingColumnTile?.kind === 'loading';

  // Stored mod prefixes for the loaded item columns; agents can override them
  // and manual entries win over automatic resolution.
  const [itemModPrefixes, setItemModPrefixes] = useState<Record<string, string>>({});
  const fetchedModPrefixItemNumbersRef = useRef<Set<string>>(new Set());
  // Item numbers edited in this session: a fetch started before the save must
  // never overwrite the value the agent just stored.
  const manuallySetModPrefixesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    fetchedModPrefixItemNumbersRef.current = new Set();
    manuallySetModPrefixesRef.current = new Set();
    setItemModPrefixes({});
  }, [selectedChainId]);

  // Only real item numbers have prefixes; barcode-only columns carry a synthetic
  // column key that must never reach the store.
  const modPrefixItemNumbers = React.useMemo(
    () =>
      [
        ...new Set(
          editor.upcData
            .map((upc) => upc.actualItemNumber)
            .filter((itemNumber): itemNumber is string => Boolean(itemNumber))
        ),
      ].sort(),
    [editor.upcData]
  );
  const modPrefixItemNumbersKey = modPrefixItemNumbers.join(',');

  useEffect(() => {
    if (!token) return undefined;
    const pending = modPrefixItemNumbers.filter(
      (itemNumber) => !fetchedModPrefixItemNumbersRef.current.has(itemNumber)
    );
    if (pending.length === 0) return undefined;
    pending.forEach((itemNumber) => fetchedModPrefixItemNumbersRef.current.add(itemNumber));
    // The endpoint caps each request, so ask in batches instead of dropping the tail.
    const batches: string[][] = [];
    for (let index = 0; index < pending.length; index += MOD_PREFIX_REQUEST_BATCH_SIZE) {
      batches.push(pending.slice(index, index + MOD_PREFIX_REQUEST_BATCH_SIZE));
    }
    const requestedChainId = selectedChainId;
    void Promise.all(
      batches.map((batch) => ForgeApiService.fetchItemModPrefixes(token, requestedChainId, batch))
    )
      .then((responses) => {
        const entries = responses.flatMap((response) => response.entries);
        // Editing a column re-runs this effect; only a chain switch invalidates
        // an in-flight response, and dropping it here would strand these item
        // numbers because the ref already marks them as fetched.
        if (entries.length === 0 || selectedChainIdRef.current !== requestedChainId) return;
        setItemModPrefixes((prev) => {
          const next = { ...prev };
          for (const entry of entries) {
            if (manuallySetModPrefixesRef.current.has(entry.itemNumber)) continue;
            next[entry.itemNumber] = entry.modPrefix;
          }
          return next;
        });
      })
      .catch(() => {
        // Allow a later column load to retry these item numbers.
        pending.forEach((itemNumber) => fetchedModPrefixItemNumbersRef.current.delete(itemNumber));
      });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedChainId, modPrefixItemNumbersKey]);

  const handleModPrefixEdit = useCallback(
    (itemNumber: string, currentPrefix: string | null) => {
      const input = window.prompt(
        `Mod prefix for ${itemNumber} (2 characters, e.g. GR):`,
        currentPrefix ?? ''
      );
      if (input === null) return;
      const value = input.trim().toUpperCase();
      if (!/^[A-Z0-9]{2}$/.test(value)) {
        notify('Mod prefix must be 2 letters or digits', 'warning');
        return;
      }
      const requestedChainId = selectedChainId;
      void ForgeApiService.setItemModPrefix(token, requestedChainId, itemNumber, value)
        .then((entry) => {
          // A chain switch during the save would otherwise file this prefix
          // under the chain now on screen.
          if (selectedChainIdRef.current !== requestedChainId) return;
          manuallySetModPrefixesRef.current.add(itemNumber);
          setItemModPrefixes((prev) => ({ ...prev, [itemNumber]: entry.modPrefix }));
          notify(`Saved mod prefix ${entry.modPrefix} for ${itemNumber}`);
        })
        .catch(() => notify('Failed to save mod prefix', 'error'));
    },
    [notify, token, selectedChainId]
  );

  const {
    handleImageHover,
    handleImageMove,
    handleImageLeave,
    handleMetadataHover,
    handleMetadataMove,
    handleMetadataLeave,
    ZoomOverlay,
    preloadImages,
    zoomEnabled,
    toggleZoom,
  } = useZoomPreview();
  const parsedUrlTargets = React.useMemo(
    () => parseMasterLabelUrlTargets(searchParams),
    [searchParams]
  );
  const highlightedNormalizedBarcodes = React.useMemo(
    () => parsedUrlTargets.normalizedBarcodes,
    [parsedUrlTargets]
  );
  const highlightedDesignIds = React.useMemo(() => parsedUrlTargets.designIds, [parsedUrlTargets]);
  const highlightedNormalizedBarcode = highlightedNormalizedBarcodes[0] ?? '';
  const highlightedDesignId = highlightedDesignIds[0] ?? '';
  const highlightedNormalizedBarcodeSet = React.useMemo(
    () => new Set(highlightedNormalizedBarcodes),
    [highlightedNormalizedBarcodes]
  );
  const highlightedDesignIdSet = React.useMemo(
    () => new Set(highlightedDesignIds),
    [highlightedDesignIds]
  );
  const highlightedCropIdsFromUrl = React.useMemo(
    () => new Set(parsedUrlTargets.cropIds),
    [parsedUrlTargets]
  );
  const secondaryHighlightedCropIds = React.useMemo(
    () => new Set(parsedUrlTargets.secondaryCropIds),
    [parsedUrlTargets]
  );
  const tertiaryHighlightedCropIds = React.useMemo(
    () => new Set(parsedUrlTargets.tertiaryCropIds),
    [parsedUrlTargets]
  );
  const rawHighlightPresetKey = searchParams.get('m');
  const highlightPresetKey: HighlightPresetKey | null =
    rawHighlightPresetKey && rawHighlightPresetKey in HIGHLIGHT_PRESETS
      ? (rawHighlightPresetKey as HighlightPresetKey)
      : null;
  const rawHighlightScope = searchParams.get('ms');
  const highlightScope: CleaningIssueScope | null =
    rawHighlightScope === 'one_design' || rawHighlightScope === 'full_group'
      ? rawHighlightScope
      : null;
  const highlightPreset = highlightPresetKey ? HIGHLIGHT_PRESETS[highlightPresetKey] : null;
  const taskHighlightedCropIdsFromUrl = React.useMemo(() => {
    if (highlightPresetKey === 'coherence_confusion') {
      return new Set(secondaryHighlightedCropIds);
    }
    const ids = new Set(highlightedCropIdsFromUrl);
    if (highlightPresetKey === 'peer_groups') {
      secondaryHighlightedCropIds.forEach((cropId) => ids.add(cropId));
    }
    return ids;
  }, [highlightPresetKey, highlightedCropIdsFromUrl, secondaryHighlightedCropIds]);
  const highlightTaskKeyEvidenceGroups = React.useMemo(
    () =>
      resolveHighlightTaskKeyEvidenceGroups(
        highlightPresetKey,
        highlightedCropIdsFromUrl,
        secondaryHighlightedCropIds,
        tertiaryHighlightedCropIds
      ),
    [
      highlightPresetKey,
      highlightedCropIdsFromUrl,
      secondaryHighlightedCropIds,
      tertiaryHighlightedCropIds,
    ]
  );
  const highlightTaskKey = React.useMemo(
    () =>
      buildMasterLabelHighlightTaskKey(
        selectedChainId,
        selectedItemNumbers,
        taskHighlightedCropIdsFromUrl,
        highlightPresetKey,
        highlightTaskKeyEvidenceGroups.secondaryCropIds,
        highlightTaskKeyEvidenceGroups.tertiaryCropIds
      ),
    [
      highlightPresetKey,
      highlightTaskKeyEvidenceGroups,
      selectedChainId,
      selectedItemNumbers,
      taskHighlightedCropIdsFromUrl,
    ]
  );
  const currentHighlightStateById = React.useMemo(
    () => buildHighlightedCropSnapshotLookup(editor.upcData),
    [editor.upcData]
  );
  const currentHighlightStateByIdRef = useRef(currentHighlightStateById);
  useEffect(() => {
    currentHighlightStateByIdRef.current = currentHighlightStateById;
  }, [currentHighlightStateById]);
  const taskHighlightCropIdsRef = useRef(taskHighlightCropIds);
  useEffect(() => {
    taskHighlightCropIdsRef.current = taskHighlightCropIds;
  }, [taskHighlightCropIds]);
  const taskHighlightBaselineByIdRef = useRef(taskHighlightBaselineById);
  useEffect(() => {
    taskHighlightBaselineByIdRef.current = taskHighlightBaselineById;
  }, [taskHighlightBaselineById]);
  const unresolvedHighlightCropIds = React.useMemo(() => {
    const nextIds = new Set<string>();
    taskHighlightCropIds.forEach((cropId) => {
      const baseline = taskHighlightBaselineById.get(cropId);
      const current = currentHighlightStateById.get(cropId);
      if (baseline && current && areHighlightedCropSnapshotsEqual(baseline, current)) {
        nextIds.add(cropId);
      }
    });
    return nextIds;
  }, [currentHighlightStateById, taskHighlightBaselineById, taskHighlightCropIds]);
  const displayPrimaryHighlightCropIds = React.useMemo(() => {
    if (highlightPresetKey === 'coherence_confusion') return highlightedCropIdsFromUrl;
    if (highlightPresetKey !== 'peer_groups') return unresolvedHighlightCropIds;
    return new Set(
      [...unresolvedHighlightCropIds].filter((cropId) => highlightedCropIdsFromUrl.has(cropId))
    );
  }, [highlightPresetKey, highlightedCropIdsFromUrl, unresolvedHighlightCropIds]);
  const displaySecondaryHighlightCropIds = React.useMemo(() => {
    if (highlightPresetKey === 'coherence_confusion') return unresolvedHighlightCropIds;
    if (highlightPresetKey !== 'peer_groups') return secondaryHighlightedCropIds;
    return new Set(
      [...unresolvedHighlightCropIds].filter((cropId) => secondaryHighlightedCropIds.has(cropId))
    );
  }, [highlightPresetKey, secondaryHighlightedCropIds, unresolvedHighlightCropIds]);
  const navigableHighlightCropIds = React.useMemo(() => {
    const ids = new Set([
      ...displayPrimaryHighlightCropIds,
      ...displaySecondaryHighlightCropIds,
      ...tertiaryHighlightedCropIds,
    ]);
    return new Set([...ids].filter((cropId) => currentHighlightStateById.has(cropId)));
  }, [
    currentHighlightStateById,
    displayPrimaryHighlightCropIds,
    displaySecondaryHighlightCropIds,
    tertiaryHighlightedCropIds,
  ]);
  const unresolvedHighlightCropIdsRef = useRef(unresolvedHighlightCropIds);
  useEffect(() => {
    unresolvedHighlightCropIdsRef.current = unresolvedHighlightCropIds;
  }, [unresolvedHighlightCropIds]);

  // Persist crop size and section style to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('forge-crop-size', String(cropSize));
    } catch {
      /* ignore */
    }
  }, [cropSize]);

  useEffect(() => {
    try {
      localStorage.setItem(TEXT_SCALE_STORAGE_KEY, String(textScale));
    } catch {
      /* ignore */
    }
  }, [textScale]);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_RELOAD_STORAGE_KEY, String(autoReloadImages));
    } catch {
      /* ignore */
    }
  }, [autoReloadImages]);

  useEffect(() => {
    try {
      localStorage.setItem(SAVE_REVIEW_STORAGE_KEY, String(showSaveReview));
    } catch {
      /* ignore */
    }
  }, [showSaveReview]);

  // Preload a small crop-image head first, then hydrate the rest after the initial screen is usable.
  const preloadUpcImages = useCallback(
    (data: MasterUPCData, priorityCropIds?: Iterable<string>) => {
      const urls = collectMasterLabelUpcCropImageUrls(data, priorityCropIds);
      const initialUrls = urls.slice(0, MASTER_LABEL_INITIAL_PRELOAD_IMAGE_COUNT);
      const deferredUrls = urls.slice(MASTER_LABEL_INITIAL_PRELOAD_IMAGE_COUNT);

      if (initialUrls.length > 0) {
        preloadImages(initialUrls, {
          batchSize: 4,
          delayMs: 150,
        });
      }
      if (deferredUrls.length > 0) {
        preloadImages(deferredUrls, {
          batchSize: 2,
          delayMs: 300,
          startDelayMs: MASTER_LABEL_DEFERRED_PRELOAD_START_DELAY_MS,
        });
      }
    },
    [preloadImages]
  );

  const allCropImageUrls = React.useMemo(
    () => collectMasterLabelCropImageUrls(editor.upcData),
    [editor.upcData]
  );

  const refreshImageCacheSummary = useCallback(() => {
    const nextSummary = getImageCacheSummary(allCropImageUrls);
    setImageCacheSummary((previousSummary) =>
      hasImageCacheSummaryChanged(previousSummary, nextSummary) ? nextSummary : previousSummary
    );
  }, [allCropImageUrls]);

  useEffect(() => {
    refreshImageCacheSummary();
    if (allCropImageUrls.length === 0) return;
    const interval = setInterval(refreshImageCacheSummary, 1500);
    return () => clearInterval(interval);
  }, [allCropImageUrls.length, refreshImageCacheSummary]);

  const loadRemainingImages = useCallback(
    (forceRetry: boolean) => {
      const remainingUrls = allCropImageUrls.filter((url) => {
        const status = getCachedImageStatus(url);
        return status === 'idle' || status === 'failed';
      });

      if (remainingUrls.length === 0) {
        refreshImageCacheSummary();
        return 0;
      }

      preloadImages(remainingUrls, {
        forceRetry,
        batchSize: 2,
        delayMs: 250,
      });
      refreshImageCacheSummary();
      return remainingUrls.length;
    },
    [allCropImageUrls, preloadImages, refreshImageCacheSummary]
  );

  const handleLoadRemainingImages = useCallback(() => {
    const count = loadRemainingImages(true);
    setIsLoadingRemainingImages(count > 0);
    if (count > 0) {
      setReloadVersion((prev) => prev + 1);
      notify(`Retrying ${count} remaining crop image${count !== 1 ? 's' : ''}`);
    } else {
      notify('No remaining crop images to reload');
    }
    setTimeout(() => setIsLoadingRemainingImages(false), 1500);
  }, [loadRemainingImages, notify]);

  useEffect(() => {
    if (!autoReloadImages || allCropImageUrls.length === 0) return;
    const kick = () => {
      const count = loadRemainingImages(true);
      if (count > 0) {
        setReloadVersion((prev) => prev + 1);
        setIsLoadingRemainingImages(true);
        setTimeout(() => setIsLoadingRemainingImages(false), 1500);
      }
    };
    kick();
    const interval = setInterval(kick, 8000);
    return () => clearInterval(interval);
  }, [autoReloadImages, allCropImageUrls.length, loadRemainingImages]);

  const buildCanonicalUrlStateFromVisibleColumns = useCallback(
    (visibleUpcs: MasterUPCData[], additions?: Partial<MasterLabelUrlTargets>) => {
      const targetsToPrune: MasterLabelUrlTargets = {
        itemNumbers: [],
        normalizedBarcodes: mergeUniqueValues(
          parsedUrlTargets.normalizedBarcodes,
          additions?.normalizedBarcodes ?? []
        ),
        designIds: mergeUniqueValues(parsedUrlTargets.designIds, additions?.designIds ?? []),
        cropIds: mergeUniqueValues(parsedUrlTargets.cropIds, additions?.cropIds ?? []),
        secondaryCropIds: mergeUniqueValues(
          parsedUrlTargets.secondaryCropIds,
          additions?.secondaryCropIds ?? []
        ),
        tertiaryCropIds: mergeUniqueValues(
          parsedUrlTargets.tertiaryCropIds,
          additions?.tertiaryCropIds ?? []
        ),
      };
      return pruneUrlTargetsForVisibleColumns(targetsToPrune, visibleUpcs);
    },
    [parsedUrlTargets]
  );

  const syncUrlAfterHighlightChange = useCallback(
    (visibleUpcs: MasterUPCData[], additions?: Partial<MasterLabelUrlTargets>) => {
      const nextTargets = buildCanonicalUrlStateFromVisibleColumns(visibleUpcs, additions);
      setSearchParams(
        buildCanonicalMasterLabelSearchParams(
          selectedChainId,
          nextTargets,
          highlightPresetKey,
          highlightScope
        ),
        { replace: true }
      );
    },
    [
      buildCanonicalUrlStateFromVisibleColumns,
      highlightPresetKey,
      highlightScope,
      selectedChainId,
      setSearchParams,
    ]
  );

  const syncUrlAfterAddColumn = useCallback(
    (visibleUpcs: MasterUPCData[], additions?: Partial<MasterLabelUrlTargets>) => {
      syncUrlAfterHighlightChange(visibleUpcs, additions);
    },
    [syncUrlAfterHighlightChange]
  );

  const syncUrlAfterRemoveColumn = useCallback(
    (visibleUpcs: MasterUPCData[]) => {
      syncUrlAfterHighlightChange(visibleUpcs);
    },
    [syncUrlAfterHighlightChange]
  );

  const syncUrlAfterChainChange = useCallback(
    (chainId: number) => {
      const emptyTargets = parseMasterLabelUrlTargets(new URLSearchParams());
      setSearchParams(buildCanonicalMasterLabelSearchParams(chainId, emptyTargets), {
        replace: true,
      });
    },
    [setSearchParams]
  );

  const handleSelectedChainChange = useCallback(
    (chainId: number) => {
      const nextChainId =
        canDoTasks ||
        labelerChainOptions === null ||
        labelerChainOptions.some((chain) => chain.id === chainId)
          ? chainId
          : selectedChainIdRef.current;
      selectedChainIdRef.current = nextChainId;
      saveSequenceRef.current += 1;
      setSelectedChainId(nextChainId);
    },
    [canDoTasks, labelerChainOptions]
  );

  useEffect(() => {
    if (canDoTasks || labelerChainOptions === null || labelerChainOptions.length === 0) {
      return;
    }
    if (!labelerChainOptions.some((chain) => chain.id === selectedChainId)) {
      handleSelectedChainChange(labelerChainOptions[0].id);
    }
  }, [handleSelectedChainChange, canDoTasks, labelerChainOptions, selectedChainId]);

  useEffect(() => {
    if (canDoTasks || !token) {
      setLabelerChainOptions(null);
      return;
    }

    let cancelled = false;
    ForgeApiService.fetchSupportedChains(token)
      .then((response) => {
        if (cancelled) return;
        const options = buildSupportedChainOptions(response.chains);
        setLabelerChainOptions(options);
        if (options.length === 0) return;

        const currentChainId = selectedChainIdRef.current;
        if (options.some((chain) => chain.id === currentChainId)) {
          return;
        }

        const requestedChainId = parseMasterLabelChainId(searchParams);
        const requestedAllowed = options.some((chain) => chain.id === requestedChainId);
        const nextChainId = requestedAllowed ? requestedChainId : options[0].id;
        selectedChainIdRef.current = nextChainId;
        saveSequenceRef.current += 1;
        setSelectedChainId(nextChainId);
      })
      .catch(() => {
        if (!cancelled) {
          setLabelerChainOptions(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canDoTasks, searchParams, token]);

  // Reset loaded items when chain changes
  const prevChainId = useRef(selectedChainId);
  useEffect(() => {
    if (prevChainId.current === selectedChainId) {
      selectedChainIdRef.current = selectedChainId;
      return;
    }
    prevChainId.current = selectedChainId;
    selectedChainIdRef.current = selectedChainId;
    saveSequenceRef.current += 1;
    setIsSaving(false);
    setSelectedItemNumbers([]);
    setFocusedItemNumber(null);
    setSearchQuery('');
    editor.replaceAllUpcs([]);
    setTrailingColumnTile(null);
    setSaveError(null);
    setSaveReviewData(null);
    setDesignLockDialogTarget(null);
    setIsDesignLockSubmitting(false);
    setImageCacheSummary(EMPTY_IMAGE_CACHE_SUMMARY);
    setGroupManagerTarget(null);
    cropQueryCacheRef.current.clear();
    setCropQueryTarget(null);
    setCropQueryResponse(null);
    setCropQueryItems({});
    setCropQueryReferenceCrops({});
    setCropQueryError(null);
    setIsCropQueryLoading(false);
    setIsCropQueryDetailsLoading(false);
    setDataCollectionTargetUpc(null);
    setGroupConflict(null);
    if (saveWarningPrompt) {
      saveWarningPrompt.resolve(null);
      setSaveWarningPrompt(null);
    }
    syncUrlAfterChainChange(selectedChainId);
  }, [selectedChainId, editor, saveWarningPrompt, syncUrlAfterChainChange]);

  const buildTrailingTile = useCallback(
    (kind: MasterLabelTrailingTile['kind'], query: string): MasterLabelTrailingTile => {
      trailingTileSequence.current += 1;
      return {
        kind,
        query,
        key: `${kind}-${trailingTileSequence.current}-${query}`,
      };
    },
    []
  );

  useEffect(() => {
    if (!focusedItemNumber) return;
    if (editor.upcData.some((upc) => upc.itemNumber === focusedItemNumber)) return;
    setFocusedItemNumber(null);
  }, [editor.upcData, focusedItemNumber]);

  const fetchItemWithPaddingFallback = useCallback(
    async (query: string) => {
      try {
        return await ForgeApiService.fetchItem(token, query, selectedChainId);
      } catch (error) {
        if (!(error instanceof ForgeApiError) || error.status !== 404) {
          throw error;
        }
        const paddedQuery = getPaddedItemNumberQuery(query, selectedChainId);
        if (!paddedQuery || paddedQuery === query.trim()) {
          throw error;
        }
        return ForgeApiService.fetchItem(token, paddedQuery, selectedChainId);
      }
    },
    [selectedChainId, token]
  );

  // Handle deep links: ?in=..., ?nb=..., ?d=..., ?c=... and long-form aliases.
  useEffect(() => {
    if (deepLinkHandled.current || !token) return;
    const itemNumberParams = parsedUrlTargets.itemNumbers;
    const barcodeQueryParams = parsedUrlTargets.normalizedBarcodes;
    const designIdQueryParams = parsedUrlTargets.designIds;
    const cropIdQueryParams =
      highlightPresetKey === 'peer_groups'
        ? mergeUniqueValues(parsedUrlTargets.cropIds, parsedUrlTargets.secondaryCropIds)
        : highlightPresetKey === 'coherence_confusion'
          ? mergeUniqueValues(
              parsedUrlTargets.cropIds,
              parsedUrlTargets.secondaryCropIds,
              parsedUrlTargets.tertiaryCropIds
            )
          : parsedUrlTargets.cropIds;
    if (
      itemNumberParams.length === 0 &&
      barcodeQueryParams.length === 0 &&
      designIdQueryParams.length === 0 &&
      cropIdQueryParams.length === 0
    ) {
      return;
    }
    deepLinkHandled.current = true;
    setDeepLinkLoading(true);

    ForgeApiService.resolveMasterLabelLinkTargets(token, {
      chainId: selectedChainId,
      itemNumbers: itemNumberParams,
      normalizedBarcodes: barcodeQueryParams,
      designIds: designIdQueryParams,
      cropIds: cropIdQueryParams,
    })
      .then((linkResult) => {
        const loaded = new Map<string, MasterUPCData>();
        const failedItems = linkResult.failed.map((failure) => failure.targetValue);
        linkResult.items.forEach((apiData) => {
          const data = transformApiUPCData(apiData);
          if (isDisplayableMasterLabelColumn(data)) {
            loaded.set(data.itemNumber, data);
          } else {
            failedItems.push(
              apiData.itemNumber || apiData.columnKey || apiData.lookupValue || 'query'
            );
          }
        });

        if (loaded.size > 0) {
          const loadedItemNumbers = [...loaded.keys()];
          const loadedItems = [...loaded.values()];
          const initialHighlightedCropIds = new Set(
            highlightPresetKey === 'coherence_confusion'
              ? parsedUrlTargets.secondaryCropIds
              : cropIdQueryParams
          );
          setSelectedItemNumbers(loadedItemNumbers);
          editor.replaceAllUpcs(loadedItems);
          if (initialHighlightedCropIds.size > 0) {
            const baselineLookup = buildHighlightedCropSnapshotLookup(loadedItems);
            const nextBaselineById = new Map<string, HighlightedCropSnapshot>();
            initialHighlightedCropIds.forEach((cropId) => {
              const snapshot = baselineLookup.get(cropId);
              if (snapshot) {
                nextBaselineById.set(cropId, snapshot);
              }
            });
            pendingHighlightResolutionRef.current = null;
            const taskKeyEvidenceGroups = resolveHighlightTaskKeyEvidenceGroups(
              highlightPresetKey,
              parsedUrlTargets.cropIds,
              parsedUrlTargets.secondaryCropIds,
              parsedUrlTargets.tertiaryCropIds
            );
            appliedHighlightTaskKeyRef.current = buildMasterLabelHighlightTaskKey(
              selectedChainId,
              loadedItemNumbers,
              initialHighlightedCropIds,
              highlightPresetKey,
              taskKeyEvidenceGroups.secondaryCropIds,
              taskKeyEvidenceGroups.tertiaryCropIds
            );
            setTaskHighlightCropIds(initialHighlightedCropIds);
            setTaskHighlightBaselineById(nextBaselineById);
            setFocusedHighlight(null);
          }
          scheduleMasterLabelImagePreload(() =>
            loadedItems.forEach((item) => preloadUpcImages(item, initialHighlightedCropIds))
          );
        }

        if (failedItems.length > 0) {
          notify(
            failedItems.length ===
              itemNumberParams.length +
                barcodeQueryParams.length +
                designIdQueryParams.length +
                cropIdQueryParams.length
              ? 'Failed to load item(s) from link'
              : `Some item(s) from link could not be loaded: ${failedItems.join(', ')}`,
            'warning'
          );
        }
      })
      .catch(() => {
        notify('Failed to load item(s) from link', 'warning');
      })
      .finally(() => setDeepLinkLoading(false));
  }, [
    searchParams,
    editor,
    token,
    selectedChainId,
    parsedUrlTargets,
    notify,
    preloadUpcImages,
    highlightPresetKey,
  ]);

  useEffect(() => {
    const hasHighlightTask = taskHighlightedCropIdsFromUrl.size > 0;
    if (!hasHighlightTask) {
      appliedHighlightTaskKeyRef.current = highlightTaskKey;
      pendingHighlightResolutionRef.current = null;
      setTaskHighlightCropIds(new Set());
      setTaskHighlightBaselineById(new Map());
      setFocusedHighlight(null);
      return;
    }
    if (selectedItemNumbers.length === 0 && editor.baseUpcData.length === 0) {
      return;
    }
    if (appliedHighlightTaskKeyRef.current === highlightTaskKey) {
      return;
    }

    const nextIds = new Set(taskHighlightedCropIdsFromUrl);

    const baselineLookup = buildHighlightedCropSnapshotLookup(editor.baseUpcData);
    const nextBaselineById = new Map<string, HighlightedCropSnapshot>();
    nextIds.forEach((cropId) => {
      const snapshot = baselineLookup.get(cropId);
      if (snapshot) {
        nextBaselineById.set(cropId, snapshot);
      }
    });

    pendingHighlightResolutionRef.current = null;
    appliedHighlightTaskKeyRef.current = highlightTaskKey;
    setTaskHighlightCropIds(nextIds);
    setTaskHighlightBaselineById(nextBaselineById);
    setFocusedHighlight(null);
  }, [
    editor.baseUpcData,
    highlightTaskKey,
    selectedItemNumbers.length,
    taskHighlightedCropIdsFromUrl,
  ]);

  const getVisibleHighlightOrder = useCallback(
    (highlightIds: Set<string> = navigableHighlightCropIds) => {
      const container = contentRef.current;
      if (highlightIds.size === 0) return [];
      if (!container) return [...highlightIds];
      const seen = new Set<string>();
      const visibleOrder = Array.from(container.querySelectorAll<HTMLElement>('[data-crop-id]'))
        .map((node) => node.getAttribute('data-crop-id') || '')
        .filter((cropId) => {
          if (!cropId || !highlightIds.has(cropId) || seen.has(cropId)) return false;
          seen.add(cropId);
          return true;
        });
      return visibleOrder.length > 0 ? visibleOrder : [...highlightIds];
    },
    [navigableHighlightCropIds]
  );

  const focusHighlightedCrop = useCallback(
    (cropId: string, behavior: ScrollBehavior = 'smooth') => {
      const cropState =
        currentHighlightStateById.get(cropId) ?? taskHighlightBaselineById.get(cropId);
      setFocusedHighlight({
        type: 'master-label-specific-highlight',
        cropId,
        cropIds: [cropId],
        cropItemNumber: cropState?.itemNumber,
      });

      window.requestAnimationFrame(() => {
        const target = contentRef.current?.querySelector<HTMLElement>(
          `[data-crop-id="${CSS.escape(cropId)}"]`
        );
        target?.scrollIntoView({ behavior, block: 'center', inline: 'center' });
      });
    },
    [currentHighlightStateById, taskHighlightBaselineById]
  );

  const capturePendingHighlightResolution = useCallback(
    (candidateCropIds?: Iterable<string>) => {
      const currentTaskHighlightCropIds = taskHighlightCropIdsRef.current;
      if (currentTaskHighlightCropIds.size === 0) {
        pendingHighlightResolutionRef.current = null;
        return;
      }
      const ids = [
        ...new Set(candidateCropIds ? [...candidateCropIds] : [...editor.selectedCropIds]),
      ];
      const currentUnresolvedHighlightCropIds = unresolvedHighlightCropIdsRef.current;
      const unresolvedCandidates = ids.filter((cropId) =>
        currentUnresolvedHighlightCropIds.has(cropId)
      );
      if (unresolvedCandidates.length === 0) {
        pendingHighlightResolutionRef.current = null;
        return;
      }
      const orderBefore = getVisibleHighlightOrder(currentUnresolvedHighlightCropIds);
      const firstCandidateId =
        orderBefore.find((cropId) => unresolvedCandidates.includes(cropId)) ??
        unresolvedCandidates[0];
      const itemNumber =
        currentHighlightStateByIdRef.current.get(firstCandidateId)?.itemNumber ??
        taskHighlightBaselineByIdRef.current.get(firstCandidateId)?.itemNumber ??
        null;
      pendingHighlightResolutionRef.current = {
        itemNumber,
        orderBefore,
        unresolvedCandidates,
      };
    },
    [editor.selectedCropIds, getVisibleHighlightOrder]
  );

  const navigateVisibleHighlights = useCallback(
    (direction: -1 | 1) => {
      const order = getVisibleHighlightOrder();
      if (order.length === 0) return;
      const currentIndex =
        focusedHighlight?.cropId && navigableHighlightCropIds.has(focusedHighlight.cropId)
          ? order.indexOf(focusedHighlight.cropId)
          : -1;
      const targetIndex =
        currentIndex === -1
          ? direction === 1
            ? 0
            : order.length - 1
          : (currentIndex + direction + order.length) % order.length;
      focusHighlightedCrop(order[targetIndex]);
    },
    [
      focusHighlightedCrop,
      focusedHighlight?.cropId,
      getVisibleHighlightOrder,
      navigableHighlightCropIds,
    ]
  );

  useEffect(() => {
    const pendingResolution = pendingHighlightResolutionRef.current;
    if (!pendingResolution) return;

    const resolvedInOrder = pendingResolution.orderBefore.filter(
      (cropId) =>
        pendingResolution.unresolvedCandidates.includes(cropId) &&
        !unresolvedHighlightCropIds.has(cropId)
    );

    pendingHighlightResolutionRef.current = null;
    if (resolvedInOrder.length === 0) return;

    const firstResolvedId = resolvedInOrder[0];
    const targetItemNumber =
      taskHighlightBaselineById.get(firstResolvedId)?.itemNumber ?? pendingResolution.itemNumber;
    if (!targetItemNumber) {
      const nextInOrder = pendingResolution.orderBefore
        .slice(pendingResolution.orderBefore.indexOf(firstResolvedId) + 1)
        .find((cropId) => unresolvedHighlightCropIds.has(cropId));
      if (nextInOrder) {
        focusHighlightedCrop(nextInOrder);
        return;
      }
      setFocusedHighlight(null);
      return;
    }

    const nextInSameColumn = pendingResolution.orderBefore
      .slice(pendingResolution.orderBefore.indexOf(firstResolvedId) + 1)
      .find((cropId) => {
        if (!unresolvedHighlightCropIds.has(cropId)) return false;
        const cropState =
          currentHighlightStateById.get(cropId) ?? taskHighlightBaselineById.get(cropId);
        return cropState?.itemNumber === targetItemNumber;
      });

    if (nextInSameColumn) {
      focusHighlightedCrop(nextInSameColumn);
      return;
    }

    const orderAfter = getVisibleHighlightOrder();
    const wrappedSameColumn = orderAfter.find((cropId) => {
      const cropState =
        currentHighlightStateById.get(cropId) ?? taskHighlightBaselineById.get(cropId);
      return cropState?.itemNumber === targetItemNumber;
    });
    if (wrappedSameColumn) {
      focusHighlightedCrop(wrappedSameColumn);
      return;
    }

    setFocusedHighlight(null);
  }, [
    currentHighlightStateById,
    focusHighlightedCrop,
    getVisibleHighlightOrder,
    taskHighlightBaselineById,
    unresolvedHighlightCropIds,
  ]);

  useEffect(() => {
    if (!focusedHighlight?.cropId) return;
    if (navigableHighlightCropIds.has(focusedHighlight.cropId)) return;
    if (pendingHighlightResolutionRef.current) return;
    setFocusedHighlight(null);
  }, [focusedHighlight, navigableHighlightCropIds]);

  // Check if any selected crop has source === 'scan' (limited labelers cannot deactivate these)
  const selectedHasScanSource = React.useMemo(() => {
    if (canDoTasks || editor.selectedCropIds.size === 0) return false;
    const ids = editor.selectedCropIds;
    for (const upc of editor.upcData) {
      const allCrops = [
        ...upc.designs.flatMap((d) => d.crops),
        ...(upc.pendingCrops ?? []),
        ...upc.unassignedCrops,
        ...upc.cartonCrops,
        ...(upc.inactiveCrops ?? []),
      ];
      if (allCrops.some((crop) => ids.has(crop.id) && crop.source === 'scan')) return true;
    }
    return false;
  }, [canDoTasks, editor.selectedCropIds, editor.upcData]);
  const selectedVizSimClassName = React.useMemo(() => {
    if (editor.selectedCropIds.size === 0) return null;
    const names = new Set<string>();
    const addName = (value?: string | null) => {
      const normalized = value?.trim();
      if (normalized) names.add(normalized);
    };
    const captureCrop = (crop: MasterCropData, upc: MasterUPCData, design?: MasterDesignData) => {
      if (!editor.selectedCropIds.has(crop.id)) return;
      addName(
        upc.productName ||
          design?.normalizedBarcode ||
          crop.normalizedBarcode ||
          crop.normalizedUpc ||
          crop.barcode
      );
    };

    for (const upc of editor.upcData) {
      for (const design of upc.designs) {
        for (const crop of design.crops) captureCrop(crop, upc, design);
      }
      for (const crop of upc.pendingCrops ?? []) captureCrop(crop, upc);
      for (const crop of upc.unassignedCrops) captureCrop(crop, upc);
      for (const crop of upc.cartonCrops) captureCrop(crop, upc);
      for (const crop of upc.inactiveCrops ?? []) captureCrop(crop, upc);
    }

    if (names.size === 1) return [...names][0];
    return null;
  }, [editor.selectedCropIds, editor.upcData]);
  const contextViewEnabled = useCropContextViewEnabled();
  const canToggleContextView = useCropContextViewAllowed();
  useEffect(() => {
    // Context is internal-only for now.
    setCropContextViewAllowed(hasInternalAccess);
  }, [hasInternalAccess]);

  const cropsToCompare = React.useMemo(
    () => collectSelectedCrops(editor.upcData, editor.selectedCropIds),
    [editor.upcData, editor.selectedCropIds]
  );
  const openCropCompare = useCallback(() => {
    const count = editor.selectedCropIds.size;
    if (count < CROP_COMPARE_MIN) {
      notify(CROP_COMPARE_TOO_FEW_MESSAGE, 'warning');
      return;
    }
    if (count > CROP_COMPARE_MAX) {
      notify(CROP_COMPARE_TOO_MANY_MESSAGE, 'warning');
      return;
    }
    setIsCropCompareOpen(true);
  }, [editor.selectedCropIds, notify]);
  useEffect(() => {
    // A selection cleared behind the popup leaves nothing to compare.
    if (isCropCompareOpen && cropsToCompare.length === 0) setIsCropCompareOpen(false);
  }, [isCropCompareOpen, cropsToCompare.length]);

  const shouldPreserveSelection = useCallback(() => shiftPressedRef.current, []);

  const changeCount = editor.diffSummary.totalChanges;
  const unresolvedHighlightCount = unresolvedHighlightCropIds.size;
  const hasHighlightTask = highlightPreset !== null && taskHighlightCropIds.size > 0;
  const highlightBannerMessage =
    highlightPreset === null
      ? ''
      : highlightScope === 'full_group' && highlightPreset.fullGroupMessage
        ? highlightPreset.fullGroupMessage
        : secondaryHighlightedCropIds.size > 0
          ? highlightPreset.referenceMessage
          : highlightPreset.message;
  const highlightCountLabel = `${navigableHighlightCropIds.size} highlighted`;

  const newDesignOptions = React.useMemo(() => {
    if (!editor.selectedCropItemNumber) return [];
    return [
      {
        itemNumber: editor.selectedCropItemNumber,
        label: 'New Design',
      },
    ];
  }, [editor.selectedCropItemNumber]);

  const openReviewChooser = useCallback(() => {
    if (editor.selectedCropIds.size === 0) return;
    setIsReviewChooserOpen(true);
  }, [editor.selectedCropIds.size]);

  const closeReviewChooser = useCallback(() => {
    setIsReviewChooserOpen(false);
  }, []);

  const handleHighlightedSectionClick = useCallback(
    (target: Parameters<typeof editor.handleSectionClick>[0]) => {
      if (target.type === 'inactive' && selectedHasScanSource) {
        notify('Cannot deactivate scanned crops', 'warning');
        return;
      }
      if (editor.mode === 'crops-selected') {
        capturePendingHighlightResolution();
      }
      editor.handleSectionClick(target, { preserveSelection: shouldPreserveSelection() });
    },
    [
      capturePendingHighlightResolution,
      editor,
      notify,
      selectedHasScanSource,
      shouldPreserveSelection,
    ]
  );

  const handleDeactivateSelected = useCallback(() => {
    capturePendingHighlightResolution();
    editor.deactivateCrops({ preserveSelection: shouldPreserveSelection() });
  }, [capturePendingHighlightResolution, editor, shouldPreserveSelection]);

  const handleCreateNewDesign = useCallback(
    (itemNumber: string, options?: { normalizedBarcode?: string | null }) => {
      // If caller already supplied a barcode (e.g. overlay confirmation), go straight through.
      if (options?.normalizedBarcode) {
        capturePendingHighlightResolution();
        editor.createNewDesign(itemNumber, {
          ...options,
          preserveSelection: shouldPreserveSelection(),
        });
        return;
      }

      const selectedIds = editor.selectedCropIds;
      if (selectedIds.size === 0) return;

      const selectedCrops: MasterCropData[] = [];
      for (const upc of editor.upcData) {
        const cropsInUpc = [
          ...upc.designs.flatMap((design) => design.crops),
          ...(upc.pendingCrops ?? []),
          ...upc.unassignedCrops,
          ...upc.cartonCrops,
          ...(upc.inactiveCrops ?? []),
        ];
        for (const crop of cropsInUpc) {
          if (selectedIds.has(crop.id)) selectedCrops.push(crop);
        }
      }

      if (resolveDraftDesignVerifiedBarcode(selectedCrops)) {
        capturePendingHighlightResolution();
        editor.createNewDesign(itemNumber, {
          preserveSelection: shouldPreserveSelection(),
        });
        return;
      }

      // No scan crop with a barcode — require admin confirmation with an explicit barcode.
      const targetUpc = editor.upcData.find((item) => item.itemNumber === itemNumber);
      const availableBarcodes = targetUpc
        ? getAllBarcodeEntries(targetUpc).filter((entry) => entry.upc.trim().length > 0)
        : [];
      if (availableBarcodes.length === 0) {
        notify('No barcodes available — cannot create design without a scan source', 'error');
        return;
      }

      const bySource: Record<string, number> = {};
      for (const crop of selectedCrops) {
        const sourceKey = crop.source ?? 'unknown';
        bySource[sourceKey] = (bySource[sourceKey] ?? 0) + 1;
      }
      setPendingNewDesign({
        itemNumber,
        cropSummary: { total: selectedCrops.length, bySource },
        availableBarcodes,
      });
    },
    [capturePendingHighlightResolution, editor, notify, shouldPreserveSelection]
  );

  const handleSetRepresentative = useCallback(
    (designId: string, cropId: string) => {
      capturePendingHighlightResolution([cropId]);
      editor.setRepresentative(designId, cropId, { preserveSelection: shouldPreserveSelection() });
    },
    [capturePendingHighlightResolution, editor, shouldPreserveSelection]
  );

  const handleSetSelectedCropOrientation = useCallback(
    (orientation: 'front' | 'not-front') => {
      capturePendingHighlightResolution();
      editor.setSelectedCropOrientation(orientation, {
        preserveSelection: shouldPreserveSelection(),
      });
    },
    [capturePendingHighlightResolution, editor, shouldPreserveSelection]
  );

  const handleMarkSelectedForDeletion = useCallback(() => {
    capturePendingHighlightResolution();
    editor.markSelectedForDeletion({ preserveSelection: shouldPreserveSelection() });
  }, [capturePendingHighlightResolution, editor, shouldPreserveSelection]);

  const applyReviewIssue = useCallback(
    (reviewIssueType: MasterLabelReviewIssueType) => {
      capturePendingHighlightResolution();
      editor.setSelectedReviewIssue(reviewIssueType, {
        preserveSelection: shouldPreserveSelection(),
      });
      setIsReviewChooserOpen(false);
    },
    [capturePendingHighlightResolution, editor, shouldPreserveSelection]
  );

  const clearReviewIssue = useCallback(() => {
    capturePendingHighlightResolution();
    editor.clearSelectedReviewIssue({ preserveSelection: shouldPreserveSelection() });
    setIsReviewChooserOpen(false);
  }, [capturePendingHighlightResolution, editor, shouldPreserveSelection]);

  useEffect(() => {
    if (editor.selectedCropIds.size === 0) {
      if (isReviewChooserOpen) setIsReviewChooserOpen(false);
      if (pendingNewDesign) setPendingNewDesign(null);
    }
  }, [editor.selectedCropIds.size, isReviewChooserOpen, pendingNewDesign]);

  const loadItem = useCallback(
    async (query: string, label: string, options?: MasterLabelLoadOptions) => {
      if (!token) return;
      const trimmedQuery = query.trim();
      const lookupKind = options?.lookupKind ?? 'auto';
      const shouldLookupByDesignId =
        lookupKind === 'design_id' || (lookupKind === 'auto' && isMongoObjectIdQuery(trimmedQuery));
      const shouldLookupByBarcode =
        lookupKind === 'normalized_barcode' ||
        (lookupKind === 'auto' && isNormalizedBarcodeQuery(trimmedQuery, selectedChainId));
      setTrailingColumnTile(buildTrailingTile('loading', label));
      try {
        let data: MasterUPCData;
        let normalizedBarcodeHighlight: string | null = options?.normalizedBarcodeHighlight ?? null;
        let designIdHighlight: string | null = null;
        let cropIdHighlights: string[] | null = null;
        if (shouldLookupByDesignId) {
          try {
            const designLookup = await ForgeApiService.fetchItemByDesignId(
              token,
              trimmedQuery,
              selectedChainId
            );
            data = transformApiUPCData(designLookup.item);
            designIdHighlight = designLookup.designId;
          } catch (error) {
            if (!(error instanceof ForgeApiError) || error.status !== 404) {
              throw error;
            }
            const linkLookup = await ForgeApiService.resolveMasterLabelLinkTargets(token, {
              chainId: selectedChainId,
              itemNumbers: [],
              normalizedBarcodes: [],
              designIds: [],
              cropIds: [trimmedQuery],
            });
            const resolvedCrop = linkLookup.resolvedTargets.find(
              (target) => target.targetType === 'crop_id' && target.targetValue === trimmedQuery
            );
            const matchedItem =
              linkLookup.items.find(
                (item) =>
                  (resolvedCrop?.columnKey && item.columnKey === resolvedCrop.columnKey) ||
                  (resolvedCrop?.itemNumber && item.itemNumber === resolvedCrop.itemNumber) ||
                  (resolvedCrop?.normalizedBarcode &&
                    item.lookupNormalizedBarcode === resolvedCrop.normalizedBarcode)
              ) ?? linkLookup.items[0];
            if (!matchedItem) {
              throw error;
            }
            data = transformApiUPCData(matchedItem);
            cropIdHighlights = [trimmedQuery];
          }
        } else if (shouldLookupByBarcode) {
          const barcodeLookup = await ForgeApiService.fetchItemByBarcode(
            token,
            trimmedQuery,
            selectedChainId
          );
          data = transformApiUPCData(barcodeLookup.item);
          normalizedBarcodeHighlight = barcodeLookup.normalizedBarcode;
        } else if (lookupKind === 'item_number') {
          const apiData = await fetchItemWithPaddingFallback(trimmedQuery);
          data = transformApiUPCData(apiData);
        } else {
          try {
            const apiData = await fetchItemWithPaddingFallback(query);
            data = transformApiUPCData(apiData);
          } catch (error) {
            if (!(error instanceof ForgeApiError) || error.status !== 404) {
              throw error;
            }
            const barcodeLookup = await ForgeApiService.fetchItemByBarcode(
              token,
              trimmedQuery,
              selectedChainId
            );
            data = transformApiUPCData(barcodeLookup.item);
            normalizedBarcodeHighlight = barcodeLookup.normalizedBarcode;
          }
        }

        if (selectedItemNumbers.includes(data.itemNumber)) {
          syncUrlAfterHighlightChange(editor.upcData, {
            normalizedBarcodes: normalizedBarcodeHighlight ? [normalizedBarcodeHighlight] : [],
            designIds: designIdHighlight ? [designIdHighlight] : [],
            cropIds: cropIdHighlights ?? [],
          });
          notify(`Item already loaded: ${data.itemNumber}`, 'warning');
          setTrailingColumnTile(null);
          return;
        }

        if (!isDisplayableMasterLabelColumn(data)) {
          setTrailingColumnTile(buildTrailingTile('no-results', trimmedQuery));
          setSearchQuery('');
          setSearchFocused(false);
          return;
        }

        const updated = [...selectedItemNumbers, data.itemNumber];
        const updatedUpcs = [
          ...editor.upcData.filter((upc) => upc.itemNumber !== data.itemNumber),
          data,
        ];
        setSelectedItemNumbers(updated);
        syncUrlAfterAddColumn(updatedUpcs, {
          normalizedBarcodes: normalizedBarcodeHighlight ? [normalizedBarcodeHighlight] : [],
          designIds: designIdHighlight ? [designIdHighlight] : [],
          cropIds: cropIdHighlights ?? [],
        });
        editor.appendLoadedUpc(data);
        scheduleMasterLabelImagePreload(() => preloadUpcImages(data, cropIdHighlights ?? []));
        setTrailingColumnTile(null);
        setSearchQuery('');
        setSearchFocused(false);
      } catch (error) {
        if (error instanceof ForgeApiError && error.status === 404) {
          setTrailingColumnTile(buildTrailingTile('no-results', trimmedQuery));
          setSearchQuery('');
          setSearchFocused(false);
          return;
        }
        setTrailingColumnTile(null);
        notify(`Failed to load item or barcode: ${trimmedQuery}`, 'error');
      }
    },
    [
      token,
      selectedChainId,
      selectedItemNumbers,
      editor,
      notify,
      syncUrlAfterAddColumn,
      syncUrlAfterHighlightChange,
      preloadUpcImages,
      fetchItemWithPaddingFallback,
      buildTrailingTile,
    ]
  );

  const openCropQuery = useCallback((crop: MasterCropData, upc: MasterUPCData) => {
    setCropQueryResponse(null);
    setCropQueryError(null);
    setCropQueryItems({});
    setCropQueryReferenceCrops({});
    setIsCropQueryLoading(true);
    setIsCropQueryDetailsLoading(false);
    setCropQueryTarget({ crop, upc });
  }, []);

  // Open the product-image dialog for the single selected crop (keyboard "P").
  // Only valid with exactly one crop selected — the "P" hint is hidden otherwise.
  const handleUseSelectedCropAsProductImage = useCallback(() => {
    if (editor.selectedCropIds.size !== 1) return;
    const [cropId] = [...editor.selectedCropIds];
    for (const upc of editor.upcData) {
      const crop = collectUpcCrops(upc).find((c) => c.id === cropId);
      if (crop) {
        setProductImageTarget({ crop, upc });
        return;
      }
    }
  }, [editor.selectedCropIds, editor.upcData]);

  const openVizSimDatasetDialog = useCallback(() => {
    if (!canDoTasks) return;
    if (editor.selectedCropIds.size === 0) return;
    if (!token) {
      notify('Sign in before adding crops to VizSim datasets', 'warning');
      return;
    }
    setIsVizSimDatasetDialogOpen(true);
  }, [editor.selectedCropIds.size, canDoTasks, notify, token]);

  const closeCropQuery = useCallback(() => {
    setCropQueryTarget(null);
    setCropQueryResponse(null);
    setCropQueryItems({});
    setCropQueryReferenceCrops({});
    setCropQueryError(null);
    setIsCropQueryLoading(false);
    setIsCropQueryDetailsLoading(false);
  }, []);

  const addCropQueryColumn = useCallback(
    (target: CropQueryColumnTarget) => {
      if (target.itemNumber) {
        void loadItem(target.itemNumber, target.label, {
          lookupKind: 'item_number',
          normalizedBarcodeHighlight: target.normalizedBarcode,
        });
        return;
      }
      if (target.normalizedBarcode) {
        void loadItem(target.normalizedBarcode, target.label, {
          lookupKind: 'normalized_barcode',
        });
        return;
      }
      void loadItem(target.fallbackQuery, target.label);
    },
    [loadItem]
  );

  useEffect(() => {
    if (!token || !cropQueryTarget) return;

    let cancelled = false;
    const cropId = cropQueryTarget.crop.id;
    const cached = cropQueryCacheRef.current.get(cropId);
    setCropQueryError(null);
    setCropQueryItems({});
    setCropQueryReferenceCrops({});

    if (cached) {
      setCropQueryResponse(cached);
      setIsCropQueryLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setCropQueryResponse(null);
    setIsCropQueryLoading(true);
    ForgeApiService.fetchMasterLabelCropQuery(token, cropId, {
      chainId: selectedChainId,
      limit: 10,
    })
      .then((response) => {
        if (cancelled) return;
        cropQueryCacheRef.current.set(cropId, response);
        setCropQueryResponse(response);
      })
      .catch((error) => {
        if (cancelled) return;
        const message =
          error instanceof ForgeApiError
            ? error.message
            : 'Failed to query this crop. The embedding may not be available yet.';
        setCropQueryError(message);
      })
      .finally(() => {
        if (!cancelled) setIsCropQueryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cropQueryTarget, selectedChainId, token]);

  useEffect(() => {
    if (!token || !cropQueryResponse || !cropQueryTarget) return;
    let cancelled = false;
    const groups = cropQueryResponse.candidateGroups.slice(0, 6);
    if (!groups.length) {
      setCropQueryItems({});
      setCropQueryReferenceCrops({});
      setIsCropQueryDetailsLoading(false);
      return;
    }

    async function loadDetails() {
      setIsCropQueryDetailsLoading(true);
      const cropIds = [...new Set(groups.flatMap((group) => cropQueryReferenceCropIds(group)))];
      const [itemEntries, referenceCrops] = await Promise.all([
        Promise.all(
          groups.map(async (group) => {
            try {
              if (group.itemNumber) {
                const item = await ForgeApiService.fetchItem(
                  token,
                  group.itemNumber,
                  selectedChainId
                );
                return [group.key, transformApiUPCData(item)] as const;
              }
              const barcode = cropQueryPrimaryBarcode(group);
              if (barcode) {
                const response = await ForgeApiService.fetchItemByBarcode(
                  token,
                  barcode,
                  selectedChainId
                );
                return [group.key, transformApiUPCData(response.item)] as const;
              }
            } catch {
              return [group.key, null] as const;
            }
            return [group.key, null] as const;
          })
        ),
        cropIds.length
          ? ForgeApiService.searchEmbeddingsByCropIds(token, cropIds).catch(
              (): CropEmbeddingSearchResponse[] => []
            )
          : Promise.resolve([]),
      ]);

      if (cancelled) return;
      setCropQueryItems(Object.fromEntries(itemEntries));
      setCropQueryReferenceCrops(
        Object.fromEntries(
          referenceCrops
            .filter((crop) => crop.productEmbeddingCropId)
            .map((crop) => [crop.productEmbeddingCropId as string, crop])
        )
      );
      setIsCropQueryDetailsLoading(false);
    }

    void loadDetails().finally(() => {
      if (!cancelled) setIsCropQueryDetailsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cropQueryResponse, cropQueryTarget, selectedChainId, token]);

  // Check if an item has pending (unsaved) changes
  const upcHasChanges = useCallback(
    (itemNumber: string): boolean => {
      return editor.upcHasChanges(itemNumber);
    },
    [editor]
  );
  const displayUpcData = React.useMemo(
    () => normalizeDesignGroups(selectedChainId, editor.upcData),
    [editor.upcData, selectedChainId]
  );
  const currentGroupManagerContext = React.useMemo(
    () => resolveLoadedDesignContext(displayUpcData, groupManagerTarget),
    [displayUpcData, groupManagerTarget]
  );
  const currentNoteEditorContext = React.useMemo(
    () => resolveLoadedDesignContext(displayUpcData, noteEditorTarget),
    [displayUpcData, noteEditorTarget]
  );
  const currentNoteEditorNotes = React.useMemo(
    () =>
      currentNoteEditorContext ? getDesignNotesForEditor(currentNoteEditorContext.design) : [],
    [currentNoteEditorContext]
  );
  const dataCollectionTargetsByScope = React.useMemo<DataCollectionTargetsByScope | null>(
    () => (dataCollectionTargetUpc ? getDataCollectionTargets(dataCollectionTargetUpc) : null),
    [dataCollectionTargetUpc]
  );
  const currentGroupManagerOpenDesigns = React.useMemo<OpenDesignGroupCandidate[]>(() => {
    const themeByDesignId = getDesignThemeMap(displayUpcData);
    return displayUpcData.flatMap((candidateUpc) =>
      candidateUpc.designs
        .filter((candidateDesign) => !isDesignPendingDeletion(candidateDesign))
        .map((candidateDesign) => ({
          upc: candidateUpc,
          design: candidateDesign,
          summary: buildGroupSummaryForDesign(selectedChainId, candidateDesign),
          theme: themeByDesignId.get(candidateDesign.id) ?? getMasterBarcodeGroupTheme(0),
        }))
    );
  }, [displayUpcData, selectedChainId]);
  const visibleUpcs = React.useMemo(() => {
    if (!focusedItemNumber) return displayUpcData;
    return displayUpcData.filter((upc) => upc.itemNumber === focusedItemNumber);
  }, [displayUpcData, focusedItemNumber]);
  useEffect(() => {
    remoteDataCollectionLinksByKeyRef.current = remoteDataCollectionLinksByKey;
  }, [remoteDataCollectionLinksByKey]);

  const loadRemoteDataCollectionLinks = useCallback(
    (upc: MasterUPCData, maxStores = REMOTE_DATA_COLLECTION_DEFAULT_STORE_LIMIT) => {
      const { itemNumber, normalizedBarcode } = getRemoteDataCollectionQuery(upc);
      if (!itemNumber && !normalizedBarcode) return;

      const baseKey = getRemoteDataCollectionLookupBaseKey(selectedChainId, upc);
      const key = getRemoteDataCollectionLookupKey(selectedChainId, upc, maxStores);
      setRemoteDataCollectionStoreLimitByKey((previous) => ({
        ...previous,
        [baseKey]: maxStores,
      }));
      const currentEntry = remoteDataCollectionLinksByKeyRef.current[key];
      if (currentEntry?.status === 'loading' || currentEntry?.status === 'ready') {
        return;
      }

      setRemoteDataCollectionLinksByKey((previous) => {
        const next = {
          ...previous,
          [key]: {
            status: 'loading' as const,
            links: previous[key]?.links ?? [],
            searchedStoreCount: previous[key]?.searchedStoreCount,
            maxStores,
          },
        };
        remoteDataCollectionLinksByKeyRef.current = next;
        return next;
      });

      ForgeApiService.fetchMasterDataCollectionReviewTargets(token, {
        chainId: selectedChainId,
        itemNumber,
        normalizedBarcode,
        maxStores,
      })
        .then((response) => {
          setRemoteDataCollectionLinksByKey((previous) => {
            const next = {
              ...previous,
              [key]: {
                status: 'ready' as const,
                links: response.targets,
                searchedStoreCount: response.searchedStoreCount,
                maxStores,
              },
            };
            remoteDataCollectionLinksByKeyRef.current = next;
            return next;
          });
        })
        .catch(() => {
          setRemoteDataCollectionLinksByKey((previous) => {
            const next = {
              ...previous,
              [key]: {
                status: 'error' as const,
                links: previous[key]?.links ?? [],
                searchedStoreCount: previous[key]?.searchedStoreCount,
                maxStores,
              },
            };
            remoteDataCollectionLinksByKeyRef.current = next;
            return next;
          });
        });
    },
    [selectedChainId, token]
  );
  const shouldRenderTrailingTile = !focusedItemNumber && Boolean(trailingColumnTile);
  const saveBlockedByFocus = Boolean(focusedItemNumber);
  const remainingWorkCounts = React.useMemo(
    () =>
      editor.upcData.reduce(
        (counts, upc) => {
          counts.pending += upc.pendingCrops?.length ?? 0;
          counts.unassigned += upc.unassignedCrops.length;
          counts.inactive += upc.inactiveCrops?.length ?? 0;
          return counts;
        },
        { pending: 0, unassigned: 0, inactive: 0 }
      ),
    [editor.upcData]
  );
  const getUpcControlEmbeddingsUrl = useCallback(
    (upc: MasterUPCData) => {
      const upcCrops = collectUpcCrops(upc);
      const selectedCropId = [...editor.selectedCropIds].find((cropId) =>
        upcCrops.some((crop) => crop.id === cropId)
      );
      const focusedCropId =
        focusedHighlight?.cropId && upcCrops.some((crop) => crop.id === focusedHighlight.cropId)
          ? focusedHighlight.cropId
          : null;
      const preferredCropId = selectedCropId ?? focusedCropId ?? null;
      const crop =
        (preferredCropId ? upcCrops.find((entry) => entry.id === preferredCropId) : null) ??
        upcCrops[0];

      return buildControlEmbeddingsUrl(selectedChainId, {
        crop,
        upc,
      });
    },
    [editor.selectedCropIds, focusedHighlight?.cropId, selectedChainId]
  );

  const closeDesignGroupManager = useCallback(() => {
    setGroupManagerTarget(null);
    setGroupConflict(null);
  }, []);

  const openDesignGroupManager = useCallback((design: MasterDesignData, upc: MasterUPCData) => {
    setGroupManagerTarget({
      itemNumber: upc.itemNumber,
      designId: design.id,
    });
    setGroupConflict(null);
  }, []);

  const editDesignNote = useCallback((design: MasterDesignData, upc: MasterUPCData) => {
    setNoteEditorTarget({
      itemNumber: upc.itemNumber,
      designId: design.id,
      designLabel: `${upc.itemNumber} ${design.normalizedBarcode ?? ''} ${design.id}`,
    });
    setNoteDraft('');
    setEditingNoteKey(null);
  }, []);

  const openDataCollectionRequest = useCallback(
    (upc: MasterUPCData) => {
      const targetsByScope = getDataCollectionTargets(upc);
      const defaultScope: DataCollectionTargetScope =
        targetsByScope.item_number.length > 0 ? 'item_number' : 'normalized_barcode';
      const firstTarget =
        targetsByScope[defaultScope][0] ??
        targetsByScope.normalized_barcode[0] ??
        targetsByScope.design[0] ??
        null;
      setDataCollectionTargetUpc(upc);
      setDataCollectionScope(firstTarget?.scope ?? defaultScope);
      setDataCollectionSelections(new Map());
      setExistingDataCollectionRequests(new Map());
      setIsLoadingExistingDataCollectionRequests(true);
      ForgeApiService.fetchDataCollectionDashboard(token, {
        status: 'open',
        chainId: selectedChainId,
      })
        .then((dashboard) => {
          const existingRequests = buildExistingDataCollectionRequestMap(dashboard.rows);
          setExistingDataCollectionRequests(existingRequests);
          setDataCollectionSelections((previous) => {
            const next = new Map<string, Set<DataCollectionRequestedType>>();
            previous.forEach((types, key) => {
              const existingTypes = existingRequests.get(key) ?? new Set();
              const nextTypes = new Set([...types].filter((type) => !existingTypes.has(type)));
              if (nextTypes.size > 0) {
                next.set(key, nextTypes);
              }
            });
            return next;
          });
        })
        .catch((error) => {
          notify(
            error instanceof ForgeApiError
              ? error.detail || 'Failed to load existing data collection requests'
              : 'Failed to load existing data collection requests',
            'warning'
          );
        })
        .finally(() => setIsLoadingExistingDataCollectionRequests(false));
    },
    [notify, selectedChainId, token]
  );

  const closeDataCollectionRequest = useCallback(() => {
    if (isCreatingDataCollection) return;
    setDataCollectionTargetUpc(null);
    setDataCollectionSelections(new Map());
  }, [isCreatingDataCollection]);

  const selectDataCollectionScope = useCallback(
    (scope: DataCollectionTargetScope) => {
      if (!dataCollectionTargetsByScope || dataCollectionTargetsByScope[scope].length === 0) {
        return;
      }
      setDataCollectionScope(scope);
    },
    [dataCollectionTargetsByScope]
  );

  const toggleDataCollectionType = useCallback(
    (target: DataCollectionTargetOption, type: DataCollectionRequestedType) => {
      const selectionKey = getDataCollectionTargetSelectionKey(target);
      if (existingDataCollectionRequests.get(selectionKey)?.has(type)) {
        return;
      }
      setDataCollectionSelections((previous) => {
        const next = new Map(previous);
        const nextTypes = new Set(next.get(selectionKey) ?? []);
        if (nextTypes.has(type)) {
          nextTypes.delete(type);
        } else {
          nextTypes.add(type);
        }
        if (nextTypes.size > 0) {
          next.set(selectionKey, nextTypes);
        } else {
          next.delete(selectionKey);
        }
        return next;
      });
    },
    [existingDataCollectionRequests]
  );

  const submitDataCollectionRequest = useCallback(async () => {
    if (!dataCollectionTargetUpc || !dataCollectionTargetsByScope) {
      return;
    }
    const selectedPayloads: DataCollectionCreateRequest[] = [];
    const targets = Object.values(dataCollectionTargetsByScope).flat();
    targets.forEach((target) => {
      const selectionKey = getDataCollectionTargetSelectionKey(target);
      const selectedTypes = dataCollectionSelections.get(selectionKey);
      if (!selectedTypes || selectedTypes.size === 0) {
        return;
      }
      const existingTypes = existingDataCollectionRequests.get(selectionKey) ?? new Set();
      const requestedTypes = [...selectedTypes].filter((type) => !existingTypes.has(type));
      if (requestedTypes.length === 0) {
        return;
      }
      selectedPayloads.push({
        chain_id: selectedChainId,
        target_scope: target.scope,
        target_key: target.key,
        requested_types: requestedTypes,
        item_number: target.itemNumber ?? null,
        normalized_barcode: target.normalizedBarcode ?? null,
        design_id: target.designId ?? null,
        product_name: target.productName ?? null,
        preview_viz_url: target.previewVizUrl ?? null,
        preview_images: target.previewImages ?? [],
      });
    });

    if (selectedPayloads.length === 0) {
      return;
    }
    setIsCreatingDataCollection(true);
    try {
      await Promise.all(
        selectedPayloads.map((payload) =>
          ForgeApiService.createDataCollectionRequest(token, payload)
        )
      );
      notify(
        `${selectedPayloads.length} data collection request${
          selectedPayloads.length === 1 ? '' : 's'
        } created`,
        'success'
      );
      setDataCollectionTargetUpc(null);
      setDataCollectionSelections(new Map());
    } catch (error) {
      notify(
        error instanceof ForgeApiError
          ? error.detail || 'Failed to create data collection request'
          : 'Failed to create data collection request',
        'error'
      );
    } finally {
      setIsCreatingDataCollection(false);
    }
  }, [
    dataCollectionTargetUpc,
    dataCollectionTargetsByScope,
    dataCollectionSelections,
    existingDataCollectionRequests,
    notify,
    selectedChainId,
    token,
  ]);

  const closeNoteEditor = useCallback(() => {
    setNoteEditorTarget(null);
    setNoteDraft('');
    setEditingNoteKey(null);
  }, []);

  const cancelNoteEdit = useCallback(() => {
    setEditingNoteKey(null);
    setNoteDraft('');
  }, []);

  const patchDesignNotes = useCallback(
    (
      target: { itemNumber: string; designId: string },
      updateNotes: (notes: ForgeDesignNote[]) => ForgeDesignNote[],
      historyLabel: string
    ) => {
      editor.patchWorkingUpcData(
        (currentUpcs) =>
          currentUpcs.map((currentUpc) => {
            if (currentUpc.itemNumber !== target.itemNumber) return currentUpc;
            return {
              ...currentUpc,
              designs: currentUpc.designs.map((currentDesign) => {
                if (currentDesign.id !== target.designId) return currentDesign;
                const nextNotes = updateNotes(getDesignNotesForEditor(currentDesign));
                return {
                  ...currentDesign,
                  notes: nextNotes,
                };
              }),
            };
          }),
        historyLabel
      );
    },
    [editor]
  );

  const startNoteEdit = useCallback((note: ForgeDesignNote, noteKey: string) => {
    setEditingNoteKey(noteKey);
    setNoteDraft(note.text);
  }, []);

  const deleteDesignNote = useCallback(
    (noteKey: string) => {
      if (!noteEditorTarget) return;
      patchDesignNotes(
        noteEditorTarget,
        (notes) => notes.filter((note, index) => getDesignNoteKey(note, index) !== noteKey),
        'Delete design note'
      );
      if (editingNoteKey === noteKey) {
        setEditingNoteKey(null);
        setNoteDraft('');
      }
      notify('Design note deletion staged. Press Save to persist.', 'success');
    },
    [editingNoteKey, noteEditorTarget, notify, patchDesignNotes]
  );

  const submitDesignNote = useCallback(() => {
    if (!noteEditorTarget) return;
    const normalizedNote = noteDraft.trim();
    if (normalizedNote.length === 0) return;
    if (editingNoteKey) {
      patchDesignNotes(
        noteEditorTarget,
        (notes) =>
          notes.map((note, index) =>
            getDesignNoteKey(note, index) === editingNoteKey
              ? { ...note, text: normalizedNote }
              : note
          ),
        'Edit design note'
      );
      setEditingNoteKey(null);
      setNoteDraft('');
      notify('Design note edit staged. Press Save to persist.', 'success');
      return;
    }

    const stagedNote: ForgeDesignNote = {
      id:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
      text: normalizedNote,
      createdAt: new Date().toISOString(),
      createdBy: null,
    };

    patchDesignNotes(noteEditorTarget, (notes) => [...notes, stagedNote], 'Add design note');
    setNoteDraft('');
    notify('Design note staged. Press Save to persist.', 'success');
  }, [editingNoteKey, noteDraft, noteEditorTarget, notify, patchDesignNotes]);

  const patchPersistedDesignLock = useCallback(
    (designId: string, designLock: DesignLockStatusInfo | undefined) => {
      const affectedItemNumbers = editor.upcData
        .filter((currentUpc) =>
          currentUpc.designs.some(
            (currentDesign) =>
              currentDesign.id === designId || currentDesign.persistedDesignId === designId
          )
        )
        .map((currentUpc) => currentUpc.itemNumber);

      for (const itemNumber of affectedItemNumbers) {
        editor.patchUpcMetadata(itemNumber, (currentUpc) => ({
          ...currentUpc,
          designs: currentUpc.designs.map((currentDesign) =>
            currentDesign.id === designId || currentDesign.persistedDesignId === designId
              ? { ...currentDesign, designLock }
              : currentDesign
          ),
        }));
      }
    },
    [editor]
  );

  const openDesignLockDialog = useCallback(
    (design: MasterDesignData, upc: MasterUPCData, action: DesignLockAction) => {
      if (!hasInternalAccess || !design.persistedDesignId) return;
      if (action === 'update' && design.designLock?.status === 'in_sync') return;
      setDesignLockDialogTarget({ design, upc, action });
    },
    [hasInternalAccess]
  );

  const confirmDesignLockAction = useCallback(async () => {
    if (!designLockDialogTarget || !hasInternalAccess || isDesignLockSubmitting) return;
    const { action, design } = designLockDialogTarget;
    const designId = design.persistedDesignId;
    if (!designId) return;

    setIsDesignLockSubmitting(true);
    try {
      if (action === 'unlock') {
        await ForgeApiService.deleteDesignLock(token, designId, selectedChainId);
        patchPersistedDesignLock(designId, undefined);
        notify('Design unlocked.', 'success');
      } else {
        const nextLock =
          action === 'lock'
            ? await ForgeApiService.createDesignLock(token, {
                chain_id: selectedChainId,
                design_id: designId,
              })
            : await ForgeApiService.updateDesignLock(token, designId, selectedChainId);
        patchPersistedDesignLock(designId, nextLock);
        notify(
          action === 'lock' ? 'Design locked for training.' : 'Design lock updated.',
          'success'
        );
      }
      setDesignLockDialogTarget(null);
    } catch (error) {
      const message =
        error instanceof ForgeApiError
          ? error.detail || error.message
          : error instanceof Error
            ? error.message
            : `Failed to ${action} design`;
      notify(message, 'error');
    } finally {
      setIsDesignLockSubmitting(false);
    }
  }, [
    designLockDialogTarget,
    hasInternalAccess,
    isDesignLockSubmitting,
    notify,
    patchPersistedDesignLock,
    selectedChainId,
    token,
  ]);

  const patchPersistedDesignReview = useCallback(
    (
      designId: string,
      isReviewed: boolean,
      reviewedBy: string | null,
      reviewedAt: string | null
    ) => {
      const affectedItemNumbers = editor.upcData
        .filter((currentUpc) =>
          currentUpc.designs.some(
            (currentDesign) =>
              currentDesign.id === designId || currentDesign.persistedDesignId === designId
          )
        )
        .map((currentUpc) => currentUpc.itemNumber);

      for (const itemNumber of affectedItemNumbers) {
        editor.patchUpcMetadata(itemNumber, (currentUpc) => ({
          ...currentUpc,
          designs: currentUpc.designs.map((currentDesign) =>
            currentDesign.id === designId || currentDesign.persistedDesignId === designId
              ? {
                  ...currentDesign,
                  isReviewed,
                  reviewedAt,
                  reviewedBy: isReviewed ? reviewedBy : null,
                }
              : currentDesign
          ),
        }));
      }
    },
    [editor]
  );

  const toggleDesignReview = useCallback(
    async (design: MasterDesignData) => {
      if (!canDoTasks) return;
      const designId =
        design.persistedDesignId || (isMongoObjectIdQuery(design.id) ? design.id : null);
      if (!designId) {
        notify('Save the design before marking it reviewed.', 'warning');
        return;
      }
      const nextIsReviewed = !design.isReviewed;
      try {
        const updatedDesign = await ForgeApiService.updateDesignReviewStatus(token, designId, {
          is_reviewed: nextIsReviewed,
        });
        patchPersistedDesignReview(
          designId,
          nextIsReviewed,
          updatedDesign.reviewedBy ?? null,
          updatedDesign.reviewedAt ?? null
        );
        notify(nextIsReviewed ? 'Design marked reviewed.' : 'Design review cleared.', 'success');
      } catch (error) {
        notify(
          error instanceof ForgeApiError
            ? error.detail || 'Failed to update design review'
            : 'Failed to update design review',
          'error'
        );
      }
    },
    [canDoTasks, notify, patchPersistedDesignReview, token]
  );

  const patchPersistedGroupReview = useCallback(
    (
      target: { itemNumber: string; designId: string },
      scope: ForgeDesignGroupScope,
      groupId: string,
      isReviewed: boolean,
      reviewedBy: string | null,
      reviewedAt: string | null
    ) => {
      const reviewField = getDesignGroupReviewField(scope);
      const affectedItemNumbers = editor.upcData
        .filter(
          (currentUpc) =>
            currentUpc.itemNumber === target.itemNumber ||
            currentUpc.designs.some(
              (currentDesign) => currentDesign[reviewField]?.groupId === groupId
            )
        )
        .map((currentUpc) => currentUpc.itemNumber);

      for (const itemNumber of affectedItemNumbers) {
        editor.patchUpcMetadata(itemNumber, (currentUpc) => ({
          ...currentUpc,
          designs: currentUpc.designs.map((currentDesign) => {
            const currentReview = currentDesign[reviewField];
            if (currentDesign.id !== target.designId && currentReview?.groupId !== groupId) {
              return currentDesign;
            }
            return {
              ...currentDesign,
              [reviewField]: {
                ...(currentReview ?? { groupId }),
                groupId,
                isReviewed,
                reviewedBy: isReviewed ? reviewedBy : null,
                reviewedAt,
              },
            };
          }),
        }));
      }
    },
    [editor]
  );

  const toggleGroupReview = useCallback(
    async (scope: ForgeDesignGroupScope, review: ForgeDesignGroupReviewSummary) => {
      if (!canDoTasks) return;
      if (!currentGroupManagerContext) return;
      const nextIsReviewed = !review.isReviewed;
      try {
        const updatedReview = await ForgeApiService.updateDesignGroupReviewStatus(
          token,
          review.groupId,
          {
            is_reviewed: nextIsReviewed,
          }
        );
        patchPersistedGroupReview(
          {
            itemNumber: currentGroupManagerContext.upc.itemNumber,
            designId: currentGroupManagerContext.design.id,
          },
          scope,
          review.groupId,
          updatedReview.isReviewed,
          updatedReview.reviewedBy ?? null,
          updatedReview.reviewedAt ?? null
        );
        notify(
          nextIsReviewed ? 'Design group marked reviewed.' : 'Design group review cleared.',
          'success'
        );
      } catch (error) {
        notify(
          error instanceof ForgeApiError
            ? error.detail || 'Failed to update design group review'
            : 'Failed to update design group review',
          'error'
        );
      }
    },
    [currentGroupManagerContext, canDoTasks, notify, patchPersistedGroupReview, token]
  );

  useEffect(() => {
    if (groupManagerTarget && !currentGroupManagerContext) {
      closeDesignGroupManager();
    }
  }, [closeDesignGroupManager, currentGroupManagerContext, groupManagerTarget]);

  useEffect(() => {
    if (noteEditorTarget && !currentNoteEditorContext) {
      closeNoteEditor();
    }
  }, [closeNoteEditor, currentNoteEditorContext, noteEditorTarget]);

  const applyLocalDesignGroup = useCallback(
    (scope: ForgeDesignGroupScope, targetSummary: ForgeDesignGroupSearchResult) => {
      if (!currentGroupManagerContext) {
        return;
      }

      const sourceDesignId = currentGroupManagerContext.design.id;
      const sourceSummary = buildGroupSummaryForDesign(
        selectedChainId,
        currentGroupManagerContext.design
      );
      editor.patchWorkingUpcData(
        (currentUpcs) => {
          if (
            hasIncompleteDesignGroupScopeMove(
              currentUpcs,
              sourceDesignId,
              targetSummary.designId,
              scope
            )
          ) {
            return currentUpcs;
          }
          const excludedDesignIds = collectPendingDeletedDesignIds(currentUpcs);
          const summariesById = collectGroupSummariesByDesignId(
            selectedChainId,
            currentUpcs,
            excludedDesignIds
          );
          summariesById.set(sourceSummary.designId, sourceSummary);
          summariesById.set(targetSummary.designId, targetSummary);
          const scopeMoves = collectDesignGroupScopeMoves(
            currentUpcs,
            sourceDesignId,
            targetSummary.designId,
            scope
          );
          let nextUpcs = currentUpcs.map((upc) => ({
            ...upc,
            designs: upc.designs.map((design) => {
              let nextDesign = design;

              scopeMoves.forEach(({ sourceScope, componentDesignIds }) => {
                if (!componentDesignIds.has(design.id)) {
                  return;
                }

                nextDesign = removeDesignGroupMembers(nextDesign, sourceScope, componentDesignIds);
                componentDesignIds.forEach((memberDesignId) => {
                  if (memberDesignId === design.id) {
                    return;
                  }
                  const memberSummary = summariesById.get(memberDesignId);
                  if (memberSummary) {
                    nextDesign = upsertDesignGroup(nextDesign, scope, memberSummary);
                  }
                });
              });

              if (design.id === sourceDesignId) {
                nextDesign = upsertDesignGroup(nextDesign, scope, targetSummary);
              }
              if (design.id === targetSummary.designId) {
                nextDesign = upsertDesignGroup(nextDesign, scope, sourceSummary);
              }
              return nextDesign;
            }),
          }));
          return normalizeDesignGroups(selectedChainId, nextUpcs);
        },
        `Update ${getForgeDesignGroupScopeLabel(scope)} design group`
      );
      setGroupConflict(null);
      notify('Design group staged. Press Save to persist.', 'success');
    },
    [currentGroupManagerContext, editor, notify, selectedChainId]
  );

  const handleLeaveDesignGroup = useCallback(
    (scope: ForgeDesignGroupScope) => {
      if (!currentGroupManagerContext) {
        return;
      }

      const sourceDesignId = currentGroupManagerContext.design.id;
      const groupField = getDesignGroupField(scope);
      const designIdsToRemove = new Set([sourceDesignId]);
      if (scope === 'partial' || scope === 'non-front') {
        (currentGroupManagerContext.design.fullGroupDesigns ?? []).forEach((member) => {
          designIdsToRemove.add(member.designId);
        });
      }
      if (scope === 'non-front') {
        (currentGroupManagerContext.design.partialGroupDesigns ?? []).forEach((member) => {
          designIdsToRemove.add(member.designId);
        });
      }
      editor.patchWorkingUpcData(
        (currentUpcs) => {
          const nextUpcs = currentUpcs.map((upc) => ({
            ...upc,
            designs: upc.designs.map((design) => {
              if (designIdsToRemove.has(design.id)) {
                return { ...design, [groupField]: [] };
              }
              return [...designIdsToRemove].reduce(
                (nextDesign, designIdToRemove) =>
                  removeDesignGroup(nextDesign, scope, designIdToRemove),
                design
              );
            }),
          }));
          return normalizeDesignGroups(selectedChainId, nextUpcs);
        },
        `Leave ${getForgeDesignGroupScopeLabel(scope)} design group`
      );
      notify('Design group membership staged for removal. Press Save to persist.', 'success');
    },
    [currentGroupManagerContext, editor, notify, selectedChainId]
  );

  const handleConvertDesignGroupScope = useCallback(
    (sourceScope: ForgeDesignGroupScope, targetScope: ForgeDesignGroupScope) => {
      if (!currentGroupManagerContext || sourceScope === targetScope) {
        return;
      }

      const sourceField = getDesignGroupField(sourceScope);
      const sourceDesignId = currentGroupManagerContext.design.id;
      const componentSummariesById = new Map<string, ForgeDesignGroupSearchResult>();
      componentSummariesById.set(
        sourceDesignId,
        buildGroupSummaryForDesign(selectedChainId, currentGroupManagerContext.design)
      );
      (currentGroupManagerContext.design[sourceField] ?? []).forEach((member) => {
        componentSummariesById.set(member.designId, member);
      });

      if (componentSummariesById.size < 2) {
        return;
      }

      const componentDesignIds = new Set(componentSummariesById.keys());
      editor.patchWorkingUpcData(
        (currentUpcs) => {
          const nextUpcs = currentUpcs.map((upc) => ({
            ...upc,
            designs: upc.designs.map((design) => {
              if (!componentDesignIds.has(design.id)) {
                return design;
              }

              let nextDesign = removeDesignGroupMembers(design, sourceScope, componentDesignIds);
              componentSummariesById.forEach((summary, memberDesignId) => {
                if (memberDesignId !== design.id) {
                  nextDesign = upsertDesignGroup(nextDesign, targetScope, summary);
                }
              });
              return nextDesign;
            }),
          }));
          return normalizeDesignGroups(selectedChainId, nextUpcs);
        },
        `Change ${getForgeDesignGroupScopeLabel(sourceScope)} group to ${getForgeDesignGroupScopeLabel(
          targetScope
        )}`
      );
      notify(
        `Design group type change staged as ${getForgeDesignGroupScopeLabel(targetScope)}. Press Save to persist.`,
        'success'
      );
    },
    [currentGroupManagerContext, editor, notify, selectedChainId]
  );

  const handleDesignGroupMutation = useCallback(
    (scope: ForgeDesignGroupScope, targetDesignId: string) => {
      if (!currentGroupManagerContext) {
        return;
      }

      const targetSummary =
        currentGroupManagerOpenDesigns.find((entry) => entry.design.id === targetDesignId)
          ?.summary ??
        [
          ...(currentGroupManagerContext.design.fullGroupDesigns ?? []),
          ...(currentGroupManagerContext.design.partialGroupDesigns ?? []),
          ...(currentGroupManagerContext.design.nonFrontGroupDesigns ?? []),
        ].find((member) => member.designId === targetDesignId);
      if (!targetSummary) {
        notify('Load the target design in Master Label before adding it to a group.', 'error');
        return;
      }

      applyLocalDesignGroup(scope, targetSummary);
    },
    [applyLocalDesignGroup, currentGroupManagerContext, currentGroupManagerOpenDesigns, notify]
  );

  const handleResolveDesignGroupConflict = useCallback(() => {
    if (!groupConflict) {
      return;
    }

    applyLocalDesignGroup(groupConflict.scope, groupConflict.targetSummary);
  }, [applyLocalDesignGroup, groupConflict]);

  // Remove item column
  const removeUPC = useCallback(
    (itemNumber: string) => {
      if (
        upcHasChanges(itemNumber) &&
        !window.confirm(`Item "${itemNumber}" has unsaved changes. Remove anyway?`)
      )
        return;
      const upcData = editor.upcData.find((d) => d.itemNumber === itemNumber);
      if (upcData) {
        // Release both URL variants: the context view may have been toggled since
        // these images were cached.
        releaseCachedImages(collectMasterLabelUpcCropImageUrlVariants(upcData));
      }
      const updated = selectedItemNumbers.filter((value) => value !== itemNumber);
      const updatedUpcs = editor.upcData.filter((value) => value.itemNumber !== itemNumber);
      setSelectedItemNumbers(updated);
      setFocusedItemNumber((current) => (current === itemNumber ? null : current));
      syncUrlAfterRemoveColumn(updatedUpcs);
      editor.removeUpc(itemNumber);
    },
    [editor, selectedItemNumbers, syncUrlAfterRemoveColumn, upcHasChanges]
  );

  // Load the typed item number directly on Enter.
  const handleSearchKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isSubmittingQuery) return;
        const itemNumber = searchQuery.trim();
        if (!itemNumber) return;
        await loadItem(itemNumber, itemNumber);
      }
    },
    [isSubmittingQuery, loadItem, searchQuery]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const syncShiftState = (event: KeyboardEvent) => {
      shiftPressedRef.current = event.key === 'Shift' || event.shiftKey;
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      shiftPressedRef.current = event.key === 'Shift' ? false : event.shiftKey;
    };
    const resetShiftState = () => {
      shiftPressedRef.current = false;
    };

    window.addEventListener('keydown', syncShiftState, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', resetShiftState);

    return () => {
      window.removeEventListener('keydown', syncShiftState, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', resetShiftState);
    };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      shiftPressedRef.current = e.key === 'Shift' || e.shiftKey;

      if (noteEditorTarget) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeNoteEditor();
        }
        return;
      }

      if (isVizSimDatasetDialogOpen) {
        // Swallow page shortcuts while the dialog is open; the dialog owns
        // Escape itself so it can warn about unsaved edits before closing.
        return;
      }

      if (designLockDialogTarget) return;
      // The compare popup handles its own Escape/Q; nothing else may mutate crops
      // while it is open.
      if (isCropCompareOpen) return;

      // Don't trigger when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (groupConflict) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setGroupConflict(null);
        }
        return;
      }

      if (groupManagerTarget) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeDesignGroupManager();
        }
        return;
      }

      if (isReviewChooserOpen) {
        if (e.key === 'Escape') {
          e.preventDefault();
          closeReviewChooser();
          return;
        }

        const reviewAction = reviewIssueActionFromShortcutKey(e.key);
        if (reviewAction === 'clear') {
          e.preventDefault();
          clearReviewIssue();
          return;
        }
        if (reviewAction) {
          e.preventDefault();
          applyReviewIssue(reviewAction);
          return;
        }
        return;
      }

      if (pendingNewDesign) {
        if (e.key === 'Escape') {
          e.preventDefault();
          setPendingNewDesign(null);
        }
        // While the overlay is open, suppress other page shortcuts (including 'D').
        return;
      }

      if (e.key === 'Escape') {
        if (editor.selectedCropIds.size > 0) {
          e.preventDefault();
          editor.setSelectedCropIds(new Set());
          return;
        }
        if (focusedItemNumber) {
          e.preventDefault();
          setFocusedItemNumber(null);
          return;
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        editor.undo();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      if (e.key === '[' || e.key === ']') {
        if (!hasHighlightTask || navigableHighlightCropIds.size === 0) return;
        e.preventDefault();
        navigateVisibleHighlights(e.key === ']' ? 1 : -1);
        return;
      }
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        toggleZoom();
        return;
      }
      if (e.key === 'w' || e.key === 'W') {
        e.preventDefault();
        toggleCropContextView();
        return;
      }
      // Shortcuts when crops are selected
      if (editor.selectedCropIds.size > 0) {
        if (canDoTasks && (e.key === 'e' || e.key === 'E')) {
          e.preventDefault();
          openVizSimDatasetDialog();
          return;
        }
        if (canDoTasks && (e.key === 'Delete' || e.key === 'Backspace')) {
          e.preventDefault();
          handleMarkSelectedForDeletion();
          return;
        }
        if (e.key === 'q' || e.key === 'Q') {
          e.preventDefault();
          openCropCompare();
          return;
        }
        if (e.key === 'd' || e.key === 'D') {
          // Retired on purpose: D created designs by accident too often. Keep
          // answering the key with an explanation until labelers have adjusted.
          notify(NEW_DESIGN_SHORTCUT_RETIRED_MESSAGE, 'warning');
        }
        if (e.key === 'f' || e.key === 'F') {
          handleSetSelectedCropOrientation('front');
        }
        if (e.key === 'c' || e.key === 'C') {
          if (editor.selectedCropItemNumber)
            handleHighlightedSectionClick({
              itemNumber: editor.selectedCropItemNumber,
              type: 'carton',
            });
        }
        if (e.key === 'n' || e.key === 'N') {
          handleSetSelectedCropOrientation('not-front');
        }
        if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          openReviewChooser();
          return;
        }
        if (e.key === 's' || e.key === 'S') {
          if (editor.selectedCropDesignInfo && !editor.selectedCropDesignInfo.isAlreadyRep) {
            e.preventDefault();
            handleSetRepresentative(
              editor.selectedCropDesignInfo.designId,
              editor.selectedCropDesignInfo.cropId
            );
          }
          return;
        }
        if (e.key === 'i' || e.key === 'I') {
          if (selectedHasScanSource) {
            notify('Cannot deactivate scanned crops', 'warning');
            return;
          }
          if (editor.selectedCropItemNumber)
            handleHighlightedSectionClick({
              itemNumber: editor.selectedCropItemNumber,
              type: 'inactive',
            });
        }
        if (e.key === 'p' || e.key === 'P') {
          e.preventDefault();
          handleUseSelectedCropAsProductImage();
          return;
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    applyReviewIssue,
    clearReviewIssue,
    closeNoteEditor,
    closeReviewChooser,
    closeDesignGroupManager,
    designLockDialogTarget,
    editor,
    focusedItemNumber,
    handleHighlightedSectionClick,
    handleMarkSelectedForDeletion,
    handleSetRepresentative,
    handleSetSelectedCropOrientation,
    handleUseSelectedCropAsProductImage,
    canDoTasks,
    isCropCompareOpen,
    isVizSimDatasetDialogOpen,
    isReviewChooserOpen,
    groupConflict,
    groupManagerTarget,
    hasHighlightTask,
    navigateVisibleHighlights,
    navigableHighlightCropIds,
    noteEditorTarget,
    notify,
    openCropCompare,
    openDesignGroupManager,
    editDesignNote,
    openReviewChooser,
    openVizSimDatasetDialog,
    pendingNewDesign,
    selectedHasScanSource,
    toggleZoom,
    unresolvedHighlightCount,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!token || isSaving || changeCount === 0) return;
    if (focusedItemNumber) {
      notify('Un-zoom to save changes', 'warning');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    const saveId = saveSequenceRef.current + 1;
    saveSequenceRef.current = saveId;
    const saveChainId = selectedChainId;
    const isCurrentSave = () =>
      saveSequenceRef.current === saveId && selectedChainIdRef.current === saveChainId;
    let debugGroupOpen = false;
    let debugInfo: MasterLabelDebugInfo | null = null;
    let shouldLogSaveDebug = false;
    let conflictRefreshTargets: {
      itemNumber: string;
      actualItemNumber: string | null;
      lookupNormalizedBarcode: string | null;
    }[] = [];
    try {
      debugInfo = buildMasterLabelDebugInfo(selectedChainId, editor.baseUpcData, editor.upcData);
      const conflictItemNumbers = [...editor.diffSummary.dirtyItemNumbers];
      conflictRefreshTargets = conflictItemNumbers.map((itemNumber) => {
        const upc = editor.upcData.find((item) => item.itemNumber === itemNumber);
        return {
          itemNumber,
          actualItemNumber: upc?.actualItemNumber ?? null,
          lookupNormalizedBarcode: upc?.lookupNormalizedBarcode ?? null,
        };
      });
      shouldLogSaveDebug = isMasterLabelSaveDebugEnabled();
      let request = debugInfo.request;
      if ((request.confirmedDeleteCropIds ?? []).length > 0) {
        const deleteCountsByItemNumber = new Map<string, number>();
        for (const item of editor.upcData) {
          const deleteCount = [
            ...item.designs.flatMap((design) => design.crops),
            ...(item.pendingCrops ?? []),
            ...item.unassignedCrops,
            ...item.cartonCrops,
            ...(item.inactiveCrops ?? []),
          ].filter((crop) => crop.markedForDeletion).length;
          if (deleteCount > 0) {
            deleteCountsByItemNumber.set(item.itemNumber, deleteCount);
          }
        }
        const deleteItemNumbers = [...deleteCountsByItemNumber.entries()]
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([itemNumber, deleteCount]) => `${itemNumber} (${deleteCount})`);
        const confirmed = window.confirm(
          `Permanently delete ${request.confirmedDeleteCropIds.length} crop(s) and linked embeddings?\n\n${deleteItemNumbers.join('\n')}`
        );
        if (!confirmed) {
          return;
        }
      }
      if (shouldLogSaveDebug) {
        console.groupCollapsed(
          `[Forge][Master Label] Save debug: ${debugInfo.summary.totalChanges} FE changes across ${debugInfo.summary.dirtyItemNumbers.length} item(s)`
        );
        debugGroupOpen = true;
        console.log('FE diff summary', debugInfo.summary);
        console.log('Save request', request);
        console.table(debugInfo.summary.itemSummaries);
        if (debugInfo.cropChanges.length > 0) {
          console.log('Crop placement changes', debugInfo.cropChanges);
          console.table(debugInfo.cropChanges);
        }
        if (debugInfo.representativeChanges.length > 0) {
          console.log('Representative changes', debugInfo.representativeChanges);
          console.table(debugInfo.representativeChanges);
        }
        if (debugInfo.draftDesigns.length > 0) {
          console.log('Draft designs to create', debugInfo.draftDesigns);
          console.table(debugInfo.draftDesigns);
        }
        if (debugInfo.designGroupChanges.length > 0) {
          console.log('Design group changes', debugInfo.designGroupChanges);
          console.table(debugInfo.designGroupChanges);
        }
      }
      // Loop so an enforce-mode save_check_warnings 422 can prompt for acknowledgement and
      // resubmit without re-running the setup above; a repeat 422 re-prompts with the new
      // warnings. Anything else (409, generic error) rethrows to the outer catch unchanged.
      let response: MasterLabelStateSaveResponse | null = null;
      while (response === null) {
        try {
          response = await ForgeApiService.saveState(token, request);
        } catch (saveAttemptError) {
          if (isCurrentSave() && saveAttemptError instanceof SaveCheckWarningsError) {
            const saveCheckError = saveAttemptError;
            const ackResult = await new Promise<{
              comment: string;
              outcome: SaveWarningReviewOutcome;
            } | null>((resolve) => {
              setSaveWarningPrompt({ warnings: saveCheckError.warnings, saving: false, resolve });
            });
            if (!isCurrentSave()) {
              setSaveWarningPrompt(null);
              return;
            }
            if (!ackResult) {
              setSaveWarningPrompt(null);
              return;
            }
            setSaveWarningPrompt((prev) => (prev ? { ...prev, saving: true } : prev));
            request = {
              ...request,
              ...accumulateSaveWarningAcknowledgement(
                request.acknowledgedWarningIds,
                request.acknowledgementComment,
                request.warningReviewOutcomes,
                saveCheckError.warnings,
                ackResult.comment,
                ackResult.outcome
              ),
            };
            continue;
          }
          throw saveAttemptError;
        }
      }
      setSaveWarningPrompt(null);
      if (!isCurrentSave()) {
        if (debugGroupOpen) {
          console.groupEnd();
          debugGroupOpen = false;
        }
        return;
      }
      if (shouldLogSaveDebug) {
        console.log('Backend save summary', response.summary);
        if ((response.cropMutations ?? []).length > 0) {
          console.log('Backend crop mutations', response.cropMutations);
          console.table(response.cropMutations);
        }
        if ((response.designMutations ?? []).length > 0) {
          console.log('Backend design mutations', response.designMutations);
          console.table(response.designMutations);
        }
        if ((response.embeddingMutations ?? []).length > 0) {
          console.log('Backend embedding mutations', response.embeddingMutations);
          console.table(response.embeddingMutations);
        }
        if ((response.deletionMutations ?? []).length > 0) {
          console.log('Backend deletion mutations', response.deletionMutations);
          console.table(response.deletionMutations);
        }
        console.log('Backend saved item count', (response.items ?? response.upcs ?? []).length);
        console.groupEnd();
        debugGroupOpen = false;
      }
      if (showSaveReview) {
        setSaveReviewData({
          debugInfo,
          backendSummary: response.summary,
          backendItemCount: (response.items ?? response.upcs ?? []).length,
          backendCropMutations: response.cropMutations,
          backendDesignMutations: response.designMutations,
          backendEmbeddingMutations: response.embeddingMutations,
          backendDeletionMutations: response.deletionMutations,
        });
      }
      const freshPayload = response.items ?? response.upcs ?? [];
      const freshData = mergeDesignLockStatuses(
        freshPayload.map(transformApiUPCData),
        response.designLocks
      );
      editor.rebaseUpcs(freshData);
      cropQueryCacheRef.current.clear();
      closeCropQuery();
      const savedCount = debugInfo.summary.totalChanges;
      notify(`Saved ${savedCount} change${savedCount !== 1 ? 's' : ''}`);
    } catch (error) {
      // Only reached for a stale attempt or a real error (409/generic); the save-check-warnings
      // branch above handles its own dialog and returns before throwing here. Clear it
      // unconditionally in case a re-invoked (post-acknowledge) save hit one of those instead.
      setSaveWarningPrompt(null);
      const saveErrorData = buildSaveErrorData(error);
      if (!isCurrentSave()) {
        if (debugGroupOpen) {
          console.groupEnd();
          debugGroupOpen = false;
        }
        return;
      }
      if (shouldLogSaveDebug) {
        console.error('[Forge][Master Label] Save failed', {
          error,
          saveErrorData,
          debugSummary: debugInfo?.summary,
          request: debugInfo?.request,
        });
      } else {
        console.error('[Forge][Master Label] Save failed', error);
      }
      if (debugGroupOpen) console.groupEnd();
      if (showSaveReview && debugInfo) {
        setSaveReviewData({
          debugInfo,
          error: saveErrorData,
        });
      }
      if (error instanceof ForgeApiError && error.status === 409) {
        try {
          const freshData = await Promise.all(
            conflictRefreshTargets.map(
              ({ itemNumber, actualItemNumber, lookupNormalizedBarcode }) => {
                if (actualItemNumber) {
                  return ForgeApiService.fetchItem(token, actualItemNumber, saveChainId).then(
                    transformApiUPCData
                  );
                }
                if (lookupNormalizedBarcode) {
                  return ForgeApiService.fetchItemByBarcode(
                    token,
                    lookupNormalizedBarcode,
                    saveChainId
                  ).then((lookup) => transformApiUPCData(lookup.item));
                }
                return ForgeApiService.fetchItem(token, itemNumber, saveChainId).then(
                  transformApiUPCData
                );
              }
            )
          );
          if (!isCurrentSave()) return;
          editor.rebaseUpcs(freshData);
        } catch (refreshError) {
          if (!isCurrentSave()) return;
          console.error('[Forge][Master Label] Conflict refetch failed', refreshError);
          const message =
            error.detail ||
            'Snapshot conflict. Reload affected UPCs before retrying; automatic refetch failed.';
          notify(message, 'error');
          setSaveError(message);
          return;
        }
        notify(error.detail || 'Snapshot conflict — reloaded affected UPCs', 'error');
        setSaveError(error.detail || 'Snapshot conflict');
      } else {
        const message =
          saveErrorData.detail ||
          saveErrorData.message ||
          'Save failed — changes preserved for retry';
        setSaveError(message);
        notify(message, 'error');
      }
    } finally {
      if (isCurrentSave()) {
        setIsSaving(false);
      }
    }
  }, [
    token,
    isSaving,
    changeCount,
    selectedChainId,
    editor,
    notify,
    showSaveReview,
    focusedItemNumber,
    closeCropQuery,
  ]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') {
        return;
      }
      event.preventDefault();
      void handleSubmit();
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSubmit]);

  const renderSaveButton = () => (
    <button
      onClick={handleSubmit}
      disabled={isSaving || saveBlockedByFocus}
      aria-label={
        saveBlockedByFocus
          ? 'Un-zoom to save changes'
          : `Save ${changeCount} change${changeCount !== 1 ? 's' : ''}`
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: `${chrome.saveButtonPaddingY}px 14px`,
        background: isSaving || saveBlockedByFocus ? 'var(--text-tertiary)' : 'var(--purple)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        cursor: isSaving || saveBlockedByFocus ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--font-mono)',
        opacity: isSaving || saveBlockedByFocus ? 0.7 : 1,
        flexShrink: 0,
      }}
    >
      {isSaving ? (
        <CircularProgress size={13} style={{ color: '#fff' }} />
      ) : (
        <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
      )}
      {isSaving
        ? 'Saving...'
        : saveBlockedByFocus
          ? 'Un-zoom to save'
          : `Save ${changeCount} change${changeCount !== 1 ? 's' : ''}`}
    </button>
  );

  const renderRemainingWorkCounters = ({
    includeHighlightedCount = hasHighlightTask,
  }: {
    includeHighlightedCount?: boolean;
  } = {}) => (
    <div
      data-testid="remaining-work-counters"
      aria-label="Remaining highlighted, pending, unassigned, and inactive crops"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
      }}
    >
      {[
        ...(includeHighlightedCount
          ? [
              {
                key: 'highlighted',
                label: 'highlighted',
                count: unresolvedHighlightCount,
                background: '#58a6ff',
              },
            ]
          : []),
        {
          key: 'pending',
          label: 'pending',
          count: remainingWorkCounts.pending,
          background: 'var(--warning, #e3b341)',
        },
        {
          key: 'unassigned',
          label: 'unassigned',
          count: remainingWorkCounts.unassigned,
          background: 'var(--danger, #f85149)',
        },
        {
          key: 'inactive',
          label: 'inactive',
          count: remainingWorkCounts.inactive,
          background: 'rgba(107, 114, 128, 0.92)',
        },
      ].map(({ key, label, count, background }) => (
        <div
          key={key}
          data-testid={`remaining-work-${key}`}
          data-count={String(count)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${chrome.saveButtonPaddingY}px 8px`,
            borderRadius: 'var(--radius-sm)',
            background,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'nowrap',
          }}
        >
          {count} {label}
        </div>
      ))}
    </div>
  );

  const renderSaveReviewToggle = () => (
    <label
      aria-label={showSaveReview ? 'Save review popup enabled' : 'Save review popup disabled'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 9,
        fontFamily: 'var(--font-mono)',
        color: showSaveReview ? 'var(--text-primary)' : 'var(--text-tertiary)',
        cursor: 'pointer',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      <input
        type="checkbox"
        className="forge-checkbox"
        checked={showSaveReview}
        onChange={(event) => setShowSaveReview(event.target.checked)}
      />
      Save Review
    </label>
  );

  const renderSaveError = () =>
    saveError ? (
      <span
        style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}
      >
        {saveError}
      </span>
    ) : null;

  const renderSaveControls = ({
    includeHighlightedCount = hasHighlightTask,
  }: {
    includeHighlightedCount?: boolean;
  } = {}) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
      {renderSaveButton()}
      {renderRemainingWorkCounters({ includeHighlightedCount })}
      {renderSaveReviewToggle()}
      {renderSaveError()}
    </div>
  );

  const highlightShortcutChipStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: `${chrome.actionChipPaddingY}px 4px`,
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'default',
    fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const highlightShortcutKeyStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 16,
    padding: '0 3px',
    height: 16,
    background: 'rgba(232,151,108,0.15)',
    border: '1px solid rgba(232,151,108,0.3)',
    borderRadius: 3,
    fontSize: 9,
    fontFamily: 'var(--font-mono)',
    fontWeight: 700,
    color: 'var(--blue)',
    lineHeight: 1,
    flexShrink: 0,
  };

  const renderHighlightShortcutChip = (keyLabel: string, label: string) => (
    <button
      key={`${keyLabel}-${label}`}
      aria-label={`${label} highlight (${keyLabel})`}
      style={highlightShortcutChipStyle}
      tabIndex={-1}
      aria-hidden="true"
      onClick={() => {}}
      data-testid={`highlight-shortcut-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <span style={highlightShortcutKeyStyle}>{keyLabel}</span>
      <span>{label}</span>
    </button>
  );
  const visibleChainOptions = canDoTasks
    ? CHAIN_OPTIONS
    : labelerChainOptions && labelerChainOptions.length > 0
      ? labelerChainOptions
      : CHAIN_OPTIONS.filter((c) => c.id === selectedChainId);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
      }}
    >
      {/* Top item-entry bar */}
      <div
        style={{
          padding: `${chrome.toolbarPaddingY}px 10px`,
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          background: 'var(--surface)',
          display: 'flex',
          flexDirection: 'column',
          gap: chrome.toolbarVerticalGap,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Chain selector */}
          <select
            value={selectedChainId}
            onChange={(e) => handleSelectedChainChange(Number(e.target.value))}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              background: `var(--bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 6px center`,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: `${chrome.toolbarControlPaddingY}px 22px ${chrome.toolbarControlPaddingY}px 8px`,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
              cursor: 'pointer',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--accent-border)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            {visibleChainOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>

          {/* Item-number / normalized-barcode input */}
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: `${chrome.toolbarInputPaddingY}px 8px`,
                borderColor: searchFocused ? 'var(--accent-border)' : 'var(--border)',
                transition: 'border-color 150ms ease',
              }}
            >
              <SearchOutlinedIcon
                style={{ fontSize: 14, color: 'var(--text-tertiary)', flexShrink: 0 }}
              />
              <input
                type="search"
                name="master-label-item-search"
                aria-label="Master Label item search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
                enterKeyHint="search"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="none"
                placeholder="Enter item_number or normalized_barcode and press Enter"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  width: '100%',
                }}
              />
            </div>
          </div>

          {/* Crop size slider + image controls + zoom toggle */}
          {selectedItemNumbers.length > 0 && (
            <MasterLabelWorkspaceControls
              textScale={textScale}
              onTextScaleChange={setTextScale}
              cropSize={cropSize}
              onCropSizeChange={setCropSize}
              imageCacheSummary={imageCacheSummary}
              isLoadingRemainingImages={isLoadingRemainingImages}
              onLoadRemainingImages={handleLoadRemainingImages}
              autoReloadImages={autoReloadImages}
              onToggleAutoReloadImages={() => setAutoReloadImages((prev) => !prev)}
              zoomEnabled={zoomEnabled}
              onToggleZoom={toggleZoom}
              contextViewEnabled={contextViewEnabled}
              canToggleContextView={canToggleContextView}
              onToggleContextView={toggleCropContextView}
              toolbarButtonPaddingY={chrome.toolbarButtonPaddingY}
              style={{ marginLeft: 'auto', paddingLeft: 6 }}
            />
          )}

          {/* Loaded UPC chips */}
          {selectedItemNumbers.map((itemNumber) => {
            const data = editor.upcData.find((entry) => entry.itemNumber === itemNumber);
            return (
              <div
                key={itemNumber}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  background: 'var(--accent-subtle)',
                  border: '1px solid var(--accent-border)',
                  borderRadius: 10,
                  padding: `${chrome.toolbarChipPaddingY}px 6px ${chrome.toolbarChipPaddingY}px 8px`,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--accent)',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {data?.actualItemNumber === null ? data.columnKey : data?.itemNumber || itemNumber}
                <CloseIcon
                  onClick={() => removeUPC(itemNumber)}
                  style={{ fontSize: 12, cursor: 'pointer', opacity: 0.7 }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      {selectedItemNumbers.length === 0 && !deepLinkLoading && !shouldRenderTrailingTile ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            color: 'var(--text-tertiary)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>No items selected</div>
          <div style={{ fontSize: 12, maxWidth: 320, textAlign: 'center', lineHeight: 1.5 }}>
            Enter an item_number or normalized_barcode above and press Enter to load one or more
            item columns.
          </div>
        </div>
      ) : selectedItemNumbers.length === 0 && deepLinkLoading && !shouldRenderTrailingTile ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            color: 'var(--text-tertiary)',
          }}
        >
          <CircularProgress size={28} style={{ color: 'var(--text-tertiary)' }} />
          <div style={{ fontSize: 12, fontWeight: 600 }}>Loading items...</div>
        </div>
      ) : (
        <div
          ref={contentRef}
          style={{
            flex: 1,
            display: 'flex',
            gap: chrome.contentGap,
            padding: `${chrome.contentPaddingY}px 8px`,
            overflowX: focusedItemNumber ? 'hidden' : 'auto',
            overflowY: 'hidden',
            alignItems: 'stretch',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {visibleUpcs.map((upc) => {
            const remoteDataCollectionBaseKey = getRemoteDataCollectionLookupBaseKey(
              selectedChainId,
              upc
            );
            const remoteDataCollectionStoreLimit =
              remoteDataCollectionStoreLimitByKey[remoteDataCollectionBaseKey] ??
              REMOTE_DATA_COLLECTION_DEFAULT_STORE_LIMIT;
            const remoteDataCollectionEntry =
              remoteDataCollectionLinksByKey[
                getRemoteDataCollectionLookupKey(
                  selectedChainId,
                  upc,
                  remoteDataCollectionStoreLimit
                )
              ];
            return (
              <div
                key={upc.itemNumber}
                style={{
                  flex: focusedItemNumber ? '1 1 auto' : '1 1 0',
                  minWidth: focusedItemNumber ? 0 : 200,
                  maxWidth: focusedItemNumber ? '100%' : undefined,
                  display: 'flex',
                }}
              >
                <UPCColumnView
                  upc={upc}
                  baseUpc={editor.baseUpcData.find((item) => item.itemNumber === upc.itemNumber)}
                  modPrefix={upc.actualItemNumber ? (itemModPrefixes[upc.actualItemNumber] ?? null) : null}
                  onModPrefixEdit={handleModPrefixEdit}
                  selectedCropIds={editor.selectedCropIds}
                  focusedHighlight={focusedHighlight}
                  highlightedCropIds={displayPrimaryHighlightCropIds}
                  highlightedNormalizedBarcode={highlightedNormalizedBarcode}
                  highlightedNormalizedBarcodes={highlightedNormalizedBarcodeSet}
                  highlightedDesignId={highlightedDesignId}
                  highlightedDesignIds={highlightedDesignIdSet}
                  prioritizeHighlightedCrops={hasHighlightTask}
                  highlightScrollKey={hasHighlightTask ? highlightTaskKey : null}
                  highlightColor={highlightPreset?.color}
                  highlightGlow={highlightPreset?.glow}
                  secondaryHighlightedCropIds={displaySecondaryHighlightCropIds}
                  secondaryHighlightColor={highlightPreset?.secondaryColor}
                  secondaryHighlightGlow={highlightPreset?.secondaryGlow}
                  tertiaryHighlightedCropIds={tertiaryHighlightedCropIds}
                  tertiaryHighlightColor={highlightPreset?.tertiaryColor}
                  tertiaryHighlightGlow={highlightPreset?.tertiaryGlow}
                  onCropClick={editor.toggleCropSelection}
                  onSectionClick={handleHighlightedSectionClick}
                  mode={editor.mode}
                  mergeSourceDesignId={null}
                  activeDesignId={editor.activeDesignId}
                  onActiveDesignChange={editor.setActiveDesignId}
                  designColorMap={editor.designColorMap}
                  onImageHover={handleImageHover}
                  onImageMove={handleImageMove}
                  onImageLeave={handleImageLeave}
                  onMetadataHover={handleMetadataHover}
                  onMetadataMove={handleMetadataMove}
                  onMetadataLeave={handleMetadataLeave}
                  onRemoveUpc={removeUPC}
                  recentlyMovedCropIds={editor.recentlyMovedCropIds}
                  selectionVisualMode="master-label"
                  cropOrderMode="master-label"
                  isFocusedColumn={focusedItemNumber === upc.itemNumber}
                  canFocusColumn={focusedItemNumber === null}
                  onFocusColumn={setFocusedItemNumber}
                  onClearFocusedColumn={() => setFocusedItemNumber(null)}
                  onNotify={notify}
                  onOpenDesignGroups={openDesignGroupManager}
                  onEditDesignNote={editDesignNote}
                  onToggleDesignReview={canDoTasks ? toggleDesignReview : undefined}
                  onDesignLockAction={hasInternalAccess ? openDesignLockDialog : undefined}
                  onLoadDesignGroupMember={(member) => {
                    void loadItem(member.designId, member.normalizedBarcode || member.designId);
                  }}
                  onOpenDataCollection={openDataCollectionRequest}
                  controlEmbeddingsUrl={getUpcControlEmbeddingsUrl(upc)}
                  remoteDataCollectionLinks={remoteDataCollectionEntry?.links ?? []}
                  remoteDataCollectionStatus={remoteDataCollectionEntry?.status ?? 'idle'}
                  remoteDataCollectionScannedCount={remoteDataCollectionEntry?.searchedStoreCount}
                  remoteDataCollectionStoreLimit={remoteDataCollectionStoreLimit}
                  onLoadRemoteDataCollectionLinks={(storeLimit) =>
                    loadRemoteDataCollectionLinks(upc, storeLimit)
                  }
                  onQueryCrop={openCropQuery}
                  cropSize={cropSize}
                  textScale={textScale}
                  reloadVersion={reloadVersion}
                  onPreloadGroup={preloadImages}
                />
              </div>
            );
          })}
          {shouldRenderTrailingTile && trailingColumnTile && (
            <TrailingColumnTileView
              key={trailingColumnTile.key}
              tile={trailingColumnTile}
              onDismiss={
                trailingColumnTile.kind === 'no-results'
                  ? () => setTrailingColumnTile(null)
                  : undefined
              }
            />
          )}
        </div>
      )}

      {/* Bottom action bar — always visible when items are loaded */}
      {selectedItemNumbers.length > 0 && (
        <CropActionBar
          mode={editor.mode}
          selectedCropIds={editor.selectedCropIds}
          layoutVariant="master-label"
          textScale={textScale}
          newDesignOptions={newDesignOptions}
          onDeactivate={handleDeactivateSelected}
          onSetRep={
            editor.selectedCropDesignInfo && !editor.selectedCropDesignInfo.isAlreadyRep
              ? editor.selectedCropDesignInfo
              : null
          }
          onSetRepClick={handleSetRepresentative}
          onNewDesignClick={handleCreateNewDesign}
          onVizSimClick={canDoTasks ? openVizSimDatasetDialog : undefined}
          onReviewClick={openReviewChooser}
          onCompareClick={openCropCompare}
          onDeleteClick={canDoTasks ? handleMarkSelectedForDeletion : undefined}
          undoCount={editor.undoStack.length}
          onUndo={editor.undo}
          idleContent={
            hasHighlightTask ? (
              changeCount > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  {renderSaveButton()}
                  <span
                    aria-hidden="true"
                    style={{
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    •
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span
                      style={{
                        color: highlightPreset.color,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}
                    >
                      {highlightBannerMessage}
                    </span>
                    <div
                      data-testid="highlighted-work-banner-count"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: `${chrome.saveButtonPaddingY}px 8px`,
                        borderRadius: 'var(--radius-sm)',
                        background: '#58a6ff',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {highlightCountLabel}
                    </div>
                  </div>
                  <span
                    aria-hidden="true"
                    style={{
                      color: 'var(--text-tertiary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    •
                  </span>
                  {renderRemainingWorkCounters({ includeHighlightedCount: false })}
                  {renderSaveReviewToggle()}
                  {renderSaveError()}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span
                    data-testid="highlight-issue-type"
                    style={{
                      padding: '3px 6px',
                      borderRadius: 'var(--radius-sm)',
                      background: 'rgba(88, 166, 255, 0.12)',
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    Issue: {highlightPreset.issueLabel}
                  </span>
                  <span
                    style={{
                      color: highlightPreset.color,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {highlightBannerMessage}
                  </span>
                  {renderHighlightShortcutChip('[', 'Prev')}
                  {renderHighlightShortcutChip(']', 'Next')}
                  <div
                    data-testid="highlighted-work-banner-count"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: `${chrome.saveButtonPaddingY}px 8px`,
                      borderRadius: 'var(--radius-sm)',
                      background: '#58a6ff',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: 'var(--font-mono)',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {highlightCountLabel}
                  </div>
                </div>
              )
            ) : changeCount > 0 ? (
              renderSaveControls()
            ) : undefined
          }
        />
      )}

      {ZoomOverlay}

      {isReviewChooserOpen && (
        <ReviewIssueChooserOverlay
          onSelect={applyReviewIssue}
          onClear={clearReviewIssue}
          onClose={closeReviewChooser}
        />
      )}

      {noteEditorTarget && (
        <DesignNoteEditorOverlay
          designLabel={noteEditorTarget.designLabel}
          notes={currentNoteEditorNotes}
          value={noteDraft}
          editingNoteKey={editingNoteKey}
          onChange={setNoteDraft}
          onSubmit={submitDesignNote}
          onStartEdit={startNoteEdit}
          onCancelEdit={cancelNoteEdit}
          onDelete={deleteDesignNote}
          onClose={closeNoteEditor}
        />
      )}

      {dataCollectionTargetUpc && dataCollectionTargetsByScope && (
        <DataCollectionRequestOverlay
          upc={dataCollectionTargetUpc}
          targetsByScope={dataCollectionTargetsByScope}
          selectedScope={dataCollectionScope}
          selections={dataCollectionSelections}
          existingRequests={existingDataCollectionRequests}
          isLoadingExistingRequests={isLoadingExistingDataCollectionRequests}
          isSubmitting={isCreatingDataCollection}
          onScopeChange={selectDataCollectionScope}
          onToggleType={toggleDataCollectionType}
          onSubmit={submitDataCollectionRequest}
          onClose={closeDataCollectionRequest}
        />
      )}

      {pendingNewDesign && (
        <NewDesignConfirmOverlay
          itemNumber={pendingNewDesign.itemNumber}
          cropSummary={pendingNewDesign.cropSummary}
          availableBarcodes={pendingNewDesign.availableBarcodes}
          onConfirm={(normalizedBarcode) => {
            const targetItemNumber = pendingNewDesign.itemNumber;
            setPendingNewDesign(null);
            handleCreateNewDesign(targetItemNumber, { normalizedBarcode });
          }}
          onClose={() => setPendingNewDesign(null)}
        />
      )}

      {designLockDialogTarget && hasInternalAccess && (
        <DesignLockConfirmOverlay
          target={designLockDialogTarget}
          isSubmitting={isDesignLockSubmitting}
          onConfirm={() => void confirmDesignLockAction()}
          onClose={() => {
            if (!isDesignLockSubmitting) setDesignLockDialogTarget(null);
          }}
        />
      )}

      {currentGroupManagerContext && (
        <DesignGroupManagerOverlay
          design={currentGroupManagerContext.design}
          upc={currentGroupManagerContext.upc}
          openDesigns={currentGroupManagerOpenDesigns}
          isSubmitting={false}
          cropSize={cropSize}
          onImageHover={handleImageHover}
          onImageMove={handleImageMove}
          onImageLeave={handleImageLeave}
          onAddToGroup={(scope, targetDesignId) => handleDesignGroupMutation(scope, targetDesignId)}
          onLeaveGroup={handleLeaveDesignGroup}
          onConvertGroupScope={handleConvertDesignGroupScope}
          onToggleGroupReview={canDoTasks ? toggleGroupReview : undefined}
          onLoadGroupMember={(member) => {
            void loadItem(member.designId, member.normalizedBarcode || member.designId);
          }}
          onClose={closeDesignGroupManager}
        />
      )}

      {groupConflict && (
        <DesignGroupConflictOverlay
          conflict={groupConflict}
          onMove={handleResolveDesignGroupConflict}
          onClose={() => setGroupConflict(null)}
        />
      )}

      {cropQueryTarget && (
        <MasterLabelCropQueryOverlay
          target={cropQueryTarget}
          response={cropQueryResponse}
          candidateItems={cropQueryItems}
          referenceCropsById={cropQueryReferenceCrops}
          isLoading={isCropQueryLoading}
          isDetailsLoading={isCropQueryDetailsLoading}
          error={cropQueryError}
          chainId={selectedChainId}
          onAddColumn={addCropQueryColumn}
          onClose={closeCropQuery}
          onImageHover={handleImageHover}
          onImageMove={handleImageMove}
          onImageLeave={handleImageLeave}
        />
      )}

      {saveReviewData && (
        <SaveReviewDialog
          data={saveReviewData}
          enabled={showSaveReview}
          onEnabledChange={setShowSaveReview}
          onClose={() => setSaveReviewData(null)}
        />
      )}

      {saveWarningPrompt && (
        <SaveWarningDialog
          warnings={saveWarningPrompt.warnings}
          saving={saveWarningPrompt.saving}
          onAcknowledge={(comment, outcome) => saveWarningPrompt.resolve({ comment, outcome })}
          onGoBack={() => saveWarningPrompt.resolve(null)}
        />
      )}

      {isCropCompareOpen && (
        <CropComparePopup crops={cropsToCompare} onClose={() => setIsCropCompareOpen(false)} />
      )}

      {isVizSimDatasetDialogOpen && token && (
        <VizSimDatasetAddDialog
          token={token}
          chainId={selectedChainId}
          selectedCropIds={[...editor.selectedCropIds]}
          selectedCrops={editor.upcData
            .flatMap(collectUpcCrops)
            .filter((crop) => editor.selectedCropIds.has(crop.id))}
          cropSize={cropSize}
          classNameSuggestion={selectedVizSimClassName}
          onNotify={notify}
          onAdded={() => {
            editor.setSelectedCropIds(new Set());
          }}
          onClose={() => setIsVizSimDatasetDialogOpen(false)}
        />
      )}

      {productImageTarget && (
        <ProductImageDialogContainer
          chainId={selectedChainId}
          crop={productImageTarget.crop}
          upc={productImageTarget.upc}
          onNotify={notify}
          onUpdated={(itemNumber, primaryUrl, alternativeUrls) => {
            editor.patchUpcMetadata(itemNumber, (current) => ({
              ...current,
              productImageUrl: primaryUrl || undefined,
              alternativeImageUrls: alternativeUrls,
            }));
          }}
          onClose={() => setProductImageTarget(null)}
        />
      )}

      {NotificationToast}
    </div>
  );
};
